/**
 * VueLayers
 * Web map Vue components with the power of OpenLayers
 *
 * @package vuelayers
 * @author Vladimir Vershinin <ghettovoice@gmail.com>
 * @version 0.12.0-rc.14
 * @license MIT
 * @copyright (c) 2017-2020, Vladimir Vershinin <ghettovoice@gmail.com>
 */
import _regeneratorRuntime from '@babel/runtime/regenerator';
import _asyncToGenerator from '@babel/runtime/helpers/esm/asyncToGenerator';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import _typeof from '@babel/runtime/helpers/esm/typeof';
import { cleanSourceParams, EPSG_3857, transformPoint, transformLine, transformPolygon, transformMultiPoint, transformMultiLine, transformMultiPolygon, transformExtent as transformExtent$1, writeGeoJsonGeometry, readGeoJsonGeometry, writeGeoJsonFeature, readGeoJsonFeature, getGeomType, getGeomCoords, getFeatureId, getFeatureProperties, getFeatureGeom, COORD_PRECISION, roundExtent, initializeLayer, getLayerId, setLayerId, getControlId, initializeControl, findPointOnSurface, isGeoJSONGeometry, dumpStyle, setFeatureId, setFeatureProperties, initializeFeature, CIRCLE_SERIALIZE_PROP, STYLE_SERIALIZE_PROP, dumpFillStyle, initializeGeometry, getGeometryId, setGeometryId, roundPointCoords, getSourceId, setSourceId, initializeSource, initializeStyle, getStyleId, setStyleId, dumpImageStyle, initializeInteraction, getInteractionId, setInteractionId, getInteractionPriority, setInteractionPriority, getOverlayId, initializeOverlay, dumpStrokeStyle, roundCoords, flatCoords, isEqualCoord, transforms, dumpTextStyle, expandUrl, isGeoJSONFeature, getGeoJsonFmt, transform, extentFromProjection } from './ol-ext';
import { isArray, sequential, makeWatchers, isEqual, hasProp, stubObject, identity, keys, IdentityMap, noop, newLogger, mergeDescriptors, kebabCase, isFunction, assert, coalesce, serialize as serialize$1, extractChildren, waitFor, stubTrue, isNumber, pick, addPrefix, map as map$1, isPlainObject, instanceOf, find, forEach, clonePlainObject, get as get$1, isString, upperFirst, negate, isEmpty, sealFactory, replaceTokens, and, stubArray, every, difference, constant } from './utils';
import debounce from 'debounce-promise';
import { get } from 'ol/proj';
import { merge, race, of, throwError, from } from 'rxjs';
import { retry, filter, publishReplay, refCount, first, mergeMap, delayWhen, map, mapTo, skipWhile, tap, debounceTime } from 'rxjs/operators';
import { fromVueEvent, fromOlEvent, bufferDebounceTime, fromVueWatcher, fromOlChangeEvent } from './rx-ext';
import _classCallCheck from '@babel/runtime/helpers/esm/classCallCheck';
import _createClass from '@babel/runtime/helpers/esm/createClass';
import _assertThisInitialized from '@babel/runtime/helpers/esm/assertThisInitialized';
import _inherits from '@babel/runtime/helpers/esm/inherits';
import _possibleConstructorReturn from '@babel/runtime/helpers/esm/possibleConstructorReturn';
import _getPrototypeOf from '@babel/runtime/helpers/esm/getPrototypeOf';
import _wrapNativeSuper from '@babel/runtime/helpers/esm/wrapNativeSuper';
import _toConsumableArray from '@babel/runtime/helpers/esm/toConsumableArray';
import { Observable, Collection, Feature, getUid, Overlay } from 'ol';
import EventType from 'ol/events/EventType';
import { v4 } from 'uuid';
import VueQuery from 'vuequery';
import Vue from 'vue';
import LRU from 'lru-cache';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import CollectionEventType from 'ol/CollectionEventType';
import { defaults, Control } from 'ol/control';
import ObjectEventType from 'ol/ObjectEventType';
import { Style } from 'ol/style';
import RenderEventType from 'ol/render/EventType';
import { defaults as defaults$1, Interaction } from 'ol/interaction';
import BaseLayer from 'ol/layer/Base';
import { boundingExtent } from 'ol/extent';
import TileEventType from 'ol/source/TileEventType';
import { all } from 'ol/loadingstrategy';
import VectorEventType from 'ol/source/VectorEventType';
import WMSServerType from 'ol/source/WMSServerType';
import { createTileUrlFunctionFromTemplates } from 'ol-tilecache';
import { XYZ } from 'ol/source';
import { createXYZ } from 'ol/tilegrid';

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

var serialize = function serialize(value) {
  if (value == null) return value;
  return _typeof(value) === 'object' ? JSON.stringify(value) : value;
};
/**
 * Shared ArcGIS source params and methods.
 */


var arcgisSource = {
  props: {
    // ArcGIS params from https://developers.arcgis.com/rest/services-reference/export-map.htm

    /**
     * @type {boolean}
     */
    hidpi: {
      type: Boolean,
      default: true
    },

    /**
     * @type {string}
     */
    format: {
      type: String,
      default: 'PNG32'
    },

    /**
     * @type {string|string[]|undefined}
     */
    layers: [String, Array],

    /**
     * @type {Object|string|undefined}
     */
    layerDefs: [Object, String],

    /**
     * @type {Object[]|string|undefined}
     */
    dynamicLayers: [Array, String],

    /**
     * @type {number|undefined}
     */
    dpi: Number,

    /**
     * @type {boolean}
     */
    transparent: {
      type: Boolean,
      default: true
    },

    /**
     * @type {string|string[]|undefined}
     */
    time: [String, Array],

    /**
     * @type {Object|string|undefined}
     */
    layerTimeOptions: [Object, String],

    /**
     * @type {string|undefined}
     */
    gdbVersion: String,

    /**
     * @type {string|undefined}
     */
    mapScale: String,

    /**
     * @type {number|undefined}
     */
    rotation: Number,

    /**
     * @type {Array|string|undefined}
     */
    datumTransformations: [Array, String],

    /**
     * @type {Array|string|undefined}
     */
    mapRangeValues: [Array, String],

    /**
     * @type {Array|string|undefined}
     */
    layerRangeValues: [Array, String],

    /**
     * @type {Array|string|undefined}
     */
    layerParameterValues: [Array, String],

    /**
     * @type {number|undefined}
     */
    historicMoment: Number,

    /**
     * Additional ArcGIS request parameters
     * @params {Object|undefined}
     */
    params: Object
  },
  computed: {
    /**
     * @returns {string|undefined}
     */
    layersStr: function layersStr() {
      return isArray(this.layers) ? this.layers.join(',') : this.layers;
    },

    /**
     * @type {string|undefined}
     */
    timeStr: function timeStr() {
      return isArray(this.time) ? this.time.join(',') : this.time;
    },

    /**
     * @returns {Object|null}
     */
    customParams: function customParams() {
      return this.params ? cleanSourceParams(this.params, ['FORMAT', 'F', 'LAYERS', 'LAYERDEFS', 'DYNAMICLAYERS', 'DPI', 'TRANSPARENT', 'TIME', 'LAYERTIMEOPTIONS', 'GDBVERSION', 'MAPSCALE', 'ROTATION', 'DATUMTRANSFORMATIONS', 'MAPRANGEVALUES', 'LAYERRANGEVALUES', 'LAYERPARAMETERVALUES', 'HISTORICMOMENT']) : null;
    },

    /**
     * @returns {Object}
     */
    allParams: function allParams() {
      return _objectSpread(_objectSpread({}, this.customParams || {}), {}, {
        LAYERS: this.layersStr,
        FORMAT: this.format,
        LAYERDEFS: serialize(this.layerDefs),
        DYNAMICLAYERS: serialize(this.dynamicLayers),
        DPI: this.dpi,
        TRANSPARENT: this.transparent,
        TIME: this.timeStr,
        LAYERTIMEOPTIONS: serialize(this.layerTimeOptions),
        GDBVERSION: this.gdbVersion,
        MAPSCALE: this.mapScale,
        ROTATION: this.rotation,
        DATUMTRANSFORMATIONS: serialize(this.datumTransformations),
        MAPRANGEVALUES: serialize(this.mapRangeValues),
        LAYERRANGEVALUES: serialize(this.layerRangeValues),
        LAYERPARAMETERVALUES: serialize(this.layerParameterValues),
        HISTORICMOMENT: serialize(this.historicMoment)
      });
    }
  },
  watch: _objectSpread(_objectSpread({
    allParams: /*#__PURE__*/sequential( /*#__PURE__*/function () {
      var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(value) {
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                _context.next = 2;
                return this.updateParams(value);

              case 2:
              case "end":
                return _context.stop();
            }
          }
        }, _callee, this);
      }));

      return function (_x) {
        return _ref.apply(this, arguments);
      };
    }()),
    layersStr: /*#__PURE__*/sequential( /*#__PURE__*/function () {
      var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(value) {
        return _regeneratorRuntime.wrap(function _callee2$(_context2) {
          while (1) {
            switch (_context2.prev = _context2.next) {
              case 0:
                _context2.next = 2;
                return this.updateParam('layers', value);

              case 2:
              case "end":
                return _context2.stop();
            }
          }
        }, _callee2, this);
      }));

      return function (_x2) {
        return _ref2.apply(this, arguments);
      };
    }()),
    timeStr: /*#__PURE__*/sequential( /*#__PURE__*/function () {
      var _ref3 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3(value) {
        return _regeneratorRuntime.wrap(function _callee3$(_context3) {
          while (1) {
            switch (_context3.prev = _context3.next) {
              case 0:
                _context3.next = 2;
                return this.updateParam('time', value);

              case 2:
              case "end":
                return _context3.stop();
            }
          }
        }, _callee3, this);
      }));

      return function (_x3) {
        return _ref3.apply(this, arguments);
      };
    }())
  }, /*#__PURE__*/makeWatchers(['format', 'layerDefs', 'dynamicLayers', 'dpi', 'transparent', 'layerTimeOptions', 'gdbVersion', 'mapScale', 'rotation', 'datumTransformations', 'mapRangeValues', 'layerRangeValues', 'layerParameterValues', 'historicMoment'], function (prop) {
    return /*#__PURE__*/sequential( /*#__PURE__*/function () {
      var _ref4 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee4(value) {
        return _regeneratorRuntime.wrap(function _callee4$(_context4) {
          while (1) {
            switch (_context4.prev = _context4.next) {
              case 0:
                _context4.next = 2;
                return this.updateParam(prop, serialize(value));

              case 2:
              case "end":
                return _context4.stop();
            }
          }
        }, _callee4, this);
      }));

      return function (_x4) {
        return _ref4.apply(this, arguments);
      };
    }());
  })), /*#__PURE__*/makeWatchers(['hidpi'], function (prop) {
    return /*#__PURE__*/sequential( /*#__PURE__*/function () {
      var _ref5 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee5(val, prev) {
        return _regeneratorRuntime.wrap(function _callee5$(_context5) {
          while (1) {
            switch (_context5.prev = _context5.next) {
              case 0:
                if (!isEqual(val, prev)) {
                  _context5.next = 2;
                  break;
                }

                return _context5.abrupt("return");

              case 2:
                if (process.env.VUELAYERS_DEBUG) {
                  this.$logger.log("".concat(prop, " changed, scheduling recreate..."));
                }

                _context5.next = 5;
                return this.scheduleRecreate();

              case 5:
              case "end":
                return _context5.stop();
            }
          }
        }, _callee5, this);
      }));

      return function (_x5, _x6) {
        return _ref5.apply(this, arguments);
      };
    }());
  })),
  methods: {
    /**
     * @returns {Promise<Object>}
     */
    getParams: function getParams() {
      var _this = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee6() {
        return _regeneratorRuntime.wrap(function _callee6$(_context6) {
          while (1) {
            switch (_context6.prev = _context6.next) {
              case 0:
                _context6.next = 2;
                return _this.resolveSource();

              case 2:
                return _context6.abrupt("return", _context6.sent.getParams());

              case 3:
              case "end":
                return _context6.stop();
            }
          }
        }, _callee6);
      }))();
    },

    /**
     * @param {Object} params
     * @returns {Promise<void>}
     */
    updateParams: function updateParams(params) {
      var _this2 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee7() {
        return _regeneratorRuntime.wrap(function _callee7$(_context7) {
          while (1) {
            switch (_context7.prev = _context7.next) {
              case 0:
                params = _objectSpread(_objectSpread({}, _this2.allParams), params);
                _context7.t0 = isEqual;
                _context7.t1 = params;
                _context7.next = 5;
                return _this2.getParams();

              case 5:
                _context7.t2 = _context7.sent;

                if (!(0, _context7.t0)(_context7.t1, _context7.t2)) {
                  _context7.next = 8;
                  break;
                }

                return _context7.abrupt("return");

              case 8:
                _context7.next = 10;
                return _this2.resolveSource();

              case 10:
                _context7.sent.updateParams(params);

              case 11:
              case "end":
                return _context7.stop();
            }
          }
        }, _callee7);
      }))();
    },

    /**
     * @param {string} param
     * @param {*} value
     * @returns {Promise<void>}
     */
    updateParam: function updateParam(param, value) {
      var _this3 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee8() {
        return _regeneratorRuntime.wrap(function _callee8$(_context8) {
          while (1) {
            switch (_context8.prev = _context8.next) {
              case 0:
                _context8.next = 2;
                return _this3.updateParams(_defineProperty({}, param.toUpperCase(), value));

              case 2:
              case "end":
                return _context8.stop();
            }
          }
        }, _callee8);
      }))();
    }
  }
};

var EVENT_BUS_PROP = '$vlEventBus';
var eventBus = {
  created: function created() {
    var _this = this;

    initEventBus(); // define local getter

    Object.defineProperties(this, {
      /**
       * @type {Vue}
       */
      $eventBus: {
        enumerable: true,
        get: function get() {
          return _this[EVENT_BUS_PROP];
        }
      }
    });
  }
};

function initEventBus() {
  if (hasProp(Vue, EVENT_BUS_PROP) || hasProp(Vue.prototype, EVENT_BUS_PROP)) return;
  var bus = new Vue();

  if (!hasProp(Vue, EVENT_BUS_PROP)) {
    Object.defineProperties(Vue, _defineProperty({}, EVENT_BUS_PROP, {
      enumerable: true,
      get: function get() {
        return bus;
      }
    }));
  }

  if (!hasProp(Vue.prototype, EVENT_BUS_PROP)) {
    Object.defineProperties(Vue.prototype, _defineProperty({}, EVENT_BUS_PROP, {
      enumerable: true,
      get: function get() {
        return bus;
      }
    }));
  }
}

var IDENTITY_MAP_PROP = '$vlIdentityMap';
var INSTANCES_POOL = 'instances';
var identMap = {
  data: function data() {
    return {
      idents: stubObject()
    };
  },
  beforeCreate: function beforeCreate() {
    var _this = this;

    initIdentityMap(); // define local getter

    Object.defineProperties(this, {
      $identityMap: {
        enumerable: true,
        get: function get() {
          return _this[IDENTITY_MAP_PROP];
        }
      }
    });
  },
  destroyed: function destroyed() {
    this.unsetInstances();
  },
  methods: {
    /**
     * @param parts
     * @return {string}
     */
    makeIdent: function makeIdent() {
      for (var _len = arguments.length, parts = new Array(_len), _key = 0; _key < _len; _key++) {
        parts[_key] = arguments[_key];
      }

      return parts.filter(identity).join('.');
    },

    /**
     * Caches or reuse factory result in the identity map
     * and returns result.
     *
     * @param {string|undefined} ident
     * @param {function} factory
     * @returns {*}
     */
    instanceFactoryCall: function instanceFactoryCall(ident, factory) {
      if (ident && this.hasInstance(ident)) {
        return this.getInstance(ident);
      }

      var inst = factory();

      if (ident) {
        this.setInstance(ident, inst);
      }

      return inst;
    },

    /**
     * @param {string|undefined} ident
     * @returns {*}
     */
    getInstance: function getInstance(ident) {
      if (!ident || !this.hasInstance(ident)) return;
      this.idents[ident] = true;
      return this.$identityMap.get(ident, INSTANCES_POOL);
    },

    /**
     * @param ident
     * @param inst
     */
    setInstance: function setInstance(ident, inst) {
      if (!ident) return;
      this.idents[ident] = true;
      this.$identityMap.set(ident, inst, INSTANCES_POOL);
    },

    /**
     * @param {string|undefined} ident
     * @returns {*}
     */
    hasInstance: function hasInstance(ident) {
      if (!ident) return;
      return this.$identityMap.has(ident, INSTANCES_POOL);
    },

    /**
     * @param {string|undefined} ident
     * @return {void}
     */
    unsetInstance: function unsetInstance(ident) {
      if (!ident) return;
      delete this.idents[ident];
      this.$identityMap.unset(ident, INSTANCES_POOL);
    },

    /**
     * Unsets all self indets
     * @return {void}
     */
    unsetInstances: function unsetInstances() {
      keys(this.idents).forEach(this.unsetInstance.bind(this));
    },

    /**
     * @param {string|undefined} fromIdent
     * @param {string|undefined} toIdent
     * @returns {boolean}
     */
    moveInstance: function moveInstance(fromIdent, toIdent) {
      return this.$identityMap.move(fromIdent, toIdent, INSTANCES_POOL);
    }
  }
};

function initIdentityMap() {
  if (hasProp(Vue, IDENTITY_MAP_PROP) || hasProp(Vue.prototype, IDENTITY_MAP_PROP)) return;
  var imap = new IdentityMap();

  if (!hasProp(Vue, IDENTITY_MAP_PROP)) {
    Object.defineProperties(Vue, _defineProperty({}, IDENTITY_MAP_PROP, {
      enumerable: true,
      get: function get() {
        return imap;
      }
    }));
  }

  if (!hasProp(Vue.prototype, IDENTITY_MAP_PROP)) {
    Object.defineProperties(Vue.prototype, _defineProperty({}, IDENTITY_MAP_PROP, {
      enumerable: true,
      get: function get() {
        return imap;
      }
    }));
  }
}

/**
 * RxJS subscriptions manager.
 */

var rxSubs = {
  methods: {
    /**
     * @return {void}
     * @protected
     */
    subscribeAll: function subscribeAll() {},

    /**
     * @return {void}
     * @protected
     */
    unsubscribeAll: function unsubscribeAll() {
      this._rxSubs.forEach(function (subs) {
        return subs.unsubscribe();
      });

      this._rxSubs = [];
    },

    /**
     * @param {Observable<T>} observable
     * @param {function} [next] Next handler or Observer object.
     * @param {function} [error] Error handler.
     * @param {function} [complete] Complete handler.
     * @return {Subscription}
     * @protected
     */
    subscribeTo: function subscribeTo(observable) {
      var _this = this;

      var next = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : noop;
      var error = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : noop;
      var complete = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : noop;

      var errorWrap = function errorWrap(err) {
        if (process.env.NODE_ENV !== 'production') {
          if (_this.$logger) {
            _this.$logger.error(err.stack);
          } else {
            newLogger(_this.vmName).error(err.stack);
          }
        }

        error(err);
      };

      var subs = observable.pipe(retry(3)).subscribe(next, errorWrap, complete);

      this._rxSubs.push(subs);

      return subs;
    },
    unsubscribe: function unsubscribe(subs) {
      var idx = this._rxSubs.indexOf(subs);

      if (idx === -1) return;
      subs.unsubscribe();

      this._rxSubs.splice(idx, 1);
    }
  },
  beforeCreate: function beforeCreate() {
    /**
     * @type {Subscription[]}
     * @private
     */
    this._rxSubs = [];
  },
  destroyed: function destroyed() {
    this.unsubscribeAll();
  }
};

// const SERVICES_PROP = Symbol('services')

var SERVICES_PROP = 'services';
/**
 * Service container mixin
 */

var services = {
  inject: {
    $services: SERVICES_PROP // todo works only in Vue 2.5.x
    // $services: {from: SERVICES_PROP, default: Object.create(null)},

  },
  provide: function provide() {
    return _defineProperty({}, SERVICES_PROP, this.getServices());
  },
  methods: {
    /**
     * @returns {Object}
     * @protected
     */
    getServices: function getServices() {
      return mergeDescriptors({}, this.$services || {});
    }
  },
  beforeCreate: function beforeCreate() {
    var source = this.$parent;

    while (source) {
      if (source._provided != null && source._provided[SERVICES_PROP] != null) {
        break;
      }

      source = source.$parent;
    }

    if (source == null || source._provided[SERVICES_PROP] == null) {
      delete this.$options.inject.$services;
    }
  }
};

function _createSuper(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct(); return function _createSuperInternal() { var Super = _getPrototypeOf(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn(this, result); }; }

function _isNativeReflectConstruct() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Date.prototype.toString.call(Reflect.construct(Date, [], function () {})); return true; } catch (e) { return false; } }
var VM_PROP = 'vm';
var FRAME_TIME = 1000 / 60;
var OlObjectState = {
  UNDEF: 'undef',
  CREATING: 'creating',
  CREATED: 'created',
  MOUNTING: 'mounting',
  MOUNTED: 'mounted',
  UNMOUNTING: 'unmounting',
  UNMOUNTED: 'unmounted',
  DESTROYING: 'destroying',
  DESTROYED: 'destroyed'
};
var OlObjectEvent = {
  CREATED: OlObjectState.CREATED,
  MOUNTED: OlObjectState.MOUNTED,
  UNMOUNTED: OlObjectState.UNMOUNTED,
  DESTROYED: OlObjectState.DESTROYED,
  ERROR: 'error'
};
var OlObjectAction = {
  CREATE: 'create',
  MOUNT: 'mount',
  UNMOUNT: 'unmount',
  DESTROY: 'destroy'
};
/**
 * Basic ol component mixin.
 * todo try to subscribe to generic change event here and update rev according to internal ol counter
 */

var olCmp = {
  mixins: [identMap, rxSubs, services, eventBus],
  props: {
    /**
     * @type {string|number}
     */
    id: {
      type: [String, Number],
      default: v4,
      validator: function validator(value) {
        return value != null && value !== '';
      }
    },

    /**
     * Unique key for saving to identity map
     * @type {string|number|undefined}
     */
    ident: [String, Number]
  },
  data: function data() {
    return {
      rev: 0
    };
  },
  computed: {
    currentId: function currentId() {
      if (this.rev && this.$olObject) {
        return this.getIdInternal();
      }

      return this.id;
    },

    /**
     * @type {string}
     */
    vmClass: function vmClass() {
      return kebabCase(this.$options.name);
    },

    /**
     * @type {string}
     */
    vmId: function vmId() {
      return [this.vmClass, this.currentId].filter(identity).join('.');
    },

    /**
     * @type {string}
     */
    vmName: function vmName() {
      return [this.$options.name, this.currentId].filter(identity).join('.');
    },

    /**
     * @type {string|undefined}
     */
    olObjIdent: function olObjIdent() {
      if (!this.ident) return;
      return this.makeIdent(this.ident);
    }
  },
  watch: {
    id: /*#__PURE__*/sequential( /*#__PURE__*/function () {
      var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(value) {
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                _context.next = 2;
                return this.setId(value);

              case 2:
              case "end":
                return _context.stop();
            }
          }
        }, _callee, this);
      }));

      return function (_x) {
        return _ref.apply(this, arguments);
      };
    }()),
    currentId: /*#__PURE__*/debounce(function (value) {
      if (value === this.id) return;
      this.$emit('update:id', value);
    }, FRAME_TIME),
    olObjIdent: /*#__PURE__*/sequential(function (value, prevValue) {
      if (value && prevValue) {
        this.moveInstance(value, prevValue);
      } else if (value && !prevValue && this.$olObject) {
        this.setInstance(value, this.$olObject);
      } else if (!value && prevValue) {
        this.unsetInstance(prevValue);
      }
    })
  },
  created: function created() {
    var _this = this;

    return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
      return _regeneratorRuntime.wrap(function _callee2$(_context2) {
        while (1) {
          switch (_context2.prev = _context2.next) {
            case 0:
              /**
               * @type {{warn: (function(...[*]): void), log: (function(...[*]): void), error: (function(...[*]): void)}}
               * @private
               */
              _this._logger = newLogger(_this.vmName);
              /**
               * @type {number}
               * @private
               */

              _this._olObjectState = OlObjectState.UNDEF;
              /**
               * @type {module:ol/Object~BaseObject}
               * @private
               */

              _this._olObject = undefined;
              /**
               * @type {Observable}
               * @private
               */

              _this._olObjectEvents = newOlObjectEventsObs.call(_this);
              defineDebounceMethods.call(_this);
              defineServices.call(_this);
              _context2.next = 8;
              return execInit.call(_this, false);

            case 8:
            case "end":
              return _context2.stop();
          }
        }
      }, _callee2);
    }))();
  },
  mounted: function mounted() {
    var _this2 = this;

    return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3() {
      return _regeneratorRuntime.wrap(function _callee3$(_context3) {
        while (1) {
          switch (_context3.prev = _context3.next) {
            case 0:
              _context3.next = 2;
              return execMount.call(_this2);

            case 2:
            case "end":
              return _context3.stop();
          }
        }
      }, _callee3);
    }))();
  },
  beforeDestroy: function beforeDestroy() {
    var _this3 = this;

    return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee4() {
      return _regeneratorRuntime.wrap(function _callee4$(_context4) {
        while (1) {
          switch (_context4.prev = _context4.next) {
            case 0:
              _context4.next = 2;
              return execUnmount.call(_this3, true);

            case 2:
            case "end":
              return _context4.stop();
          }
        }
      }, _callee4);
    }))();
  },
  destroyed: function destroyed() {
    var _this4 = this;

    return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee5() {
      return _regeneratorRuntime.wrap(function _callee5$(_context5) {
        while (1) {
          switch (_context5.prev = _context5.next) {
            case 0:
              _context5.next = 2;
              return execDeinit.call(_this4);

            case 2:
            case "end":
              return _context5.stop();
          }
        }
      }, _callee5);
    }))();
  },
  methods: {
    /**
     * @return {Promise<void>} Resolves when initialization completes
     * @protected
     */
    beforeInit: function beforeInit() {},

    /**
     * @return {Promise<void>} Resolves when initialization completes
     * @protected
     */
    init: function init() {
      var _this5 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee6() {
        return _regeneratorRuntime.wrap(function _callee6$(_context6) {
          while (1) {
            switch (_context6.prev = _context6.next) {
              case 0:
                _context6.next = 2;
                return _this5.instanceFactoryCall(_this5.olObjIdent, _this5.createOlObject.bind(_this5));

              case 2:
                _this5._olObject = _context6.sent;
                _this5._olObject[VM_PROP] || (_this5._olObject[VM_PROP] = []); // for loaded from IdentityMap

                if (!_this5._olObject[VM_PROP].includes(_this5)) {
                  _this5._olObject[VM_PROP].push(_this5);
                }

                ++_this5.rev;

                _this5.subscribeAll();

              case 7:
              case "end":
                return _context6.stop();
            }
          }
        }, _callee6);
      }))();
    },

    /**
     * @return {module:ol/Object~BaseObject|Promise<module:ol/Object~BaseObject>}
     * @protected
     * @abstract
     */
    createOlObject: function createOlObject() {
      throw new Error("".concat(this.vmName, " not implemented method: createOlObject()"));
    },

    /**
     * @return {Promise<void>}
     * @protected
     */
    deinit: function deinit() {
      var _this6 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee7() {
        return _regeneratorRuntime.wrap(function _callee7$(_context7) {
          while (1) {
            switch (_context7.prev = _context7.next) {
              case 0:
                _this6.unsubscribeAll();

                _this6.unsetInstances();

                if (_this6._olObject) {
                  _this6._olObject[VM_PROP] = _this6._olObject[VM_PROP].filter(function (vm) {
                    return vm !== _this6;
                  });
                  _this6._olObject = undefined;
                }

              case 3:
              case "end":
                return _context7.stop();
            }
          }
        }, _callee7);
      }))();
    },

    /**
     * @return {Promise<void>}
     * @protected
     */
    beforeMount: function beforeMount() {},

    /**
     * @return {Promise<void>}
     * @protected
     */
    mount: function mount() {
      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee8() {
        return _regeneratorRuntime.wrap(function _callee8$(_context8) {
          while (1) {
            switch (_context8.prev = _context8.next) {
              case 0:
              case "end":
                return _context8.stop();
            }
          }
        }, _callee8);
      }))();
    },

    /**
     * @return {void|Promise<void>}
     * @protected
     */
    unmount: function unmount() {
      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee9() {
        return _regeneratorRuntime.wrap(function _callee9$(_context9) {
          while (1) {
            switch (_context9.prev = _context9.next) {
              case 0:
              case "end":
                return _context9.stop();
            }
          }
        }, _callee9);
      }))();
    },

    /**
     * Refresh internal ol objects
     * @return {Promise<void>}
     */
    refresh: function refresh() {
      var _this7 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee10() {
        var olObj;
        return _regeneratorRuntime.wrap(function _callee10$(_context10) {
          while (1) {
            switch (_context10.prev = _context10.next) {
              case 0:
                _context10.next = 2;
                return _this7.resolveOlObject();

              case 2:
                olObj = _context10.sent;
                return _context10.abrupt("return", new Promise(function (resolve) {
                  if (isFunction(olObj.changed)) {
                    olObj.once('change', function () {
                      return resolve();
                    });
                    olObj.changed();
                  } else {
                    ++_this7.rev;
                    resolve();
                  }
                }));

              case 4:
              case "end":
                return _context10.stop();
            }
          }
        }, _callee10);
      }))();
    },

    /**
     * @return {Promise<void>}
     */
    scheduleRefresh: function scheduleRefresh() {
      var _this8 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee11() {
        return _regeneratorRuntime.wrap(function _callee11$(_context11) {
          while (1) {
            switch (_context11.prev = _context11.next) {
              case 0:
                _context11.next = 2;
                return _this8.debounceRefresh();

              case 2:
              case "end":
                return _context11.stop();
            }
          }
        }, _callee11);
      }))();
    },

    /**
     * Internal usage only in components that doesn't support refreshing.
     * @return {Promise<void>}
     * @protected
     */
    remount: function remount() {
      var _this9 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee12() {
        return _regeneratorRuntime.wrap(function _callee12$(_context12) {
          while (1) {
            switch (_context12.prev = _context12.next) {
              case 0:
                if (!(_this9.$olObjectState === OlObjectState.MOUNTED)) {
                  _context12.next = 8;
                  break;
                }

                if (process.env.VUELAYERS_DEBUG) {
                  _this9.$logger.log('remounting...');
                }

                _context12.next = 4;
                return execUnmount.call(_this9);

              case 4:
                _context12.next = 6;
                return execMount.call(_this9);

              case 6:
                _context12.next = 9;
                break;

              case 8:
                if (process.env.VUELAYERS_DEBUG) {
                  _this9.$logger.log('remount discarded');
                }

              case 9:
              case "end":
                return _context12.stop();
            }
          }
        }, _callee12);
      }))();
    },

    /**
     * @return {Promise<void>}
     */
    scheduleRemount: function scheduleRemount() {
      var _this10 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee13() {
        return _regeneratorRuntime.wrap(function _callee13$(_context13) {
          while (1) {
            switch (_context13.prev = _context13.next) {
              case 0:
                if (![OlObjectState.MOUNTING, OlObjectState.MOUNTED].includes(_this10.$olObjectState)) {
                  _context13.next = 6;
                  break;
                }

                if (process.env.VUELAYERS_DEBUG) {
                  _this10.$logger.log('remount scheduled');
                }

                _context13.next = 4;
                return _this10.debounceRemount();

              case 4:
                _context13.next = 7;
                break;

              case 6:
                if (process.env.VUELAYERS_DEBUG) {
                  _this10.$logger.log('remount discarded');
                }

              case 7:
              case "end":
                return _context13.stop();
            }
          }
        }, _callee13);
      }))();
    },

    /**
     * Only for internal purpose to support watching for properties
     * for which OpenLayers doesn't provide setters.
     * @return {Promise}
     * @protected
     */
    recreate: function recreate() {
      var _this11 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee14() {
        var mounted;
        return _regeneratorRuntime.wrap(function _callee14$(_context14) {
          while (1) {
            switch (_context14.prev = _context14.next) {
              case 0:
                if (![OlObjectState.CREATED, OlObjectState.MOUNTING, OlObjectState.MOUNTED, OlObjectState.UNMOUNTING, OlObjectState.UNMOUNTED].includes(_this11.$olObjectState)) {
                  _context14.next = 15;
                  break;
                }

                if (process.env.VUELAYERS_DEBUG) {
                  _this11.$logger.log('recreating...');
                }

                mounted = [OlObjectState.MOUNTING, OlObjectState.MOUNTED].includes(_this11.$olObjectState);

                if (!mounted) {
                  _context14.next = 6;
                  break;
                }

                _context14.next = 6;
                return execUnmount.call(_this11);

              case 6:
                _context14.next = 8;
                return execDeinit.call(_this11);

              case 8:
                _context14.next = 10;
                return execInit.call(_this11);

              case 10:
                if (!mounted) {
                  _context14.next = 13;
                  break;
                }

                _context14.next = 13;
                return execMount.call(_this11);

              case 13:
                _context14.next = 16;
                break;

              case 15:
                if (process.env.VUELAYERS_DEBUG) {
                  _this11.$logger.log('recreate discarded');
                }

              case 16:
              case "end":
                return _context14.stop();
            }
          }
        }, _callee14);
      }))();
    },

    /**
     * @return {Promise<void>}
     */
    scheduleRecreate: function scheduleRecreate() {
      var _this12 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee15() {
        return _regeneratorRuntime.wrap(function _callee15$(_context15) {
          while (1) {
            switch (_context15.prev = _context15.next) {
              case 0:
                if (![OlObjectState.CREATING, OlObjectState.CREATED, OlObjectState.MOUNTING, OlObjectState.MOUNTED, OlObjectState.UNMOUNTING, OlObjectState.UNMOUNTED].includes(_this12.$olObjectState)) {
                  _context15.next = 6;
                  break;
                }

                if (process.env.VUELAYERS_DEBUG) {
                  _this12.$logger.log('recreate scheduled');
                }

                _context15.next = 4;
                return _this12.debounceRecreate();

              case 4:
                _context15.next = 7;
                break;

              case 6:
                if (process.env.VUELAYERS_DEBUG) {
                  _this12.$logger.log('recreate discarded');
                }

              case 7:
              case "end":
                return _context15.stop();
            }
          }
        }, _callee15);
      }))();
    },

    /**
     * Redefine for easy call in child components
     * @returns {Object}
     * @protected
     */
    getServices: function getServices() {
      return services.methods.getServices.call(this);
    },

    /**
     * @return {void}
     */
    subscribeAll: function subscribeAll() {
      subscribeToOlObjectEvents.call(this);
    },

    /**
     * @returns {Promise<Object>}
     * @throws {Error} If underlying OpenLayers object not initialized (incorrect initialization, already destroy).
     */
    resolveOlObject: function resolveOlObject() {
      var _this13 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee16() {
        return _regeneratorRuntime.wrap(function _callee16$(_context16) {
          while (1) {
            switch (_context16.prev = _context16.next) {
              case 0:
                _context16.next = 2;
                return _this13.$createPromise;

              case 2:
                return _context16.abrupt("return", _this13.$olObject || function (e) {
                  throw e;
                }(new OLObjectNotInitializedError("".concat(_this13.vmName, " OpenLayers object is undefined"))));

              case 3:
              case "end":
                return _context16.stop();
            }
          }
        }, _callee16);
      }))();
    },
    $nextTickPromise: function $nextTickPromise() {
      var _this14 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee17() {
        return _regeneratorRuntime.wrap(function _callee17$(_context17) {
          while (1) {
            switch (_context17.prev = _context17.next) {
              case 0:
                return _context17.abrupt("return", new Promise(_this14.$nextTick.bind(_this14)));

              case 1:
              case "end":
                return _context17.stop();
            }
          }
        }, _callee17);
      }))();
    },

    /**
     * @return {Promise<string|number>}
     */
    getId: function getId() {
      var _this15 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee18() {
        return _regeneratorRuntime.wrap(function _callee18$(_context18) {
          while (1) {
            switch (_context18.prev = _context18.next) {
              case 0:
                _context18.next = 2;
                return _this15.resolveOlObject();

              case 2:
                return _context18.abrupt("return", _this15.getIdInternal());

              case 3:
              case "end":
                return _context18.stop();
            }
          }
        }, _callee18);
      }))();
    },

    /**
     * @return {string|number}
     * @protected
     */
    getIdInternal: function getIdInternal() {
      if (isFunction(this.$olObject.get)) {
        return this.$olObject.get('id');
      }

      return this.$olObject.id;
    },

    /**
     * @param {string|number} id
     * @return {Promise<void>}
     */
    setId: function setId(id) {
      var _this16 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee19() {
        return _regeneratorRuntime.wrap(function _callee19$(_context19) {
          while (1) {
            switch (_context19.prev = _context19.next) {
              case 0:
                _context19.next = 2;
                return _this16.resolveOlObject();

              case 2:
                _this16.setIdInternal(id);

              case 3:
              case "end":
                return _context19.stop();
            }
          }
        }, _callee19);
      }))();
    },

    /**
     * @param {string|number} id
     * @protected
     */
    setIdInternal: function setIdInternal(id) {
      assert(id != null && id !== '', 'Invalid id');
      if (id === this.getIdInternal()) return;

      if (isFunction(this.$olObject.set)) {
        this.$olObject.set('id', id);
        return;
      }

      this.$olObject.id = id;
      ++this.rev;
    }
  }
};

function defineDebounceMethods() {
  this.debounceRefresh = debounce(function () {
    return this.refresh();
  }, FRAME_TIME);
  this.debounceRemount = debounce(function () {
    return this.remount();
  }, FRAME_TIME);
  this.debounceRecreate = debounce(function () {
    return this.recreate();
  }, FRAME_TIME);
}

function defineServices() {
  var _this17 = this;

  Object.defineProperties(this, {
    $vq: {
      enumerable: true,
      get: function get() {
        return VueQuery(_this17);
      }
    },

    /**
     * @type {{warn: (function(...[*]): void), log: (function(...[*]): void), error: (function(...[*]): void)}}
     */
    $logger: {
      enumerable: true,
      get: function get() {
        return _this17._logger;
      }
    },

    /**
     * @type {string}
     */
    $olObjectState: {
      enumerable: true,
      get: function get() {
        return _this17._olObjectState;
      }
    },

    /**
     * @type {module:ol/Object~BaseObject|undefined}
     */
    $olObject: {
      enumerable: true,
      get: function get() {
        return _this17._olObject;
      }
    },
    $olObjectEvents: {
      enumerable: true,
      get: function get() {
        return _this17._olObjectEvents;
      }
    },

    /**
     * @type {Promise<void>}
     */
    $createPromise: {
      enumerable: true,
      get: function get() {
        return newLifecyclePromise.call(_this17, OlObjectAction.CREATE, OlObjectEvent.CREATED, _defineProperty({}, OlObjectState.CREATING, [OlObjectAction.CREATE, OlObjectEvent.CREATED]));
      }
    },

    /**
     * @type {Promise<void>}
     */
    $mountPromise: {
      enumerable: true,
      get: function get() {
        return _this17.$createPromise.then(function () {
          var _newLifecyclePromise$2;

          return newLifecyclePromise.call(_this17, OlObjectAction.MOUNT, OlObjectEvent.MOUNTED, (_newLifecyclePromise$2 = {}, _defineProperty(_newLifecyclePromise$2, OlObjectState.CREATING, [OlObjectAction.CREATE, OlObjectEvent.CREATED]), _defineProperty(_newLifecyclePromise$2, OlObjectState.MOUNTING, [OlObjectAction.MOUNT, OlObjectEvent.MOUNTED]), _newLifecyclePromise$2));
        });
      }
    },

    /**
     * @type {Promise<void>}
     */
    $unmountPromise: {
      enumerable: true,
      get: function get() {
        return _this17.$mountPromise.then(function () {
          var _newLifecyclePromise$3;

          return newLifecyclePromise.call(_this17, OlObjectAction.UNMOUNT, OlObjectEvent.UNMOUNTED, (_newLifecyclePromise$3 = {}, _defineProperty(_newLifecyclePromise$3, OlObjectState.CREATING, [OlObjectAction.CREATE, OlObjectEvent.CREATED]), _defineProperty(_newLifecyclePromise$3, OlObjectState.MOUNTING, [OlObjectAction.MOUNT, OlObjectEvent.MOUNTED]), _defineProperty(_newLifecyclePromise$3, OlObjectState.UNMOUNTING, [OlObjectAction.UNMOUNT, OlObjectEvent.UNMOUNTED]), _newLifecyclePromise$3));
        });
      }
    },

    /**
     * @type {Promise<void>}
     */
    $destroyPromise: {
      enumerable: true,
      get: function get() {
        return _this17.$unmountPromise.then(function () {
          var _newLifecyclePromise$4;

          return newLifecyclePromise.call(_this17, OlObjectAction.DESTROY, OlObjectEvent.DESTROYED, (_newLifecyclePromise$4 = {}, _defineProperty(_newLifecyclePromise$4, OlObjectState.CREATING, [OlObjectAction.CREATE, OlObjectEvent.CREATED]), _defineProperty(_newLifecyclePromise$4, OlObjectState.MOUNTING, [OlObjectAction.MOUNT, OlObjectEvent.MOUNTED]), _defineProperty(_newLifecyclePromise$4, OlObjectState.UNMOUNTING, [OlObjectAction.UNMOUNT, OlObjectEvent.UNMOUNTED]), _defineProperty(_newLifecyclePromise$4, OlObjectState.DESTROYING, [OlObjectAction.DESTROY, OlObjectEvent.DESTROYED]), _newLifecyclePromise$4));
        });
      }
    }
  });
}

function newOlObjectEventsObs() {
  var _this18 = this;

  return merge.apply(void 0, _toConsumableArray(Object.values(OlObjectEvent).map(function (name) {
    return fromVueEvent(_this18.$eventBus, name, function (args) {
      return {
        name: name,
        args: isArray(args) ? args : [args]
      };
    }).pipe(filter(function (_ref2) {
      var args = _ref2.args;
      return args[0] === _this18 || args[1] === _this18;
    }));
  }))).pipe(publishReplay(), refCount());
}

function newLifecyclePromise(action, endEvent, startStates) {
  var _this19 = this;

  var newFinishObs = function newFinishObs(action, endEvent) {
    return race(_this19.$olObjectEvents.pipe(filter(function (_ref3) {
      var name = _ref3.name;
      return name === endEvent;
    })), _this19.$olObjectEvents.pipe(filter(function (_ref4) {
      var name = _ref4.name,
          args = _ref4.args;
      return name === OlObjectEvent.ERROR && args[0] instanceof LifecycleError && args[0].action === action;
    })));
  };

  return merge(newFinishObs(action, endEvent), this.$olObjectEvents.pipe(filter(function (_ref5) {
    var name = _ref5.name,
        args = _ref5.args;
    return name === OlObjectEvent.ERROR && args[0] instanceof CanceledError;
  }))).pipe(first(), mergeMap(function (evt) {
    var obs = of(evt); // If at the time of CancelError the ol object stay in some intermediate
    // we need to delay observable emit until current hook complete

    if (evt.name === OlObjectEvent.ERROR && evt.args[0] instanceof CanceledError && startStates[_this19.$olObjectState] != null) {
      obs = obs.pipe(delayWhen(function () {
        return newFinishObs.apply(void 0, _toConsumableArray(startStates[_this19.$olObjectState]));
      }));
    }

    return obs;
  }), mergeMap(function (evt) {
    if (evt.name === OlObjectEvent.ERROR) {
      return throwError(evt.args[0]);
    }

    return of(evt);
  }), map(function (_ref6) {
    var args = _ref6.args;
    return args.length === 1 ? args[0] : args;
  })).toPromise();
}
/**
 * @param {boolean} [resetEventsObs=true]
 * @returns {Promise<void>}
 * @private
 */


function execInit() {
  return _execInit.apply(this, arguments);
}
/**
 * @return {Promise<void>}
 * @private
 */


function _execInit() {
  _execInit = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee20() {
    var resetEventsObs,
        prevState,
        lerr,
        _args20 = arguments;
    return _regeneratorRuntime.wrap(function _callee20$(_context20) {
      while (1) {
        switch (_context20.prev = _context20.next) {
          case 0:
            resetEventsObs = _args20.length > 0 && _args20[0] !== undefined ? _args20[0] : true;
            prevState = this._olObjectState;

            if (resetEventsObs) {
              this._olObjectEvents = newOlObjectEventsObs.call(this);
            }

            this._olObjectEvents.subscribe();

            _context20.prev = 4;
            this._olObjectState = OlObjectState.CREATING;
            _context20.next = 8;
            return this.beforeInit();

          case 8:
            _context20.next = 10;
            return this.init();

          case 10:
            this._olObjectState = OlObjectState.CREATED;

            if (process.env.VUELAYERS_DEBUG) {
              this.$logger.log("ol object ".concat(OlObjectEvent.CREATED));
            }

            this.$emit(OlObjectEvent.CREATED, this);
            this.$eventBus.$emit(OlObjectEvent.CREATED, this);
            _context20.next = 23;
            break;

          case 16:
            _context20.prev = 16;
            _context20.t0 = _context20["catch"](4);
            lerr = new LifecycleError(_context20.t0, this.vmName, OlObjectAction.CREATE);
            this._olObjectState = prevState;
            this.$emit(OlObjectEvent.ERROR, lerr, this);
            this.$eventBus.$emit(OlObjectEvent.ERROR, lerr, this);
            throw _context20.t0;

          case 23:
          case "end":
            return _context20.stop();
        }
      }
    }, _callee20, this, [[4, 16]]);
  }));
  return _execInit.apply(this, arguments);
}

function execMount() {
  return _execMount.apply(this, arguments);
}
/**
 * @param {boolean} [fireCancel=false]
 * @return {void|Promise<void>}
 * @private
 */


function _execMount() {
  _execMount = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee21() {
    var prevState, werr;
    return _regeneratorRuntime.wrap(function _callee21$(_context21) {
      while (1) {
        switch (_context21.prev = _context21.next) {
          case 0:
            prevState = this._olObjectState;
            _context21.prev = 1;
            _context21.next = 4;
            return this.$createPromise;

          case 4:
            this._olObjectState = OlObjectState.MOUNTING;
            _context21.next = 7;
            return this.beforeMount();

          case 7:
            _context21.next = 9;
            return this.mount();

          case 9:
            this._olObjectState = OlObjectState.MOUNTED;

            if (process.env.VUELAYERS_DEBUG) {
              this.$logger.log("ol object ".concat(OlObjectEvent.MOUNTED));
            }

            this.$emit(OlObjectEvent.MOUNTED, this);
            this.$eventBus.$emit(OlObjectEvent.MOUNTED, this);
            _context21.next = 25;
            break;

          case 15:
            _context21.prev = 15;
            _context21.t0 = _context21["catch"](1);

            if (!(_context21.t0 instanceof CanceledError)) {
              _context21.next = 20;
              break;
            }

            if (process.env.VUELAYERS_DEBUG) {
              this.$logger.log("ol object ".concat(OlObjectAction.MOUNT, " canceled"));
            }

            return _context21.abrupt("return");

          case 20:
            werr = new LifecycleError(_context21.t0, this.vmName, OlObjectAction.MOUNT);
            this._olObjectState = prevState;
            this.$emit(OlObjectEvent.ERROR, werr, this);
            this.$eventBus.$emit(OlObjectEvent.ERROR, werr, this);
            throw _context21.t0;

          case 25:
          case "end":
            return _context21.stop();
        }
      }
    }, _callee21, this, [[1, 15]]);
  }));
  return _execMount.apply(this, arguments);
}

function execUnmount() {
  return _execUnmount.apply(this, arguments);
}
/**
 * @returns {Promise<void>}
 * @private
 */


function _execUnmount() {
  _execUnmount = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee22() {
    var fireCancel,
        prevState,
        err,
        werr,
        _args22 = arguments;
    return _regeneratorRuntime.wrap(function _callee22$(_context22) {
      while (1) {
        switch (_context22.prev = _context22.next) {
          case 0:
            fireCancel = _args22.length > 0 && _args22[0] !== undefined ? _args22[0] : false;
            prevState = this._olObjectState;

            if (fireCancel) {
              if ([OlObjectState.UNDEF, OlObjectState.CREATING, OlObjectState.CREATED, OlObjectState.MOUNTING].includes(this.$olObjectState)) {
                err = new CanceledError("".concat(this.vmName, " ol object lifecycle canceled"));
                this.$emit(OlObjectEvent.ERROR, err, this);
                this.$eventBus.$emit(OlObjectEvent.ERROR, err, this);
              }
            }

            _context22.prev = 3;
            _context22.prev = 4;
            _context22.next = 7;
            return this.$mountPromise;

          case 7:
            _context22.next = 18;
            break;

          case 9:
            _context22.prev = 9;
            _context22.t0 = _context22["catch"](4);

            if (!(_context22.t0 instanceof CanceledError)) {
              _context22.next = 17;
              break;
            }

            if (!(this.$olObjectState !== OlObjectState.MOUNTED)) {
              _context22.next = 15;
              break;
            }

            if (process.env.VUELAYERS_DEBUG) {
              this.$logger.log("ol object ".concat(OlObjectAction.UNMOUNT, " canceled"));
            }

            return _context22.abrupt("return");

          case 15:
            _context22.next = 18;
            break;

          case 17:
            throw _context22.t0;

          case 18:
            this._olObjectState = OlObjectState.UNMOUNTING;
            _context22.next = 21;
            return this.unmount();

          case 21:
            this._olObjectState = OlObjectState.UNMOUNTED;

            if (process.env.VUELAYERS_DEBUG) {
              this.$logger.log("ol object ".concat(OlObjectEvent.UNMOUNTED));
            }

            this.$emit(OlObjectEvent.UNMOUNTED, this);
            this.$eventBus.$emit(OlObjectEvent.UNMOUNTED, this);
            _context22.next = 34;
            break;

          case 27:
            _context22.prev = 27;
            _context22.t1 = _context22["catch"](3);
            werr = new LifecycleError(_context22.t1, this.vmName, OlObjectAction.UNMOUNT);
            this._olObjectState = prevState;
            this.$emit(OlObjectEvent.ERROR, this, werr);
            this.$eventBus.$emit(OlObjectEvent.ERROR, this, werr);
            throw _context22.t1;

          case 34:
          case "end":
            return _context22.stop();
        }
      }
    }, _callee22, this, [[3, 27], [4, 9]]);
  }));
  return _execUnmount.apply(this, arguments);
}

function execDeinit() {
  return _execDeinit.apply(this, arguments);
}

function _execDeinit() {
  _execDeinit = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee23() {
    var prevState, werr;
    return _regeneratorRuntime.wrap(function _callee23$(_context23) {
      while (1) {
        switch (_context23.prev = _context23.next) {
          case 0:
            prevState = this._olObjectState;
            _context23.prev = 1;
            _context23.prev = 2;
            _context23.next = 5;
            return this.$unmountPromise;

          case 5:
            _context23.next = 16;
            break;

          case 7:
            _context23.prev = 7;
            _context23.t0 = _context23["catch"](2);

            if (!(_context23.t0 instanceof CanceledError)) {
              _context23.next = 15;
              break;
            }

            if (!(this.$olObjectState !== OlObjectState.CREATED)) {
              _context23.next = 13;
              break;
            }

            if (process.env.VUELAYERS_DEBUG) {
              this.$logger.log("ol object ".concat(OlObjectAction.DESTROY, " canceled"));
            }

            return _context23.abrupt("return");

          case 13:
            _context23.next = 16;
            break;

          case 15:
            throw _context23.t0;

          case 16:
            this._olObjectState = OlObjectState.DESTROYING;
            _context23.next = 19;
            return this.deinit();

          case 19:
            this._olObjectState = OlObjectState.DESTROYED;

            if (process.env.VUELAYERS_DEBUG) {
              this.$logger.log("ol object ".concat(OlObjectEvent.DESTROYED));
            }

            this.$emit(OlObjectEvent.DESTROYED, this);
            this.$eventBus.$emit(OlObjectEvent.DESTROYED, this);
            _context23.next = 32;
            break;

          case 25:
            _context23.prev = 25;
            _context23.t1 = _context23["catch"](1);
            werr = new LifecycleError(_context23.t1, this.vmName, OlObjectAction.DESTROY);
            this._olObjectState = prevState;
            this.$emit(OlObjectEvent.ERROR, this, werr);
            this.$eventBus.$emit(OlObjectEvent.ERROR, this, werr);
            throw _context23.t1;

          case 32:
          case "end":
            return _context23.stop();
        }
      }
    }, _callee23, this, [[1, 25], [2, 7]]);
  }));
  return _execDeinit.apply(this, arguments);
}

function subscribeToOlObjectEvents() {
  var _this20 = this;

  if (!(this.$olObject instanceof Observable)) return;
  var changes = fromOlEvent(this.$olObject, EventType.CHANGE).pipe(bufferDebounceTime(FRAME_TIME));
  this.subscribeTo(changes, function () {
    ++_this20.rev;
  });
  var errors = fromOlEvent(this.$olObject, EventType.ERROR);
  this.subscribeTo(errors, function (evt) {
    return _this20.$emit(OlObjectEvent.ERROR, evt);
  });
}

var LifecycleError = /*#__PURE__*/function (_Error) {
  _inherits(LifecycleError, _Error);

  var _super = _createSuper(LifecycleError);

  function LifecycleError(err, vmName, action) {
    var _this21;

    _classCallCheck(this, LifecycleError);

    _this21 = _super.call(this, "".concat(vmName, " ").concat(action, " ol object failed"));

    _defineProperty(_assertThisInitialized(_this21), "name", 'LifecycleError');

    _this21.vmName = vmName;
    _this21.action = action;
    _this21.err = err;
    return _this21;
  }

  _createClass(LifecycleError, [{
    key: "unwrap",
    value: function unwrap() {
      return this.err;
    }
  }]);

  return LifecycleError;
}( /*#__PURE__*/_wrapNativeSuper(Error));
var CanceledError = /*#__PURE__*/function (_Error2) {
  _inherits(CanceledError, _Error2);

  var _super2 = _createSuper(CanceledError);

  function CanceledError() {
    var _this22;

    _classCallCheck(this, CanceledError);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this22 = _super2.call.apply(_super2, [this].concat(args));

    _defineProperty(_assertThisInitialized(_this22), "name", 'CanceledError');

    return _this22;
  }

  return CanceledError;
}( /*#__PURE__*/_wrapNativeSuper(Error));
function isCreateError(err) {
  return err instanceof LifecycleError && err.action === OlObjectAction.CREATE;
}
function isMountError(err) {
  return err instanceof LifecycleError && err.action === OlObjectAction.MOUNT;
}
function isUnmountError(err) {
  return err instanceof LifecycleError && err.action === OlObjectAction.UNMOUNT;
}
function isDestroyError(err) {
  return err instanceof LifecycleError && err.action === OlObjectAction.DESTROY;
}
var OLObjectNotInitializedError = /*#__PURE__*/function (_Error3) {
  _inherits(OLObjectNotInitializedError, _Error3);

  var _super3 = _createSuper(OLObjectNotInitializedError);

  function OLObjectNotInitializedError() {
    var _this23;

    _classCallCheck(this, OLObjectNotInitializedError);

    for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
      args[_key2] = arguments[_key2];
    }

    _this23 = _super3.call.apply(_super3, [this].concat(args));

    _defineProperty(_assertThisInitialized(_this23), "name", 'OLObjectNotInitializedError');

    return _this23;
  }

  return OLObjectNotInitializedError;
}( /*#__PURE__*/_wrapNativeSuper(Error));

/**
 * Mixin with helpers for projection transforms between current view projection and global defined projection.
 */

var projTransforms = {
  computed: {
    /**
     * @return {module:ol/proj~ProjectionLike}
     */
    resolvedViewProjection: function resolvedViewProjection() {
      return coalesce(this.viewProjection, EPSG_3857);
    },

    /**
     * @return {module:ol/proj~ProjectionLike}
     */
    resolvedDataProjection: function resolvedDataProjection() {
      var _this$$options;

      return coalesce(this.projection, // may or may not be present
      (_this$$options = this.$options) === null || _this$$options === void 0 ? void 0 : _this$$options.dataProjection, // may or may not be present
      this.dataProjection, // may or may not be present
      this.resolvedViewProjection);
    }
  },
  methods: {
    pointToViewProj: function pointToViewProj(point) {
      var precision = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : COORD_PRECISION;
      return transformPoint(point, this.resolvedDataProjection, this.resolvedViewProjection, precision);
    },
    pointToDataProj: function pointToDataProj(point) {
      var precision = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : COORD_PRECISION;
      return transformPoint(point, this.resolvedViewProjection, this.resolvedDataProjection, precision);
    },
    lineToViewProj: function lineToViewProj(line) {
      var precision = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : COORD_PRECISION;
      return transformLine(line, this.resolvedDataProjection, this.resolvedViewProjection, precision);
    },
    lineToDataProj: function lineToDataProj(line) {
      var precision = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : COORD_PRECISION;
      return transformLine(line, this.resolvedViewProjection, this.resolvedDataProjection, precision);
    },
    polygonToViewProj: function polygonToViewProj(polygon) {
      var precision = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : COORD_PRECISION;
      return transformPolygon(polygon, this.resolvedDataProjection, this.resolvedViewProjection, precision);
    },
    polygonToDataProj: function polygonToDataProj(polygon) {
      var precision = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : COORD_PRECISION;
      return transformPolygon(polygon, this.resolvedViewProjection, this.resolvedDataProjection, precision);
    },
    multiPointToViewProj: function multiPointToViewProj(multiPoint) {
      var precision = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : COORD_PRECISION;
      return transformMultiPoint(multiPoint, this.resolvedDataProjection, this.resolvedViewProjection, precision);
    },
    multiPointToDataProj: function multiPointToDataProj(multiPoint) {
      var precision = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : COORD_PRECISION;
      return transformMultiPoint(multiPoint, this.resolvedViewProjection, this.resolvedDataProjection, precision);
    },
    multiLineToViewProj: function multiLineToViewProj(multiLine) {
      var precision = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : COORD_PRECISION;
      return transformMultiLine(multiLine, this.resolvedDataProjection, this.resolvedViewProjection, precision);
    },
    multiLineToDataProj: function multiLineToDataProj(multiLine) {
      var precision = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : COORD_PRECISION;
      return transformMultiLine(multiLine, this.resolvedViewProjection, this.resolvedDataProjection, precision);
    },
    multiPolygonToViewProj: function multiPolygonToViewProj(multiPolygon) {
      var precision = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : COORD_PRECISION;
      return transformMultiPolygon(multiPolygon, this.resolvedDataProjection, this.resolvedViewProjection, precision);
    },
    multiPolygonToDataProj: function multiPolygonToDataProj(multiPolygon) {
      var precision = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : COORD_PRECISION;
      return transformMultiPolygon(multiPolygon, this.resolvedViewProjection, this.resolvedDataProjection, precision);
    },
    extentToViewProj: function extentToViewProj(extent) {
      var precision = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : COORD_PRECISION;
      return transformExtent$1(extent, this.resolvedDataProjection, this.resolvedViewProjection, precision);
    },
    extentToDataProj: function extentToDataProj(extent) {
      var precision = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : COORD_PRECISION;
      return transformExtent$1(extent, this.resolvedViewProjection, this.resolvedDataProjection, precision);
    },
    writeGeometryInDataProj: function writeGeometryInDataProj(geometry) {
      var precision = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : COORD_PRECISION;
      if (!geometry) return;
      var key = this.makeGeometryKey(geometry, this.resolvedDataProjection, precision);
      var geometryJson = this.getProjTransformCache().get(key);

      if (geometryJson) {
        return geometryJson;
      }

      geometryJson = writeGeoJsonGeometry(geometry, this.resolvedViewProjection, this.resolvedDataProjection, precision);
      this.getProjTransformCache().set(key, geometryJson);
      return geometryJson;
    },
    writeGeometryInViewProj: function writeGeometryInViewProj(geometry) {
      var precision = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : COORD_PRECISION;
      if (!geometry) return;
      var key = this.makeGeometryKey(geometry, this.resolvedViewProjection, precision);
      var geometryJson = this.getProjTransformCache().get(key);

      if (geometryJson) {
        return geometryJson;
      }

      geometryJson = writeGeoJsonGeometry(geometry, this.resolvedViewProjection, this.resolvedViewProjection, precision);
      this.getProjTransformCache().set(key, geometryJson);
      return geometryJson;
    },
    readGeometryInDataProj: function readGeometryInDataProj(geometry) {
      var precision = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : COORD_PRECISION;
      if (!geometry) return;
      return readGeoJsonGeometry(geometry, this.resolvedViewProjection, this.resolvedDataProjection, precision);
    },
    readGeometryInViewProj: function readGeometryInViewProj(geometry) {
      var precision = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : COORD_PRECISION;
      if (!geometry) return;
      return readGeoJsonGeometry(geometry, this.resolvedViewProjection, this.resolvedViewProjection, precision);
    },
    writeFeatureInDataProj: function writeFeatureInDataProj(feature) {
      var precision = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : COORD_PRECISION;
      if (!feature) return;
      var key = this.makeFeatureKey(feature, this.resolvedDataProjection, precision);
      var featureJson = this.getProjTransformCache().get(key);

      if (featureJson) {
        return featureJson;
      }

      featureJson = writeGeoJsonFeature(feature, this.resolvedViewProjection, this.resolvedDataProjection, precision);
      this.getProjTransformCache().set(key, featureJson);
      return featureJson;
    },
    writeFeatureInViewProj: function writeFeatureInViewProj(feature) {
      var precision = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : COORD_PRECISION;
      if (!feature) return;
      var key = this.makeFeatureKey(feature, this.resolvedViewProjection, precision);
      var featureJson = this.getProjTransformCache().get(key);

      if (featureJson) {
        return featureJson;
      }

      featureJson = writeGeoJsonFeature(feature, this.resolvedViewProjection, this.resolvedViewProjection, precision);
      this.getProjTransformCache().set(key, featureJson);
      return featureJson;
    },
    readFeatureInDataProj: function readFeatureInDataProj(feature) {
      var precision = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : COORD_PRECISION;
      if (!feature) return;
      return readGeoJsonFeature(feature, this.resolvedViewProjection, this.resolvedDataProjection, precision);
    },
    readFeatureInViewProj: function readFeatureInViewProj(feature) {
      var precision = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : COORD_PRECISION;
      if (!feature) return;
      return readGeoJsonFeature(feature, this.resolvedViewProjection, this.resolvedViewProjection, precision);
    },

    /**
     * @return {LRUCache}
     * @protected
     */
    getProjTransformCache: function getProjTransformCache() {
      if (!this._projTransformCache) {
        this._projTransformCache = new LRU({
          max: 10e3,
          maxAge: 3600e3
        });
      }

      return this._projTransformCache;
    },

    /**
     * @param object
     * @param projection
     * @param precision
     * @return {string}
     * @protected
     */
    makeKey: function makeKey(object, projection, precision) {
      return serialize$1({
        object: object,
        projection: projection,
        precision: precision
      });
    },

    /**
     * @param geometry
     * @param projection
     * @param precision
     * @return {string}
     * @protected
     */
    makeGeometryKey: function makeGeometryKey(geometry, projection, precision) {
      return this.makeKey({
        type: getGeomType(geometry),
        coordinates: getGeomCoords(geometry)
      }, projection, precision);
    },

    /**
     * @param feature
     * @param projection
     * @param precision
     * @return {string}
     * @protected
     */
    makeFeatureKey: function makeFeatureKey(feature, projection, precision) {
      return this.makeKey({
        id: getFeatureId(feature),
        properties: getFeatureProperties(feature),
        geometry: getFeatureGeom(feature) ? this.makeGeometryKey(getFeatureGeom(feature), projection, precision) : null
      }, projection, precision);
    }
  }
};

/**
 * Renders stub VNode for component.
 */

var stubVNode = {
  /**
   * @param {function} h
   * @returns {VNode}
   */
  render: function render(h) {
    var _this$$options;

    var options = ((_this$$options = this.$options) === null || _this$$options === void 0 ? void 0 : _this$$options.stubVNode) || {}; // render as HTML comment

    if (options.empty) {
      var vnode = h();

      if (typeof options.empty === 'string') {
        vnode.text = options.empty;
      } else if (typeof options.empty === 'function') {
        vnode.text = options.empty.call(this);
      }

      return vnode;
    }

    var children;

    if (options.slots === false) {
      children = undefined;
    } else {
      children = extractChildren(this.$slots, options.slots);
    }

    var attrs = typeof options.attrs === 'function' ? options.attrs.call(this) : options.attrs;
    var data = {
      attrs: attrs,
      style: {
        display: 'none !important'
      }
    };
    return h(options.tag || 'i', data, children);
  }
};

var waitForMap = {
  methods: {
    beforeInit: function beforeInit() {
      var _this = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                _context.prev = 0;
                _context.next = 3;
                return waitFor(function () {
                  return _this.$mapVm != null;
                }, race(_this.$olObjectEvents.pipe(filter(function (_ref) {
                  var name = _ref.name,
                      args = _ref.args;
                  return name === OlObjectEvent.ERROR && args[0] instanceof CanceledError;
                })), fromVueEvent(_this.$eventBus, OlObjectEvent.ERROR).pipe(filter(function (_ref2) {
                  var _ref3 = _slicedToArray(_ref2, 2),
                      err = _ref3[0],
                      vm = _ref3[1];

                  return isCreateError(err) && hasProp(vm, '$map') && _this.$vq.closest(vm);
                }))).pipe(mapTo(stubTrue())));

              case 3:
                _this.viewProjection = _this.$mapVm.resolvedViewProjection;
                _this.dataProjection = _this.$mapVm.resolvedDataProjection;

                _this.subscribeTo(fromVueWatcher(_this, function () {
                  return _this.$mapVm.resolvedViewProjection;
                }), function (_ref4) {
                  var value = _ref4.value;
                  _this.viewProjection = value;
                });

                _this.subscribeTo(fromVueWatcher(_this, function () {
                  return _this.$mapVm.resolvedDataProjection;
                }), function (_ref5) {
                  var value = _ref5.value;
                  _this.dataProjection = value;
                });

                _context.next = 9;
                return _this.$nextTickPromise();

              case 9:
                return _context.abrupt("return", olCmp.methods.beforeInit.call(_this));

              case 12:
                _context.prev = 12;
                _context.t0 = _context["catch"](0);
                _context.t0.message = "".concat(_this.vmName, " wait for $mapVm injection: ").concat(_context.t0.message);
                throw _context.t0;

              case 16:
              case "end":
                return _context.stop();
            }
          }
        }, _callee, null, [[0, 12]]);
      }))();
    }
  }
};

function ownKeys$1(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$1(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$1(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$1(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
/**
 * Base layer mixin.
 */

var baseLayer = {
  mixins: [stubVNode, projTransforms, olCmp, waitForMap],
  stubVNode: {
    attrs: function attrs() {
      return {
        id: this.vmId,
        class: this.vmClass
      };
    }
  },
  props: {
    // ol/layer/Base

    /**
     * A CSS class name to set to the layer element.
     * @type {string}
     */
    className: {
      type: String,
      default: 'ol-layer'
    },

    /**
     * @type {number}
     */
    opacity: {
      type: Number,
      default: 1
    },

    /**
     * @type {boolean}
     */
    visible: {
      type: Boolean,
      default: true
    },

    /**
     * @type {number[]|undefined}
     */
    extent: {
      type: Array,
      validator: function validator(value) {
        return value.length === 4 && value.every(isNumber);
      }
    },
    extentProjection: {
      type: String,
      validator: function validator(value) {
        return !!get(value);
      }
    },

    /**
     * @type {number|undefined}
     */
    zIndex: Number,

    /**
     * @type {number|undefined}
     */
    minResolution: Number,

    /**
     * @type {number|undefined}
     */
    maxResolution: Number,

    /**
     * @type {number|undefined}
     */
    minZoom: Number,

    /**
     * @type {number|undefined}
     */
    maxZoom: Number
  },
  data: function data() {
    return {
      viewProjection: EPSG_3857,
      dataProjection: EPSG_3857
    };
  },
  computed: {
    resolvedExtentProjection: function resolvedExtentProjection() {
      return this.extentProjection || this.resolvedDataProjection;
    },
    extentDataProj: function extentDataProj() {
      return roundExtent(this.extent);
    },
    extentViewProj: function extentViewProj() {
      return transformExtent$1(this.extent, this.resolvedExtentProjection, this.resolvedViewProjection);
    },
    currentExtentDataProj: function currentExtentDataProj() {
      if (this.rev && this.$layer) {
        return this.getExtentInternal();
      }

      return this.extentDataProj;
    },
    currentExtentViewProj: function currentExtentViewProj() {
      if (this.rev && this.$layer) {
        return this.getExtentInternal(true);
      }

      return this.extentViewProj;
    },
    currentMaxResolution: function currentMaxResolution() {
      if (this.rev && this.$layer) {
        return this.getMaxResolutionInternal();
      }

      return this.maxResolution;
    },
    currentMinResolution: function currentMinResolution() {
      if (this.rev && this.$layer) {
        return this.getMinResolutionInternal();
      }

      return this.minResolution;
    },
    currentMaxZoom: function currentMaxZoom() {
      if (this.rev && this.$layer) {
        return this.getMaxZoomInternal();
      }

      return this.maxZoom;
    },
    currentMinZoom: function currentMinZoom() {
      if (this.rev && this.$layer) {
        return this.getMinZoomInternal();
      }

      return this.minZoom;
    },
    currentOpacity: function currentOpacity() {
      if (this.rev && this.$layer) {
        return this.getOpacityInternal();
      }

      return this.opacity;
    },
    currentVisible: function currentVisible() {
      if (this.rev && this.$layer) {
        return this.getVisibleInternal();
      }

      return this.visible;
    },
    currentZIndex: function currentZIndex() {
      if (this.rev && this.$layer) {
        return this.getZIndexInternal();
      }

      return this.zIndex;
    }
  },
  watch: {
    opacity: /*#__PURE__*/sequential( /*#__PURE__*/function () {
      var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(value) {
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                _context.next = 2;
                return this.setOpacity(value);

              case 2:
              case "end":
                return _context.stop();
            }
          }
        }, _callee, this);
      }));

      return function (_x) {
        return _ref.apply(this, arguments);
      };
    }()),
    currentOpacity: /*#__PURE__*/debounce(function (value) {
      if (value === this.opacity) return;
      this.$emit('update:opacity', value);
    }, FRAME_TIME),
    visible: /*#__PURE__*/sequential( /*#__PURE__*/function () {
      var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(value) {
        return _regeneratorRuntime.wrap(function _callee2$(_context2) {
          while (1) {
            switch (_context2.prev = _context2.next) {
              case 0:
                _context2.next = 2;
                return this.setVisible(value);

              case 2:
              case "end":
                return _context2.stop();
            }
          }
        }, _callee2, this);
      }));

      return function (_x2) {
        return _ref2.apply(this, arguments);
      };
    }()),
    currentVisible: /*#__PURE__*/debounce(function (value) {
      if (value === this.visible) return;
      this.$emit('update:visible', value);
    }, FRAME_TIME),
    extentViewProj: /*#__PURE__*/sequential( /*#__PURE__*/function () {
      var _ref3 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3(value) {
        return _regeneratorRuntime.wrap(function _callee3$(_context3) {
          while (1) {
            switch (_context3.prev = _context3.next) {
              case 0:
                _context3.next = 2;
                return this.setExtent(value, true);

              case 2:
              case "end":
                return _context3.stop();
            }
          }
        }, _callee3, this);
      }));

      return function (_x3) {
        return _ref3.apply(this, arguments);
      };
    }()),
    currentExtentDataProj: /*#__PURE__*/debounce(function (value) {
      if (isEqual(value, this.extentDataProj)) return;
      this.$emit('update:extent', value.slice());
    }, FRAME_TIME),
    zIndex: /*#__PURE__*/sequential( /*#__PURE__*/function () {
      var _ref4 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee4(value) {
        return _regeneratorRuntime.wrap(function _callee4$(_context4) {
          while (1) {
            switch (_context4.prev = _context4.next) {
              case 0:
                _context4.next = 2;
                return this.setZIndex(value);

              case 2:
              case "end":
                return _context4.stop();
            }
          }
        }, _callee4, this);
      }));

      return function (_x4) {
        return _ref4.apply(this, arguments);
      };
    }()),
    currentZIndex: /*#__PURE__*/debounce(function (value) {
      if (value === this.zIndex) return;
      this.$emit('update:zIndex', value);
    }, FRAME_TIME),
    minResolution: /*#__PURE__*/sequential( /*#__PURE__*/function () {
      var _ref5 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee5(value) {
        return _regeneratorRuntime.wrap(function _callee5$(_context5) {
          while (1) {
            switch (_context5.prev = _context5.next) {
              case 0:
                _context5.next = 2;
                return this.setMinResolution(value);

              case 2:
              case "end":
                return _context5.stop();
            }
          }
        }, _callee5, this);
      }));

      return function (_x5) {
        return _ref5.apply(this, arguments);
      };
    }()),
    currentMinResolution: /*#__PURE__*/debounce(function (value) {
      if (value === this.minResolution) return;
      this.$emit('update:minResolution', value);
    }, FRAME_TIME),
    maxResolution: /*#__PURE__*/sequential( /*#__PURE__*/function () {
      var _ref6 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee6(value) {
        return _regeneratorRuntime.wrap(function _callee6$(_context6) {
          while (1) {
            switch (_context6.prev = _context6.next) {
              case 0:
                _context6.next = 2;
                return this.setMaxResolution(value);

              case 2:
              case "end":
                return _context6.stop();
            }
          }
        }, _callee6, this);
      }));

      return function (_x6) {
        return _ref6.apply(this, arguments);
      };
    }()),
    currentMaxResolution: /*#__PURE__*/debounce(function (value) {
      if (value === this.maxResolution) return;
      this.$emit('update:maxResolution', value);
    }, FRAME_TIME),
    minZoom: /*#__PURE__*/sequential( /*#__PURE__*/function () {
      var _ref7 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee7(value) {
        return _regeneratorRuntime.wrap(function _callee7$(_context7) {
          while (1) {
            switch (_context7.prev = _context7.next) {
              case 0:
                _context7.next = 2;
                return this.setMinZoom(value);

              case 2:
              case "end":
                return _context7.stop();
            }
          }
        }, _callee7, this);
      }));

      return function (_x7) {
        return _ref7.apply(this, arguments);
      };
    }()),
    currentMinZoom: /*#__PURE__*/debounce(function (value) {
      if (value === this.minZoom) return;
      this.$emit('update:minZoom', value);
    }, FRAME_TIME),
    maxZoom: /*#__PURE__*/sequential( /*#__PURE__*/function () {
      var _ref8 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee8(value) {
        return _regeneratorRuntime.wrap(function _callee8$(_context8) {
          while (1) {
            switch (_context8.prev = _context8.next) {
              case 0:
                _context8.next = 2;
                return this.setMaxZoom(value);

              case 2:
              case "end":
                return _context8.stop();
            }
          }
        }, _callee8, this);
      }));

      return function (_x8) {
        return _ref8.apply(this, arguments);
      };
    }()),
    currentMaxZoom: /*#__PURE__*/debounce(function (value) {
      if (value === this.maxZoom) return;
      this.$emit('update:maxZoom', value);
    }, FRAME_TIME)
  },
  created: function created() {
    defineServices$1.call(this);
  },
  methods: _objectSpread$1(_objectSpread$1(_objectSpread$1({
    /**
     * @return {Promise<module:ol/layer/Base~BaseLayer>}
     * @protected
     */
    createOlObject: function createOlObject() {
      var _this = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee9() {
        return _regeneratorRuntime.wrap(function _callee9$(_context9) {
          while (1) {
            switch (_context9.prev = _context9.next) {
              case 0:
                _context9.t0 = initializeLayer;
                _context9.next = 3;
                return _this.createLayer();

              case 3:
                _context9.t1 = _context9.sent;
                _context9.t2 = _this.currentId;
                return _context9.abrupt("return", (0, _context9.t0)(_context9.t1, _context9.t2));

              case 6:
              case "end":
                return _context9.stop();
            }
          }
        }, _callee9);
      }))();
    },

    /**
     * @return {module:ol/layer/Base~BaseLayer|Promise<module:ol/layer/Base~BaseLayer>}
     * @protected
     * @abstract
     */
    createLayer: function createLayer() {
      throw new Error("".concat(this.vmName, " not implemented method: createLayer"));
    },

    /**
     * @return {Promise<void>}
     * @protected
     */
    mount: function mount() {
      var _this2 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee10() {
        return _regeneratorRuntime.wrap(function _callee10$(_context10) {
          while (1) {
            switch (_context10.prev = _context10.next) {
              case 0:
                if (!_this2.$layersContainer) {
                  _context10.next = 3;
                  break;
                }

                _context10.next = 3;
                return _this2.$layersContainer.addLayer(_this2);

              case 3:
                return _context10.abrupt("return", olCmp.methods.mount.call(_this2));

              case 4:
              case "end":
                return _context10.stop();
            }
          }
        }, _callee10);
      }))();
    },

    /**
     * @return {Promise<void>}
     * @protected
     */
    unmount: function unmount() {
      var _this3 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee11() {
        return _regeneratorRuntime.wrap(function _callee11$(_context11) {
          while (1) {
            switch (_context11.prev = _context11.next) {
              case 0:
                if (!_this3.$layersContainer) {
                  _context11.next = 3;
                  break;
                }

                _context11.next = 3;
                return _this3.$layersContainer.removeLayer(_this3);

              case 3:
                return _context11.abrupt("return", olCmp.methods.unmount.call(_this3));

              case 4:
              case "end":
                return _context11.stop();
            }
          }
        }, _callee11);
      }))();
    },

    /**
     * @returns {Object}
     * @protected
     */
    getServices: function getServices() {
      var vm = this;
      return mergeDescriptors(olCmp.methods.getServices.call(this), {
        get layerVm() {
          return vm;
        }

      });
    },

    /**
     * @return {void}
     * @protected
     */
    subscribeAll: function subscribeAll() {
      olCmp.methods.subscribeAll.call(this);
      subscribeToLayerEvents.call(this);
    },

    /**
     * @return {Promise<module:ol/layer/Base~BaseLayer>}
     */
    resolveLayer: olCmp.methods.resolveOlObject
  }, /*#__PURE__*/pick(olCmp.methods, ['init', 'deinit', 'beforeMount', 'refresh', 'scheduleRefresh', 'remount', 'scheduleRemount', 'recreate', 'scheduleRecreate', 'resolveOlObject'])), /*#__PURE__*/pick(waitForMap.methods, ['beforeInit'])), {}, {
    /**
     * @returns {string|number}
     * @protected
     */
    getIdInternal: function getIdInternal() {
      return getLayerId(this.$layer);
    },

    /**
     * @param {string|number} id
     * @returns {void}
     * @protected
     */
    setIdInternal: function setIdInternal(id) {
      assert(id != null && id !== '', 'Invalid layer id');
      if (id === this.getIdInternal()) return;
      setLayerId(this.$layer, id);
    },

    /**
     * @param {boolean} [viewProj=false]
     * @returns {Promise<number[]|undefined>}
     */
    getExtent: function getExtent() {
      var _arguments = arguments,
          _this4 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee12() {
        var viewProj;
        return _regeneratorRuntime.wrap(function _callee12$(_context12) {
          while (1) {
            switch (_context12.prev = _context12.next) {
              case 0:
                viewProj = _arguments.length > 0 && _arguments[0] !== undefined ? _arguments[0] : false;
                _context12.next = 3;
                return _this4.resolveLayer();

              case 3:
                return _context12.abrupt("return", _this4.getExtentInternal(viewProj));

              case 4:
              case "end":
                return _context12.stop();
            }
          }
        }, _callee12);
      }))();
    },

    /**
     * @param {boolean} [viewProj=false]
     * @return {number[]|undefined}
     * @protected
     */
    getExtentInternal: function getExtentInternal() {
      var viewProj = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
      if (viewProj) return roundExtent(this.$layer.getExtent());
      return transformExtent$1(this.$layer.getExtent(), this.resolvedViewProjection, this.resolvedExtentProjection);
    },

    /**
     * @param {number[]} extent
     * @param {boolean} [viewProj=false]
     * @returns {Promise<void>}
     */
    setExtent: function setExtent(extent) {
      var _arguments2 = arguments,
          _this5 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee13() {
        var viewProj;
        return _regeneratorRuntime.wrap(function _callee13$(_context13) {
          while (1) {
            switch (_context13.prev = _context13.next) {
              case 0:
                viewProj = _arguments2.length > 1 && _arguments2[1] !== undefined ? _arguments2[1] : false;
                extent = roundExtent(extent);
                _context13.t0 = isEqual;
                _context13.t1 = extent;
                _context13.next = 6;
                return _this5.getExtent(viewProj);

              case 6:
                _context13.t2 = _context13.sent;

                if (!(0, _context13.t0)(_context13.t1, _context13.t2)) {
                  _context13.next = 9;
                  break;
                }

                return _context13.abrupt("return");

              case 9:
                if (!viewProj) {
                  extent = transformExtent$1(extent, _this5.resolvedExtentProjection, _this5.resolvedDataProjection);
                }

                _context13.next = 12;
                return _this5.resolveLayer();

              case 12:
                _context13.sent.setExtent(extent);

              case 13:
              case "end":
                return _context13.stop();
            }
          }
        }, _callee13);
      }))();
    },

    /**
     * @returns {Promise<number|undefined>}
     */
    getMaxResolution: function getMaxResolution() {
      var _this6 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee14() {
        return _regeneratorRuntime.wrap(function _callee14$(_context14) {
          while (1) {
            switch (_context14.prev = _context14.next) {
              case 0:
                _context14.next = 2;
                return _this6.resolveLayer();

              case 2:
                return _context14.abrupt("return", _this6.getMaxResolutionInternal());

              case 3:
              case "end":
                return _context14.stop();
            }
          }
        }, _callee14);
      }))();
    },

    /**
     * @return {number|undefined}
     * @protected
     */
    getMaxResolutionInternal: function getMaxResolutionInternal() {
      return this.$layer.getMaxResolution();
    },

    /**
     * @param {number} resolution
     * @returns {Promise<void>}
     */
    setMaxResolution: function setMaxResolution(resolution) {
      var _this7 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee15() {
        return _regeneratorRuntime.wrap(function _callee15$(_context15) {
          while (1) {
            switch (_context15.prev = _context15.next) {
              case 0:
                _context15.t0 = resolution;
                _context15.next = 3;
                return _this7.getMaxResolution();

              case 3:
                _context15.t1 = _context15.sent;

                if (!(_context15.t0 === _context15.t1)) {
                  _context15.next = 6;
                  break;
                }

                return _context15.abrupt("return");

              case 6:
                _context15.next = 8;
                return _this7.resolveLayer();

              case 8:
                _context15.sent.setMaxResolution(resolution);

              case 9:
              case "end":
                return _context15.stop();
            }
          }
        }, _callee15);
      }))();
    },

    /**
     * @returns {Promise<number|undefined>}
     */
    getMinResolution: function getMinResolution() {
      var _this8 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee16() {
        return _regeneratorRuntime.wrap(function _callee16$(_context16) {
          while (1) {
            switch (_context16.prev = _context16.next) {
              case 0:
                _context16.next = 2;
                return _this8.resolveLayer();

              case 2:
                return _context16.abrupt("return", _this8.getMinResolutionInternal());

              case 3:
              case "end":
                return _context16.stop();
            }
          }
        }, _callee16);
      }))();
    },

    /**
     * @return {number|undefined}
     * @protected
     */
    getMinResolutionInternal: function getMinResolutionInternal() {
      return this.$layer.getMinResolution();
    },

    /**
     * @param {number} resolution
     * @returns {Promise<void>}
     */
    setMinResolution: function setMinResolution(resolution) {
      var _this9 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee17() {
        return _regeneratorRuntime.wrap(function _callee17$(_context17) {
          while (1) {
            switch (_context17.prev = _context17.next) {
              case 0:
                _context17.t0 = resolution;
                _context17.next = 3;
                return _this9.getMinResolution();

              case 3:
                _context17.t1 = _context17.sent;

                if (!(_context17.t0 === _context17.t1)) {
                  _context17.next = 6;
                  break;
                }

                return _context17.abrupt("return");

              case 6:
                _context17.next = 8;
                return _this9.resolveLayer();

              case 8:
                _context17.sent.setMinResolution(resolution);

              case 9:
              case "end":
                return _context17.stop();
            }
          }
        }, _callee17);
      }))();
    },

    /**
     * @returns {Promise<number>}
     */
    getMaxZoom: function getMaxZoom() {
      var _this10 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee18() {
        return _regeneratorRuntime.wrap(function _callee18$(_context18) {
          while (1) {
            switch (_context18.prev = _context18.next) {
              case 0:
                _context18.next = 2;
                return _this10.resolveLayer();

              case 2:
                return _context18.abrupt("return", _this10.getMaxZoomInternal());

              case 3:
              case "end":
                return _context18.stop();
            }
          }
        }, _callee18);
      }))();
    },

    /**
     * @return {number}
     * @protected
     */
    getMaxZoomInternal: function getMaxZoomInternal() {
      return this.$layer.getMaxZoom();
    },

    /**
     * @param {number} zoom
     * @returns {Promise<void>}
     */
    setMaxZoom: function setMaxZoom(zoom) {
      var _this11 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee19() {
        return _regeneratorRuntime.wrap(function _callee19$(_context19) {
          while (1) {
            switch (_context19.prev = _context19.next) {
              case 0:
                _context19.t0 = zoom;
                _context19.next = 3;
                return _this11.getMaxZoom();

              case 3:
                _context19.t1 = _context19.sent;

                if (!(_context19.t0 === _context19.t1)) {
                  _context19.next = 6;
                  break;
                }

                return _context19.abrupt("return");

              case 6:
                _context19.next = 8;
                return _this11.resolveLayer();

              case 8:
                _context19.sent.setMaxZoom(zoom);

              case 9:
              case "end":
                return _context19.stop();
            }
          }
        }, _callee19);
      }))();
    },

    /**
     * @returns {Promise<number>}
     */
    getMinZoom: function getMinZoom() {
      var _this12 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee20() {
        return _regeneratorRuntime.wrap(function _callee20$(_context20) {
          while (1) {
            switch (_context20.prev = _context20.next) {
              case 0:
                _context20.next = 2;
                return _this12.resolveLayer();

              case 2:
                return _context20.abrupt("return", _this12.getMinZoomInternal());

              case 3:
              case "end":
                return _context20.stop();
            }
          }
        }, _callee20);
      }))();
    },

    /**
     * @return {number}
     * @protected
     */
    getMinZoomInternal: function getMinZoomInternal() {
      return this.$layer.getMinZoom();
    },

    /**
     * @param {number} zoom
     * @returns {Promise<void>}
     */
    setMinZoom: function setMinZoom(zoom) {
      var _this13 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee21() {
        return _regeneratorRuntime.wrap(function _callee21$(_context21) {
          while (1) {
            switch (_context21.prev = _context21.next) {
              case 0:
                _context21.t0 = zoom;
                _context21.next = 3;
                return _this13.getMinZoom();

              case 3:
                _context21.t1 = _context21.sent;

                if (!(_context21.t0 === _context21.t1)) {
                  _context21.next = 6;
                  break;
                }

                return _context21.abrupt("return");

              case 6:
                _context21.next = 8;
                return _this13.resolveLayer();

              case 8:
                _context21.sent.setMinZoom(zoom);

              case 9:
              case "end":
                return _context21.stop();
            }
          }
        }, _callee21);
      }))();
    },

    /**
     * @returns {Promise<number>}
     */
    getOpacity: function getOpacity() {
      var _this14 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee22() {
        return _regeneratorRuntime.wrap(function _callee22$(_context22) {
          while (1) {
            switch (_context22.prev = _context22.next) {
              case 0:
                _context22.next = 2;
                return _this14.resolveLayer();

              case 2:
                return _context22.abrupt("return", _this14.getOpacityInternal());

              case 3:
              case "end":
                return _context22.stop();
            }
          }
        }, _callee22);
      }))();
    },

    /**
     * @return {number}
     * @protected
     */
    getOpacityInternal: function getOpacityInternal() {
      return this.$layer.getOpacity();
    },

    /**
     * @param {number} opacity
     * @returns {Promise<void>}
     */
    setOpacity: function setOpacity(opacity) {
      var _this15 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee23() {
        return _regeneratorRuntime.wrap(function _callee23$(_context23) {
          while (1) {
            switch (_context23.prev = _context23.next) {
              case 0:
                _context23.t0 = opacity;
                _context23.next = 3;
                return _this15.getOpacity();

              case 3:
                _context23.t1 = _context23.sent;

                if (!(_context23.t0 === _context23.t1)) {
                  _context23.next = 6;
                  break;
                }

                return _context23.abrupt("return");

              case 6:
                _context23.next = 8;
                return _this15.resolveLayer();

              case 8:
                _context23.sent.setOpacity(opacity);

              case 9:
              case "end":
                return _context23.stop();
            }
          }
        }, _callee23);
      }))();
    },

    /**
     * @returns {Promise<boolean>}
     */
    getVisible: function getVisible() {
      var _this16 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee24() {
        return _regeneratorRuntime.wrap(function _callee24$(_context24) {
          while (1) {
            switch (_context24.prev = _context24.next) {
              case 0:
                _context24.next = 2;
                return _this16.resolveLayer();

              case 2:
                return _context24.abrupt("return", _this16.getVisibleInternal());

              case 3:
              case "end":
                return _context24.stop();
            }
          }
        }, _callee24);
      }))();
    },

    /**
     * @return {boolean}
     * @protected
     */
    getVisibleInternal: function getVisibleInternal() {
      return this.$layer.getVisible();
    },

    /**
     * @param {boolean} visible
     * @returns {Promise<void>}
     */
    setVisible: function setVisible(visible) {
      var _this17 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee25() {
        return _regeneratorRuntime.wrap(function _callee25$(_context25) {
          while (1) {
            switch (_context25.prev = _context25.next) {
              case 0:
                _context25.t0 = visible;
                _context25.next = 3;
                return _this17.getVisible();

              case 3:
                _context25.t1 = _context25.sent;

                if (!(_context25.t0 === _context25.t1)) {
                  _context25.next = 6;
                  break;
                }

                return _context25.abrupt("return");

              case 6:
                _context25.next = 8;
                return _this17.resolveLayer();

              case 8:
                _context25.sent.setVisible(visible);

              case 9:
              case "end":
                return _context25.stop();
            }
          }
        }, _callee25);
      }))();
    },

    /**
     * @returns {Promise<number>}
     */
    getZIndex: function getZIndex() {
      var _this18 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee26() {
        return _regeneratorRuntime.wrap(function _callee26$(_context26) {
          while (1) {
            switch (_context26.prev = _context26.next) {
              case 0:
                _context26.next = 2;
                return _this18.resolveLayer();

              case 2:
                return _context26.abrupt("return", _this18.getZIndexInternal());

              case 3:
              case "end":
                return _context26.stop();
            }
          }
        }, _callee26);
      }))();
    },

    /**
     * @return {number}
     * @protected
     */
    getZIndexInternal: function getZIndexInternal() {
      return this.$layer.getZIndex();
    },

    /**
     * @param {number} zIndex
     * @returns {Promise<void>}
     */
    setZIndex: function setZIndex(zIndex) {
      var _this19 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee27() {
        return _regeneratorRuntime.wrap(function _callee27$(_context27) {
          while (1) {
            switch (_context27.prev = _context27.next) {
              case 0:
                _context27.t0 = zIndex;
                _context27.next = 3;
                return _this19.getZIndex();

              case 3:
                _context27.t1 = _context27.sent;

                if (!(_context27.t0 === _context27.t1)) {
                  _context27.next = 6;
                  break;
                }

                return _context27.abrupt("return");

              case 6:
                _context27.next = 8;
                return _this19.resolveLayer();

              case 8:
                _context27.sent.setZIndex(zIndex);

              case 9:
              case "end":
                return _context27.stop();
            }
          }
        }, _callee27);
      }))();
    },

    /**
     * @param {number[]} pixel
     * @return {boolean}
     */
    isAtPixel: function isAtPixel(pixel) {
      var _this20 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee28() {
        var layer;
        return _regeneratorRuntime.wrap(function _callee28$(_context28) {
          while (1) {
            switch (_context28.prev = _context28.next) {
              case 0:
                _context28.next = 2;
                return _this20.resolveLayer();

              case 2:
                layer = _context28.sent;
                return _context28.abrupt("return", _this20.$mapVm.forEachLayerAtPixel(pixel, function (mapLayer) {
                  return mapLayer === layer;
                }));

              case 4:
              case "end":
                return _context28.stop();
            }
          }
        }, _callee28);
      }))();
    }
  })
};

function defineServices$1() {
  var _this21 = this;

  Object.defineProperties(this, {
    /**
     * @type {module:ol/layer/Base~BaseLayer|undefined}
     */
    $layer: {
      enumerable: true,
      get: function get() {
        return _this21.$olObject;
      }
    },

    /**
     * @type {Object|undefined}
     */
    $mapVm: {
      enumerable: true,
      get: function get() {
        var _this21$$services;

        return (_this21$$services = _this21.$services) === null || _this21$$services === void 0 ? void 0 : _this21$$services.mapVm;
      }
    },

    /**
     * @type {Object|undefined}
     */
    $viewVm: {
      enumerable: true,
      get: function get() {
        var _this21$$services2;

        return (_this21$$services2 = _this21.$services) === null || _this21$$services2 === void 0 ? void 0 : _this21$$services2.viewVm;
      }
    },

    /**
     * @type {Object|undefined}
     */
    $layersContainer: {
      enumerable: true,
      get: function get() {
        var _this21$$services3;

        return (_this21$$services3 = _this21.$services) === null || _this21$$services3 === void 0 ? void 0 : _this21$$services3.layersContainer;
      }
    }
  });
}

function subscribeToLayerEvents() {
  return _subscribeToLayerEvents.apply(this, arguments);
}

function _subscribeToLayerEvents() {
  _subscribeToLayerEvents = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee29() {
    var _this22 = this;

    var prefixKey, propChanges;
    return _regeneratorRuntime.wrap(function _callee29$(_context29) {
      while (1) {
        switch (_context29.prev = _context29.next) {
          case 0:
            prefixKey = addPrefix('current');
            propChanges = merge(fromOlChangeEvent(this.$layer, ['id', 'opacity', 'visible', 'zIndex', 'minResolution', 'maxResolution', 'minZoom', 'maxZoom'], true, function (evt) {
              return _objectSpread$1(_objectSpread$1({}, evt), {}, {
                compareWith: _this22[prefixKey(evt.prop)]
              });
            }), fromOlChangeEvent(this.$layer, 'extent', true).pipe(mergeMap(function (_ref9) {
              var prop = _ref9.prop;
              return from(_this22.getExtent()).pipe(map(function (extent) {
                return {
                  prop: prop,
                  value: extent,
                  compareWith: _this22.currentExtentDataProj
                };
              }));
            }))).pipe(skipWhile(function (_ref10) {
              var value = _ref10.value,
                  compareWith = _ref10.compareWith;
              return isEqual(value, compareWith);
            }));
            this.subscribeTo(propChanges, function () {
              ++_this22.rev;
            });

          case 3:
          case "end":
            return _context29.stop();
        }
      }
    }, _callee29, this);
  }));
  return _subscribeToLayerEvents.apply(this, arguments);
}

/**
 * @typedef {module:ol/control/Control~Control|Object} ControlLike
 */

/**
 * Controls collection
 */

var controlsContainer = {
  mixins: [identMap, rxSubs],
  computed: {
    /**
     * @returns {string[]}
     */
    currentControls: function currentControls() {
      if (!this.rev) return [];
      return map$1(this.getControls(), getControlId);
    },

    /**
     * @type {string|undefined}
     */
    controlsCollectionIdent: function controlsCollectionIdent() {
      if (!this.olObjIdent) return;
      return this.makeIdent(this.olObjIdent, 'controls_collection');
    }
  },
  watch: {
    currentControls: /*#__PURE__*/debounce(function (value, prev) {
      if (isEqual(value, prev)) return;
      this.$emit('update:controls', value.slice());
    }, FRAME_TIME),
    controlsCollectionIdent: /*#__PURE__*/sequential(function (value, prevValue) {
      if (value && prevValue) {
        this.moveInstance(value, prevValue);
      } else if (value && !prevValue && this.$controlsCollection) {
        this.setInstance(value, this.$controlsCollection);
      } else if (!value && prevValue) {
        this.unsetInstance(prevValue);
      }
    })
  },
  created: function created() {
    /**
     * @type {module:ol/Collection~Collection<module:ol/control/Control~Control>}
     * @private
     */
    this._controlsCollection = this.instanceFactoryCall(this.controlsCollectionIdent, function () {
      return new Collection();
    });
    defineServices$2.call(this);
  },
  methods: {
    /**
     * @returns {{readonly controlsContainer: Object}}
     * @protected
     */
    getServices: function getServices() {
      var vm = this;
      return {
        get controlsContainer() {
          return vm;
        }

      };
    },

    /**
     * @return {void}
     * @protected
     */
    subscribeAll: function subscribeAll() {
      subscribeToCollectionEvents.call(this);
    },

    /**
     * @param {ControlLike[]|module:ol/Collection~Collection<ControlLike>} defaultControls
     * @returns {Promise<void>}
     */
    initDefaultControls: function initDefaultControls(defaultControls) {
      var _this = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
        var controls;
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                _this.clearControls();

                if (isArray(defaultControls) || defaultControls instanceof Collection) {
                  controls = defaultControls;
                } else if (defaultControls !== false) {
                  controls = defaults(isPlainObject(defaultControls) ? defaultControls : undefined);
                }

                if (!controls) {
                  _context.next = 5;
                  break;
                }

                _context.next = 5;
                return _this.addControls(controls);

              case 5:
              case "end":
                return _context.stop();
            }
          }
        }, _callee);
      }))();
    },

    /**
     * @param {ControlLike} control
     * @return {Promise<Control>}
     */
    initializeControl: function initializeControl$1(control) {
      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
        return _regeneratorRuntime.wrap(function _callee2$(_context2) {
          while (1) {
            switch (_context2.prev = _context2.next) {
              case 0:
                if (!isFunction(control.resolveOlObject)) {
                  _context2.next = 4;
                  break;
                }

                _context2.next = 3;
                return control.resolveOlObject();

              case 3:
                control = _context2.sent;

              case 4:
                return _context2.abrupt("return", initializeControl(control));

              case 5:
              case "end":
                return _context2.stop();
            }
          }
        }, _callee2);
      }))();
    },

    /**
     * @param {ControlLike[]|module:ol/Collection~Collection<ControlLike>} controls
     * @returns {Promise<void>}
     */
    addControls: function addControls(controls) {
      var _this2 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3() {
        return _regeneratorRuntime.wrap(function _callee3$(_context3) {
          while (1) {
            switch (_context3.prev = _context3.next) {
              case 0:
                _context3.next = 2;
                return Promise.all(map$1(controls, _this2.addControl.bind(_this2)));

              case 2:
              case "end":
                return _context3.stop();
            }
          }
        }, _callee3);
      }))();
    },

    /**
     * @param {ControlLike} control
     * @returns {Promise<void>}
     */
    addControl: function addControl(control) {
      var _this3 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee4() {
        return _regeneratorRuntime.wrap(function _callee4$(_context4) {
          while (1) {
            switch (_context4.prev = _context4.next) {
              case 0:
                _context4.next = 2;
                return _this3.initializeControl(control);

              case 2:
                control = _context4.sent;
                instanceOf(control, Control);

                if (!_this3.getControlById(getControlId(control))) {
                  _context4.next = 6;
                  break;
                }

                return _context4.abrupt("return");

              case 6:
                _this3.$controlsCollection.push(control);

              case 7:
              case "end":
                return _context4.stop();
            }
          }
        }, _callee4);
      }))();
    },

    /**
     * @param {ControlLike[]|module:ol/Collection~Collection<ControlLike>} controls
     * @returns {Promise<void>}
     */
    removeControls: function removeControls(controls) {
      var _this4 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee5() {
        return _regeneratorRuntime.wrap(function _callee5$(_context5) {
          while (1) {
            switch (_context5.prev = _context5.next) {
              case 0:
                _context5.next = 2;
                return Promise.all(map$1(controls, _this4.removeControl.bind(_this4)));

              case 2:
              case "end":
                return _context5.stop();
            }
          }
        }, _callee5);
      }))();
    },

    /**
     * @param {ControlLike} control
     * @returns {Promise<void>}
     */
    removeControl: function removeControl(control) {
      var _this5 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee6() {
        return _regeneratorRuntime.wrap(function _callee6$(_context6) {
          while (1) {
            switch (_context6.prev = _context6.next) {
              case 0:
                if (!isFunction(control.resolveOlObject)) {
                  _context6.next = 4;
                  break;
                }

                _context6.next = 3;
                return control.resolveOlObject();

              case 3:
                control = _context6.sent;

              case 4:
                control = _this5.getControlById(getControlId(control));

                if (control) {
                  _context6.next = 7;
                  break;
                }

                return _context6.abrupt("return");

              case 7:
                _this5.$controlsCollection.remove(control);

              case 8:
              case "end":
                return _context6.stop();
            }
          }
        }, _callee6);
      }))();
    },

    /**
     * @return {void}
     */
    clearControls: function clearControls() {
      this.$controlsCollection.clear();
    },

    /**
     * @returns {Array<module:ol/control/Control~Control>}
     */
    getControls: function getControls() {
      return this.$controlsCollection.getArray();
    },

    /**
     * @returns {module:ol/Collection~Collection<module:ol/control/Control~Control>}
     */
    getControlsCollection: function getControlsCollection() {
      return this._controlsCollection;
    },

    /**
     * @param {string|number} controlId
     * @returns {ControlLike}
     */
    getControlById: function getControlById(controlId) {
      return find(this.getControls(), function (control) {
        return getControlId(control) === controlId;
      });
    }
  }
};

function defineServices$2() {
  Object.defineProperties(this, {
    $controlsCollection: {
      enumerable: true,
      get: this.getControlsCollection
    }
  });
}

function subscribeToCollectionEvents() {
  var _this6 = this;

  var adds = fromOlEvent(this.$controlsCollection, CollectionEventType.ADD).pipe(mergeMap(function (_ref) {
    var type = _ref.type,
        element = _ref.element;
    return from(_this6.initializeControl(element)).pipe(map(function (element) {
      return {
        type: type,
        element: element
      };
    }));
  }));
  var removes = fromOlEvent(this.$controlsCollection, CollectionEventType.REMOVE);
  var events = merge(adds, removes).pipe(bufferDebounceTime(FRAME_TIME));
  this.subscribeTo(events, function (events) {
    ++_this6.rev;

    _this6.$nextTick(function () {
      forEach(events, function (_ref2) {
        var type = _ref2.type,
            element = _ref2.element;

        _this6.$emit(type + 'control', element); // todo remove in v0.13.x


        _this6.$emit(type + ':control', element);
      });
    });
  });
}

/**
 * @typedef {Geometry|Object} GeometryLike
 */

/**
 * @typedef {Object} GeometryTarget
 * @property {function(): Geometry|undefined} getGeometry
 * @property {function(Geometry|undefined): void} setGeometry
 */

/**
 * Geometry container
 */

var geometryContainer = {
  mixins: [projTransforms],
  computed: {
    currentGeometryDataProj: function currentGeometryDataProj() {
      if (!(this.rev && this.$geometry)) return;
      return this.writeGeometryInDataProj(this.$geometry);
    },
    currentGeometryViewProj: function currentGeometryViewProj() {
      if (!(this.rev && this.$geometry)) return;
      return this.writeGeometryInViewProj(this.$geometry);
    },
    currentPointDataProj: function currentPointDataProj() {
      if (!this.currentPointViewProj) return;
      return this.pointToDataProj(this.currentPointViewProj);
    },
    currentPointViewProj: function currentPointViewProj() {
      if (!(this.rev && this.$geometry)) return;
      return findPointOnSurface(this.$geometry);
    }
  },
  watch: {
    currentGeometryDataProj: {
      deep: true,
      handler: /*#__PURE__*/debounce(function (value, prev) {
        if (isEqual(value, prev)) return;
        this.$emit('update:geometry', value && clonePlainObject(value));
      }, FRAME_TIME)
    }
  },
  created: function created() {
    this._geometry = undefined;
    this._geometryVm = undefined;
    defineService.call(this);
  },
  methods: {
    /**
     * @returns {{readonly geometryContainer: Object}}
     * @protected
     */
    getServices: function getServices() {
      var vm = this;
      return {
        get geometryContainer() {
          return vm;
        }

      };
    },

    /**
     * @return {Promise<GeometryTarget|undefined>}
     * @protected
     */
    getGeometryTarget: function getGeometryTarget() {
      throw new Error("".concat(this.vmName, " not implemented method: getGeometryTarget()"));
    },

    /**
     * @return {module:ol/geom/Geometry~Geometry|undefined}
     */
    getGeometry: function getGeometry() {
      return this._geometry;
    },

    /**
     * @return {Object}
     */
    getGeometryVm: function getGeometryVm() {
      return this._geometryVm;
    },

    /**
     * @param {GeometryLike|undefined} geom
     * @param {boolean} [viewProj=false]
     * @return {Promise<void>}
     */
    setGeometry: function setGeometry(geom) {
      var _arguments = arguments,
          _this = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
        var _geom, _geom2;

        var viewProj, geomTarget;
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                viewProj = _arguments.length > 1 && _arguments[1] !== undefined ? _arguments[1] : false;

                if (!isFunction((_geom = geom) === null || _geom === void 0 ? void 0 : _geom.resolveOlObject)) {
                  _context.next = 7;
                  break;
                }

                _context.next = 4;
                return geom.resolveOlObject();

              case 4:
                geom = _context.sent;
                _context.next = 8;
                break;

              case 7:
                if (isGeoJSONGeometry(geom)) {
                  if (viewProj) {
                    geom = _this.readGeometryInViewProj(geom);
                  } else {
                    geom = _this.readGeometryInDataProj(geom);
                  }
                }

              case 8:
                geom || (geom = undefined);

                if (!(geom === _this._geometry)) {
                  _context.next = 11;
                  break;
                }

                return _context.abrupt("return");

              case 11:
                _context.next = 13;
                return _this.getGeometryTarget();

              case 13:
                geomTarget = _context.sent;

                if (geomTarget) {
                  _context.next = 16;
                  break;
                }

                return _context.abrupt("return");

              case 16:
                _this._geometry = geom;
                _this._geometryVm = ((_geom2 = geom) === null || _geom2 === void 0 ? void 0 : _geom2.vm) && geom.vm[0];
                _context.next = 20;
                return geomTarget.setGeometry(geom);

              case 20:
              case "end":
                return _context.stop();
            }
          }
        }, _callee);
      }))();
    }
  }
};

function defineService() {
  Object.defineProperties(this, {
    $geometry: {
      enumerable: true,
      get: this.getGeometry
    },
    $geometryVm: {
      enumerable: true,
      get: this.getGeometryVm
    }
  });
}

/**
 * @typedef {
 *            module:ol/style/Style~Style |
 *            Array<module:ol/style/Style~Style> |
 *            module:ol/style/Style~StyleFunction
 *          } OlStyleLike
 */

/**
 * @typedef {Object} StyleTarget
 * @property {function(OlStyleLike|undefined): void} setStyle
 */

/**
 * @typedef {OlStyleLike|Object} StyleLike
 */

/**
 * Style container mixin.
 */

var styleContainer = {
  computed: {
    currentStyle: function currentStyle() {
      var _this = this;

      if (!(this.rev && this.$style)) return;
      var style = this.$style;
      if (isFunction(style)) return style;
      if (!style) return;
      isArray(style) || (style = [style]);
      return style.map(function (style) {
        return dumpStyle(style, function (geom) {
          return _this.writeGeometryInDataProj(geom);
        });
      });
    }
  },
  watch: {
    currentStyle: {
      deep: true,
      handler: /*#__PURE__*/debounce(function (value, prev) {
        if (isEqual(value, prev)) return;
        this.$emit('update:style', value && clonePlainObject(value));
      }, FRAME_TIME)
    }
  },
  created: function created() {
    this._style = undefined;
    defineServices$3.call(this);
  },
  methods: {
    /**
     * @returns {{readonly styleContainer: Object}}
     * @protected
     */
    getServices: function getServices() {
      var vm = this;
      return {
        get styleContainer() {
          return vm;
        }

      };
    },

    /**
     * Default style factory
     * @return {OlStyleLike|undefined}
     * @protected
     */
    getDefaultStyle: function getDefaultStyle() {},

    /**
     * Returns OL object that can be styled (i.e. has setStyle/getStyle methods) or undefined
     * @return {Promise<StyleTarget>|StyleTarget}
     * @protected
     * @abstract
     */
    getStyleTarget: function getStyleTarget() {
      throw new Error("".concat(this.vmName, " not implemented method: getStyleTarget()"));
    },

    /**
     * @return {StyleLike|null}
     */
    getStyle: function getStyle() {
      return this._style;
    },

    /**
     * @param {StyleLike} style
     * @return {Promise<void>}
     */
    addStyle: function addStyle(style) {
      var _this2 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
        var olStyle, currentStyle;
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                if (style) {
                  _context.next = 2;
                  break;
                }

                return _context.abrupt("return");

              case 2:
                if (!isFunction(style.resolveOlObject)) {
                  _context.next = 8;
                  break;
                }

                _context.next = 5;
                return style.resolveOlObject();

              case 5:
                olStyle = _context.sent;
                _context.next = 9;
                break;

              case 8:
                olStyle = style;

              case 9:
                currentStyle = _this2.$style;

                if (isFunction(olStyle)) {
                  if (currentStyle) {
                    if (process.env.NODE_ENV !== 'production') {
                      _this2.$logger.warn('Component already has style components among its descendants. ' + 'Avoid use of multiple vl-style-func or combining vl-style-func with vl-style-box on the same level.');
                    }
                  }

                  currentStyle = style;
                } else {
                  if (!isArray(currentStyle)) {
                    if (currentStyle) {
                      if (process.env.NODE_ENV !== 'production') {
                        _this2.$logger.warn('Component already has style components among its descendants. ' + 'Avoid use of multiple vl-style-func or combining vl-style-func with vl-style-box on the same level.');
                      }
                    }

                    currentStyle = [];
                  }

                  if (!currentStyle.includes(olStyle)) {
                    currentStyle.push(olStyle);
                  }
                }

                _context.next = 13;
                return _this2.setStyle(currentStyle);

              case 13:
              case "end":
                return _context.stop();
            }
          }
        }, _callee);
      }))();
    },

    /**
     * @param {StyleLike|undefined} style
     * @return {Promise<void>}
     */
    removeStyle: function removeStyle(style) {
      var _this3 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
        var currentStyle;
        return _regeneratorRuntime.wrap(function _callee2$(_context2) {
          while (1) {
            switch (_context2.prev = _context2.next) {
              case 0:
                if (style) {
                  _context2.next = 2;
                  break;
                }

                return _context2.abrupt("return");

              case 2:
                if (!isFunction(style.resolveOlObject)) {
                  _context2.next = 6;
                  break;
                }

                _context2.next = 5;
                return style.resolveOlObject();

              case 5:
                style = _context2.sent;

              case 6:
                currentStyle = _this3.$style;

                if (currentStyle === style) {
                  currentStyle = undefined;
                } else if (isArray(currentStyle)) {
                  currentStyle = currentStyle.filter(function (s) {
                    return s !== style;
                  });

                  if (currentStyle.length === 0) {
                    currentStyle = undefined;
                  }
                }

                _context2.next = 10;
                return _this3.setStyle(currentStyle);

              case 10:
              case "end":
                return _context2.stop();
            }
          }
        }, _callee2);
      }))();
    },

    /**
     * @param {StyleLike|undefined} style
     * @return {void}
     */
    setStyle: function setStyle(style) {
      var _this4 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee4() {
        var styleTarget;
        return _regeneratorRuntime.wrap(function _callee4$(_context4) {
          while (1) {
            switch (_context4.prev = _context4.next) {
              case 0:
                if (!style) {
                  _context4.next = 11;
                  break;
                }

                if (!isFunction(style.resolveOlObject)) {
                  _context4.next = 7;
                  break;
                }

                _context4.next = 4;
                return style.resolveOlObject();

              case 4:
                style = _context4.sent;
                _context4.next = 11;
                break;

              case 7:
                if (!isArray(style)) {
                  _context4.next = 11;
                  break;
                }

                _context4.next = 10;
                return Promise.all(style.map( /*#__PURE__*/function () {
                  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3(style) {
                    return _regeneratorRuntime.wrap(function _callee3$(_context3) {
                      while (1) {
                        switch (_context3.prev = _context3.next) {
                          case 0:
                            if (!isFunction(style.resolveOlObject)) {
                              _context3.next = 2;
                              break;
                            }

                            return _context3.abrupt("return", style.resolveOlObject());

                          case 2:
                            return _context3.abrupt("return", style);

                          case 3:
                          case "end":
                            return _context3.stop();
                        }
                      }
                    }, _callee3);
                  }));

                  return function (_x) {
                    return _ref.apply(this, arguments);
                  };
                }()));

              case 10:
                style = _context4.sent;

              case 11:
                style || (style = undefined);

                if (!isEqual(style, _this4._style)) {
                  _context4.next = 14;
                  break;
                }

                return _context4.abrupt("return");

              case 14:
                _context4.next = 16;
                return _this4.getStyleTarget();

              case 16:
                styleTarget = _context4.sent;

                if (styleTarget) {
                  _context4.next = 19;
                  break;
                }

                return _context4.abrupt("return");

              case 19:
                if (style) {
                  if (isFunction(style)) {
                    style = _this4.createStyleFunc(style, _this4.getDefaultStyle());
                  } else {
                    isArray(style) || (style = [style]);
                    style.length > 0 || (style = undefined);
                  }
                } else {
                  style = undefined;
                }

                _this4._style = style;
                _context4.next = 23;
                return styleTarget.setStyle(style);

              case 23:
              case "end":
                return _context4.stop();
            }
          }
        }, _callee4);
      }))();
    },

    /**
     * Style function factory
     * @param {StyleLike|undefined} style
     * @param {StyleLike|undefined} defaultStyle
     * @returns {module:ol/style/Style~StyleFunction}
     * @protected
     */
    createStyleFunc: function createStyleFunc(style, defaultStyle) {
      return function __styleFunc(feature, resolution) {
        if (!feature.getGeometry()) return;
        var compiledStyle;

        if (style && isFunction(style)) {
          // style - custom ol/style/Style~StyleFunction
          compiledStyle = style(feature, resolution);
        } else if (isArray(style)) {
          // style - array of ol/style/Style objects
          compiledStyle = style.slice();
        } // not empty or null style


        if (compiledStyle == null || isArray(compiledStyle) && compiledStyle.length || compiledStyle instanceof Style) {
          return compiledStyle;
        } // fallback to default style


        if (defaultStyle) {
          return isFunction(defaultStyle) ? defaultStyle(feature, resolution) : defaultStyle;
        }

        return null;
      };
    }
  }
};

function defineServices$3() {
  Object.defineProperties(this, {
    $style: {
      enumerable: true,
      get: this.getStyle
    }
  });
}

function ownKeys$2(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$2(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$2(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$2(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
/**
 * A vector object for geographic features with a geometry and other attribute properties,
 * similar to the features in vector file formats like **GeoJSON**.
 */

var feature = {
  mixins: [stubVNode, projTransforms, geometryContainer, styleContainer, olCmp, waitForMap],
  stubVNode: {
    empty: false,
    attrs: function attrs() {
      return {
        id: this.vmId,
        class: this.vmClass
      };
    }
  },
  props: {
    properties: {
      type: Object,
      default: stubObject
    }
  },
  data: function data() {
    return {
      viewProjection: EPSG_3857,
      dataProjection: EPSG_3857
    };
  },
  computed: {
    currentProperties: function currentProperties() {
      if (this.rev && this.$feature) {
        return this.getPropertiesInternal();
      }

      return this.properties;
    },
    currentGeometryName: function currentGeometryName() {
      if (!(this.rev && this.$feature)) return;
      return this.getGeometryNameInternal();
    }
  },
  watch: {
    properties: {
      deep: true,
      handler: /*#__PURE__*/sequential( /*#__PURE__*/function () {
        var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(value) {
          return _regeneratorRuntime.wrap(function _callee$(_context) {
            while (1) {
              switch (_context.prev = _context.next) {
                case 0:
                  _context.next = 2;
                  return this.setProperties(value);

                case 2:
                case "end":
                  return _context.stop();
              }
            }
          }, _callee, this);
        }));

        return function (_x) {
          return _ref.apply(this, arguments);
        };
      }())
    },
    currentProperties: {
      deep: true,
      handler: /*#__PURE__*/debounce(function (value) {
        if (isEqual(value, this.properties)) return;
        this.$emit('update:properties', clonePlainObject(value));
      }, FRAME_TIME)
    },
    currentGeometryName: /*#__PURE__*/debounce(function (value, prev) {
      if (value === prev) return;
      this.$emit('update:geometryName', value);
    }, FRAME_TIME)
  },
  created: function created() {
    defineServices$4.call(this);
  },
  methods: _objectSpread$2(_objectSpread$2(_objectSpread$2(_objectSpread$2(_objectSpread$2({
    /**
     * Create feature without inner style applying, feature level style
     * will be applied in the layer level style function.
     * @return {module:ol/Feature~Feature}
     * @protected
     */
    createOlObject: function createOlObject() {
      var feature = initializeFeature(this.createFeature(), this.currentId);
      feature.setGeometry(this.$geometry);
      return feature;
    },

    /**
     * @returns {Feature}
     */
    createFeature: function createFeature() {
      return new Feature(this.properties);
    },

    /**
     * @return {Promise<void>}
     * @protected
     */
    beforeMount: function beforeMount() {
      var _this = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
        return _regeneratorRuntime.wrap(function _callee2$(_context2) {
          while (1) {
            switch (_context2.prev = _context2.next) {
              case 0:
                _context2.prev = 0;
                _context2.next = 3;
                return waitFor(function () {
                  return _this.$geometryVm != null;
                }, race(_this.$olObjectEvents.pipe(filter(function (_ref2) {
                  var name = _ref2.name,
                      args = _ref2.args;
                  return name === OlObjectEvent.ERROR && args[0] instanceof CanceledError;
                })), fromVueEvent(_this.$eventBus, ObjectEventType.ERROR).pipe(filter(function (_ref3) {
                  var _vm$$vq;

                  var _ref4 = _slicedToArray(_ref3, 2),
                      err = _ref4[0],
                      vm = _ref4[1];

                  return (isCreateError(err) || isMountError(err)) && hasProp(vm, '$geometry') && ((_vm$$vq = vm.$vq) === null || _vm$$vq === void 0 ? void 0 : _vm$$vq.closest(_this));
                }))).pipe(mapTo(stubTrue())));

              case 3:
                return _context2.abrupt("return", olCmp.methods.beforeMount.call(_this));

              case 6:
                _context2.prev = 6;
                _context2.t0 = _context2["catch"](0);
                _context2.t0.message = "".concat(_this.vmName, " wait for $geometry failed: ").concat(_context2.t0.message);
                throw _context2.t0;

              case 10:
              case "end":
                return _context2.stop();
            }
          }
        }, _callee2, null, [[0, 6]]);
      }))();
    },

    /**
     * @return {Promise<void>}
     * @protected
     */
    mount: function mount() {
      var _this2 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3() {
        return _regeneratorRuntime.wrap(function _callee3$(_context3) {
          while (1) {
            switch (_context3.prev = _context3.next) {
              case 0:
                if (!_this2.$featuresContainer) {
                  _context3.next = 3;
                  break;
                }

                _context3.next = 3;
                return _this2.$featuresContainer.addFeature(_this2);

              case 3:
                return _context3.abrupt("return", olCmp.methods.mount.call(_this2));

              case 4:
              case "end":
                return _context3.stop();
            }
          }
        }, _callee3);
      }))();
    },

    /**
     * @return {Promise<void>}
     * @protected
     */
    unmount: function unmount() {
      var _this3 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee4() {
        return _regeneratorRuntime.wrap(function _callee4$(_context4) {
          while (1) {
            switch (_context4.prev = _context4.next) {
              case 0:
                if (!_this3.$featuresContainer) {
                  _context4.next = 3;
                  break;
                }

                _context4.next = 3;
                return _this3.$featuresContainer.removeFeature(_this3);

              case 3:
                return _context4.abrupt("return", olCmp.methods.unmount.call(_this3));

              case 4:
              case "end":
                return _context4.stop();
            }
          }
        }, _callee4);
      }))();
    },

    /**
     * @return {void}
     * @protected
     */
    subscribeAll: function subscribeAll() {
      olCmp.methods.subscribeAll.call(this);
      subscribeToEvents.call(this);
    },

    /**
     * @return {Object}
     * @protected
     */
    getServices: function getServices() {
      var vm = this;
      return mergeDescriptors(olCmp.methods.getServices.call(this), geometryContainer.methods.getServices.call(this), styleContainer.methods.getServices.call(this), {
        get featureVm() {
          return vm;
        }

      });
    }
  }, /*#__PURE__*/pick(olCmp.methods, ['refresh', 'scheduleRefresh', 'remount', 'scheduleRemount', 'recreate', 'scheduleRecreate', 'resolveOlObject'])), /*#__PURE__*/pick(waitForMap.methods, ['beforeInit'])), /*#__PURE__*/pick(geometryContainer.methods, ['setGeometry'])), /*#__PURE__*/pick(styleContainer.methods, ['setStyle'])), {}, {
    resolveFeature: olCmp.methods.resolveOlObject,
    getGeometryTarget: olCmp.methods.resolveOlObject,
    getStyleTarget: olCmp.methods.resolveOlObject,

    /**
     * @return {string|number}
     */
    getIdInternal: function getIdInternal() {
      return getFeatureId(this.$feature);
    },

    /**
     * @param {string|number} id
     * @return {void}
     */
    setIdInternal: function setIdInternal(id) {
      assert(id != null && id !== '', 'Invalid feature id');
      if (id === this.getIdInternal()) return;
      setFeatureId(this.$feature, id);
    },

    /**
     * @return {Promise<string>}
     */
    getGeometryName: function getGeometryName() {
      var _this4 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee5() {
        return _regeneratorRuntime.wrap(function _callee5$(_context5) {
          while (1) {
            switch (_context5.prev = _context5.next) {
              case 0:
                _context5.next = 2;
                return _this4.resolveFeature();

              case 2:
                return _context5.abrupt("return", _this4.getGeometryNameInternal());

              case 3:
              case "end":
                return _context5.stop();
            }
          }
        }, _callee5);
      }))();
    },

    /**
     * @return {string}
     * @protected
     */
    getGeometryNameInternal: function getGeometryNameInternal() {
      return this.$feature.getGeometryName();
    },

    /**
     * @param {string} geometryName
     * @return {Promise<void>}
     */
    setGeometryName: function setGeometryName(geometryName) {
      var _this5 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee6() {
        return _regeneratorRuntime.wrap(function _callee6$(_context6) {
          while (1) {
            switch (_context6.prev = _context6.next) {
              case 0:
                _context6.t0 = geometryName;
                _context6.next = 3;
                return _this5.getGeometryName();

              case 3:
                _context6.t1 = _context6.sent;

                if (!(_context6.t0 === _context6.t1)) {
                  _context6.next = 6;
                  break;
                }

                return _context6.abrupt("return");

              case 6:
                _context6.next = 8;
                return _this5.resolveFeature();

              case 8:
                _context6.sent.setGeometryName(geometryName);

              case 9:
              case "end":
                return _context6.stop();
            }
          }
        }, _callee6);
      }))();
    },

    /**
     * @return {Promise<Object>}
     */
    getProperties: function getProperties() {
      var _this6 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee7() {
        return _regeneratorRuntime.wrap(function _callee7$(_context7) {
          while (1) {
            switch (_context7.prev = _context7.next) {
              case 0:
                _context7.next = 2;
                return _this6.resolveFeature();

              case 2:
                return _context7.abrupt("return", _this6.getPropertiesInternal());

              case 3:
              case "end":
                return _context7.stop();
            }
          }
        }, _callee7);
      }))();
    },
    getPropertiesInternal: function getPropertiesInternal() {
      return getFeatureProperties(this.$feature);
    },

    /**
     * @param {Object} properties
     * @return {Promise<void>}
     */
    setProperties: function setProperties(properties) {
      var _this7 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee8() {
        return _regeneratorRuntime.wrap(function _callee8$(_context8) {
          while (1) {
            switch (_context8.prev = _context8.next) {
              case 0:
                properties = getFeatureProperties({
                  properties: properties
                });
                _context8.t0 = isEqual;
                _context8.t1 = properties;
                _context8.next = 5;
                return _this7.getProperties();

              case 5:
                _context8.t2 = _context8.sent;

                if (!(0, _context8.t0)(_context8.t1, _context8.t2)) {
                  _context8.next = 8;
                  break;
                }

                return _context8.abrupt("return");

              case 8:
                _context8.t3 = setFeatureProperties;
                _context8.next = 11;
                return _this7.resolveFeature();

              case 11:
                _context8.t4 = _context8.sent;
                _context8.t5 = properties;
                (0, _context8.t3)(_context8.t4, _context8.t5);

              case 14:
              case "end":
                return _context8.stop();
            }
          }
        }, _callee8);
      }))();
    },

    /**
     * Checks if feature lies at `pixel`.
     * @param {number[]} pixel
     * @return {Promise<boolean>}
     */
    isAtPixel: function isAtPixel(pixel) {
      var _this8 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee9() {
        var selfFeature, layerFilter, selfLayer;
        return _regeneratorRuntime.wrap(function _callee9$(_context9) {
          while (1) {
            switch (_context9.prev = _context9.next) {
              case 0:
                _context9.next = 2;
                return _this8.resolveFeature();

              case 2:
                selfFeature = _context9.sent;

                if (!_this8.$layerVm) {
                  _context9.next = 8;
                  break;
                }

                _context9.next = 6;
                return _this8.$layerVm.resolveLayer();

              case 6:
                selfLayer = _context9.sent;

                layerFilter = function layerFilter(layer) {
                  return layer === selfLayer;
                };

              case 8:
                return _context9.abrupt("return", _this8.$mapVm.forEachFeatureAtPixel(pixel, function (feature) {
                  return feature === selfFeature;
                }, {
                  layerFilter: layerFilter
                }));

              case 9:
              case "end":
                return _context9.stop();
            }
          }
        }, _callee9);
      }))();
    }
  })
};

function defineServices$4() {
  var _this9 = this;

  Object.defineProperties(this, {
    $feature: {
      enumerable: true,
      get: function get() {
        return _this9.$olObject;
      }
    },
    $layerVm: {
      enumerable: true,
      get: function get() {
        var _this9$$services;

        return (_this9$$services = _this9.$services) === null || _this9$$services === void 0 ? void 0 : _this9$$services.layerVm;
      }
    },
    $mapVm: {
      enumerable: true,
      get: function get() {
        var _this9$$services2;

        return (_this9$$services2 = _this9.$services) === null || _this9$$services2 === void 0 ? void 0 : _this9$$services2.mapVm;
      }
    },
    $featuresContainer: {
      enumerable: true,
      get: function get() {
        var _this9$$services3;

        return (_this9$$services3 = _this9.$services) === null || _this9$$services3 === void 0 ? void 0 : _this9$$services3.featuresContainer;
      }
    }
  });
}

function subscribeToEvents() {
  return _subscribeToEvents.apply(this, arguments);
}

function _subscribeToEvents() {
  _subscribeToEvents = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee10() {
    var _this10 = this;

    var propChanges;
    return _regeneratorRuntime.wrap(function _callee10$(_context10) {
      while (1) {
        switch (_context10.prev = _context10.next) {
          case 0:
            propChanges = fromOlEvent(this.$feature, ObjectEventType.PROPERTYCHANGE, function (_ref5) {
              var key = _ref5.key;

              switch (key) {
                case _this10.$feature.getGeometryName():
                  return {
                    prop: 'geometry',
                    value: _this10.writeGeometryInDataProj(_this10.getGeometry()),
                    compareWith: _this10.currentGeometryDataProj
                  };

                default:
                  return {
                    prop: 'properties',
                    value: _objectSpread$2(_objectSpread$2({}, _this10.properties), {}, _defineProperty({}, key, _this10.$feature.get(key))),
                    compareWith: _this10.currentProperties
                  };
              }
            }).pipe(skipWhile(function (_ref6) {
              var value = _ref6.value,
                  compareWith = _ref6.compareWith;
              return isEqual(value, compareWith);
            }));
            this.subscribeTo(propChanges, function () {
              ++_this10.rev;
            });

          case 2:
          case "end":
            return _context10.stop();
        }
      }
    }, _callee10, this);
  }));
  return _subscribeToEvents.apply(this, arguments);
}

var featureHelper = {
  methods: {
    /**
     * @param {FeatureLike} feature
     * @param {boolean} [viewProj=false]
     * @return {Promise<Feature>}
     * @protected
     */
    initializeFeature: function initializeFeature$1(feature) {
      var _arguments = arguments,
          _this = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
        var _feature;

        var viewProj;
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                viewProj = _arguments.length > 1 && _arguments[1] !== undefined ? _arguments[1] : false;

                if (!isFunction((_feature = feature) === null || _feature === void 0 ? void 0 : _feature.resolveOlObject)) {
                  _context.next = 7;
                  break;
                }

                _context.next = 4;
                return feature.resolveOlObject();

              case 4:
                feature = _context.sent;
                _context.next = 8;
                break;

              case 7:
                if (isPlainObject(feature)) {
                  if (viewProj) {
                    feature = _this.readFeatureInViewProj(feature);
                  } else {
                    feature = _this.readFeatureInDataProj(feature);
                  }
                }

              case 8:
                return _context.abrupt("return", initializeFeature(feature));

              case 9:
              case "end":
                return _context.stop();
            }
          }
        }, _callee);
      }))();
    },

    /**
     * @param {module:ol/Feature~Feature} feature
     * @param {module:ol/Feature~Feature} newFeature
     * @protected
     */
    updateFeature: function updateFeature(feature, newFeature) {
      var featureJson = this.writeFeatureInViewProj(feature);
      var newFeatureJson = this.writeFeatureInViewProj(newFeature);
      if (isEqual(featureJson, newFeatureJson)) return;

      if (getFeatureId(feature) !== getFeatureId(newFeature)) {
        setFeatureId(feature, getFeatureId(newFeature));
      }

      var properties = getFeatureProperties(newFeature);
      var currentProperties = getFeatureProperties(feature);

      if (!isEqual(properties, currentProperties)) {
        setFeatureProperties(feature, properties);
      }

      var geomJson = get$1(newFeatureJson, "properties.".concat(CIRCLE_SERIALIZE_PROP)) || newFeatureJson.geometry || null;
      var currentGeomJson = get$1(featureJson, "properties.".concat(CIRCLE_SERIALIZE_PROP)) || featureJson.geometry || null;

      if (!isEqual(geomJson, currentGeomJson)) {
        feature.setGeometry(newFeature.getGeometry() || null);
      }

      var style = newFeature.getStyle();
      var currentStyle = feature.getStyle();

      if (isFunction(currentStyle) && isFunction(style)) {
        if (currentStyle !== style) {
          feature.setStyle(style);
        }
      } else if (isFunction(style)) {
        feature.setStyle(style);
      }

      var styleJson = get$1(newFeatureJson, "properties.".concat(STYLE_SERIALIZE_PROP)) || null;
      var currentStyleJson = get$1(featureJson, "properties.".concat(STYLE_SERIALIZE_PROP)) || null;

      if (!isEqual(styleJson, currentStyleJson)) {
        feature.setStyle(style);
      }
    }
  }
};

/**
 * @typedef {module:ol/Feature~Feature|Object} FeatureLike
 */

/**
 * Features container
 */

var featuresContainer = {
  mixins: [identMap, rxSubs, projTransforms, featureHelper],
  computed: {
    /**
     * @type {Object[]}
     */
    currentFeaturesDataProj: function currentFeaturesDataProj() {
      var _this = this;

      if (!this.rev) return [];
      return map$1(this.getFeatures(), function (feature) {
        return _this.writeFeatureInDataProj(feature);
      });
    },

    /**
     * @type {Object[]}
     */
    currentFeaturesViewProj: function currentFeaturesViewProj() {
      var _this2 = this;

      if (!this.rev) return [];
      return map$1(this.getFeatures(), function (feature) {
        return _this2.writeFeatureInViewProj(feature);
      });
    },

    /**
     * @returns {string|undefined}
     */
    featuresCollectionIdent: function featuresCollectionIdent() {
      if (!this.olObjIdent) return;
      return this.makeIdent(this.olObjIdent, 'features_collection');
    }
  },
  watch: {
    currentFeaturesDataProj: {
      deep: true,
      handler: /*#__PURE__*/debounce( /*#__PURE__*/function () {
        var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(value, prev) {
          return _regeneratorRuntime.wrap(function _callee$(_context) {
            while (1) {
              switch (_context.prev = _context.next) {
                case 0:
                  if (!isEqual(value, prev)) {
                    _context.next = 2;
                    break;
                  }

                  return _context.abrupt("return");

                case 2:
                  this.$emit('update:features', clonePlainObject(value));

                case 3:
                case "end":
                  return _context.stop();
              }
            }
          }, _callee, this);
        }));

        return function (_x, _x2) {
          return _ref.apply(this, arguments);
        };
      }(), FRAME_TIME)
    },
    featuresCollectionIdent: /*#__PURE__*/sequential(function (value, prevValue) {
      if (value && prevValue) {
        this.moveInstance(value, prevValue);
      } else if (value && !prevValue && this.$featuresCollection) {
        this.setInstance(value, this.$featuresCollection);
      } else if (!value && prevValue) {
        this.unsetInstance(prevValue);
      }
    })
  },
  created: function created() {
    /**
     * @type {module:ol/Collection~Collection<module:ol/Feature~Feature>}
     * @private
     */
    this._featuresCollection = this.instanceFactoryCall(this.featuresCollectionIdent, function () {
      return new Collection();
    });
    this._featureSubs = {};
    defineServices$5.call(this);
  },
  methods: {
    /**
     * @returns {{readonly featuresContainer: Object}}
     * @protected
     */
    getServices: function getServices() {
      var vm = this;
      return {
        get featuresContainer() {
          return vm;
        }

      };
    },

    /**
     * @return {void}
     * @protected
     */
    subscribeAll: function subscribeAll() {
      subscribeToCollectionEvents$1.call(this);
    },

    /**
     * @param {FeatureLike[]|module:ol/Collection~Collection<FeatureLike>} features
     * @param {boolean} [viewProj=false]
     * @return {Promise<void>}
     */
    addFeatures: function addFeatures(features) {
      var _arguments = arguments,
          _this3 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
        var viewProj;
        return _regeneratorRuntime.wrap(function _callee2$(_context2) {
          while (1) {
            switch (_context2.prev = _context2.next) {
              case 0:
                viewProj = _arguments.length > 1 && _arguments[1] !== undefined ? _arguments[1] : false;
                _context2.next = 3;
                return Promise.all(map$1(features, function (feature) {
                  return _this3.addFeature(feature, viewProj);
                }));

              case 3:
              case "end":
                return _context2.stop();
            }
          }
        }, _callee2);
      }))();
    },

    /**
     * @param {FeatureLike} feature
     * @param {boolean} [viewProj=false]
     * @return {Promise<void>}
     */
    addFeature: function addFeature(feature) {
      var _arguments2 = arguments,
          _this4 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3() {
        var viewProj, foundFeature;
        return _regeneratorRuntime.wrap(function _callee3$(_context3) {
          while (1) {
            switch (_context3.prev = _context3.next) {
              case 0:
                viewProj = _arguments2.length > 1 && _arguments2[1] !== undefined ? _arguments2[1] : false;
                _context3.next = 3;
                return _this4.initializeFeature(feature, viewProj);

              case 3:
                feature = _context3.sent;
                instanceOf(feature, Feature); // todo add hash {featureId => featureIdx, ....}

                foundFeature = _this4.getFeatureById(getFeatureId(feature));

                if (foundFeature == null) {
                  _this4.$featuresCollection.push(feature);
                } else {
                  _this4.updateFeature(foundFeature, feature);
                }

              case 7:
              case "end":
                return _context3.stop();
            }
          }
        }, _callee3);
      }))();
    },

    /**
     * @param {FeatureLike[]|module:ol/Collection~Collection<FeatureLike>} features
     * @return {Promise<void>}
     */
    removeFeatures: function removeFeatures(features) {
      var _this5 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee4() {
        return _regeneratorRuntime.wrap(function _callee4$(_context4) {
          while (1) {
            switch (_context4.prev = _context4.next) {
              case 0:
                _context4.next = 2;
                return Promise.all(map$1(features, _this5.removeFeature.bind(_this5)));

              case 2:
              case "end":
                return _context4.stop();
            }
          }
        }, _callee4);
      }))();
    },

    /**
     * @param {FeatureLike} feature
     * @return {Promise<void>}
     */
    removeFeature: function removeFeature(feature) {
      var _this6 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee5() {
        return _regeneratorRuntime.wrap(function _callee5$(_context5) {
          while (1) {
            switch (_context5.prev = _context5.next) {
              case 0:
                if (!isFunction(feature.resolveOlObject)) {
                  _context5.next = 4;
                  break;
                }

                _context5.next = 3;
                return feature.resolveOlObject();

              case 3:
                feature = _context5.sent;

              case 4:
                feature = _this6.getFeatureById(getFeatureId(feature));

                if (feature) {
                  _context5.next = 7;
                  break;
                }

                return _context5.abrupt("return");

              case 7:
                _this6.$featuresCollection.remove(feature);

              case 8:
              case "end":
                return _context5.stop();
            }
          }
        }, _callee5);
      }))();
    },

    /**
     * @return {void}
     */
    clearFeatures: function clearFeatures() {
      this.$featuresCollection.clear();
    },

    /**
     * @param {string|number} featureId
     * @return {module:ol/Feature~Feature|undefined}
     */
    getFeatureById: function getFeatureById(featureId) {
      // todo add hash {featureId => featureIdx, ....}
      return find(this.getFeatures(), function (feature) {
        return getFeatureId(feature) === featureId;
      });
    },

    /**
     * @return {Array<module:ol/Feature~Feature>}
     */
    getFeatures: function getFeatures() {
      return this.$featuresCollection.getArray();
    },

    /**
     * @return {module:ol/Collection~Collection<module:ol/Feature~Feature>}
     */
    getFeaturesCollection: function getFeaturesCollection() {
      return this._featuresCollection;
    }
  }
};

function defineServices$5() {
  Object.defineProperties(this, {
    $featuresCollection: {
      enumerable: true,
      get: this.getFeaturesCollection
    }
  });
}

function subscribeToCollectionEvents$1() {
  var _this7 = this;

  var vm = this;
  var adds = fromOlEvent(this.$featuresCollection, CollectionEventType.ADD).pipe(mergeMap(function (_ref2) {
    var type = _ref2.type,
        element = _ref2.element;
    return from(_this7.initializeFeature(element)).pipe(map(function (element) {
      return {
        type: type,
        element: element
      };
    }));
  }), tap(function (_ref3) {
    var type = _ref3.type,
        element = _ref3.element;
    var uid = getUid(element);
    var propChanges = fromOlEvent(element, ObjectEventType.PROPERTYCHANGE);
    var changes = fromOlEvent(element, EventType.CHANGE);
    var events = merge(propChanges, changes).pipe(debounceTime(FRAME_TIME));
    _this7._featureSubs[uid] = _this7.subscribeTo(events, function () {
      ++_this7.rev;
    });
  }));
  var removes = fromOlEvent(this.$featuresCollection, CollectionEventType.REMOVE).pipe(tap(function (_ref4) {
    var type = _ref4.type,
        element = _ref4.element;
    var uid = getUid(element);

    if (_this7._featureSubs[uid]) {
      _this7.unsubscribe(_this7._featureSubs[uid]);

      delete _this7._featureSubs[uid];
    }
  }));
  var events = merge(adds, removes).pipe(map(function (_ref5) {
    var type = _ref5.type,
        element = _ref5.element;
    return {
      type: type,
      feature: element,

      get json() {
        if (!this._json) {
          this._json = vm.writeFeatureInDataProj(this.feature);
        }

        return this._json;
      }

    };
  }), bufferDebounceTime(FRAME_TIME));
  this.subscribeTo(events, function (events) {
    ++_this7.rev;

    _this7.$nextTick(function () {
      forEach(events, function (evt) {
        _this7.$emit(evt.type + 'feature', evt); // todo remove in v0.13.x


        _this7.$emit(evt.type + ':feature', evt.feature);
      });
    });
  });
}

/**
 * @typedef {module:ol/style/Fill~Fill|Object|undefined} FillStyleLike
 */

/**
 * @typedef {Object} FillStyleTarget
 * @property {function(): module:ol/style/Fill~Fill|undefined} getFill
 * @property {function(module:ol/style/Fill~Fill|undefined): void} setFill
 */

/**
 * Fill style container.
 */

var fillStyleContainer = {
  computed: {
    currentFill: function currentFill() {
      if (!(this.rev && this.$fill)) return;
      return dumpFillStyle(this.$fill);
    }
  },
  watch: {
    currentFill: {
      deep: true,
      handler: /*#__PURE__*/debounce(function (value, prev) {
        if (isEqual(value, prev)) return;
        this.$emit('update:fill', value && clonePlainObject(value));
      }, FRAME_TIME)
    }
  },
  created: function created() {
    this._fill = undefined;
    this._fillVm = undefined;
    defineServices$6.call(this);
  },
  methods: {
    /**
     * @returns {{readonly fillStyleContainer: Object}}
     */
    getServices: function getServices() {
      var vm = this;
      return {
        get fillStyleContainer() {
          return vm;
        }

      };
    },
    getFillStyleTarget: function getFillStyleTarget() {
      throw new Error("".concat(this.vmName, " not implemented method: getFillStyleTarget()"));
    },

    /**
     * @returns {module:ol/style/Fill~Fill|undefined}
     */
    getFill: function getFill() {
      return this._fill;
    },

    /**
     * @return {Object}
     */
    getFillVm: function getFillVm() {
      return this._fillVm;
    },

    /**
     * @param {module:ol/style/Fill~Fill|undefined} fill
     * @returns {Promise<void>}
     */
    setFill: function setFill(fill) {
      var _this = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
        var _fill, _fill2;

        var fillTarget;
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                if (!isFunction((_fill = fill) === null || _fill === void 0 ? void 0 : _fill.resolveOlObject)) {
                  _context.next = 4;
                  break;
                }

                _context.next = 3;
                return fill.resolveOlObject();

              case 3:
                fill = _context.sent;

              case 4:
                fill || (fill = undefined);

                if (!(fill === _this._fill)) {
                  _context.next = 7;
                  break;
                }

                return _context.abrupt("return");

              case 7:
                _context.next = 9;
                return _this.getFillStyleTarget();

              case 9:
                fillTarget = _context.sent;

                if (fillTarget) {
                  _context.next = 12;
                  break;
                }

                return _context.abrupt("return");

              case 12:
                _this._fill = fill;
                _this._fillVm = ((_fill2 = fill) === null || _fill2 === void 0 ? void 0 : _fill2.vm) && fill.vm[0];
                _context.next = 16;
                return fillTarget.setFill(fill);

              case 16:
              case "end":
                return _context.stop();
            }
          }
        }, _callee);
      }))();
    }
  }
};

function defineServices$6() {
  Object.defineProperties(this, {
    $fill: {
      enumerable: true,
      get: this.getFill
    },
    $fillVm: {
      enumerable: true,
      get: this.getFillVm
    }
  });
}

function ownKeys$3(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$3(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$3(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$3(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
/**
 * Base geometry mixin.
 */

var geometry = {
  mixins: [stubVNode, projTransforms, olCmp, waitForMap],
  stubVNode: {
    empty: function empty() {
      return this.vmId;
    }
  },
  data: function data() {
    return {
      viewProjection: EPSG_3857,
      dataProjection: EPSG_3857
    };
  },
  computed: {
    type: function type() {
      if (!(this.rev && this.$geometry)) return;
      return this.getTypeInternal();
    },
    currentExtentDataProj: function currentExtentDataProj() {
      if (!(this.rev && this.$geometry)) return;
      return this.getExtentSync();
    },
    currentExtentViewProj: function currentExtentViewProj() {
      if (!(this.rev && this.$geometry)) return;
      return this.getExtentSync(null, true);
    }
  },
  created: function created() {
    defineServices$7.call(this);
  },
  methods: _objectSpread$3(_objectSpread$3(_objectSpread$3({
    /**
     * @return {Promise<module:ol/geom/Geometry~Geometry>}
     * @protected
     */
    createOlObject: function createOlObject() {
      var _this = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                _context.t0 = initializeGeometry;
                _context.next = 3;
                return _this.createGeometry();

              case 3:
                _context.t1 = _context.sent;
                _context.t2 = _this.currentId;
                return _context.abrupt("return", (0, _context.t0)(_context.t1, _context.t2));

              case 6:
              case "end":
                return _context.stop();
            }
          }
        }, _callee);
      }))();
    },

    /**
     * @return {module:ol/geom/Geometry~Geometry|Promise<module:ol/geom/Geometry~Geometry>}
     * @protected
     * @abstract
     */
    createGeometry: function createGeometry() {
      throw new Error("".concat(this.vmName, " not implemented method: createGeometry()"));
    },

    /**
     * @return {Promise<void>}
     * @protected
     */
    mount: function mount() {
      var _this2 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
        return _regeneratorRuntime.wrap(function _callee2$(_context2) {
          while (1) {
            switch (_context2.prev = _context2.next) {
              case 0:
                if (!_this2.$geometryContainer) {
                  _context2.next = 3;
                  break;
                }

                _context2.next = 3;
                return _this2.$geometryContainer.setGeometry(_this2);

              case 3:
                return _context2.abrupt("return", olCmp.methods.mount.call(_this2));

              case 4:
              case "end":
                return _context2.stop();
            }
          }
        }, _callee2);
      }))();
    },

    /**
     * @return {Promise<void>}
     * @protected
     */
    unmount: function unmount() {
      var _this3 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3() {
        return _regeneratorRuntime.wrap(function _callee3$(_context3) {
          while (1) {
            switch (_context3.prev = _context3.next) {
              case 0:
                if (!(_this3.$geometryContainer && _this3.$geometryContainer.getGeometryVm() === _this3)) {
                  _context3.next = 3;
                  break;
                }

                _context3.next = 3;
                return _this3.$geometryContainer.setGeometry(null);

              case 3:
                return _context3.abrupt("return", olCmp.methods.unmount.call(_this3));

              case 4:
              case "end":
                return _context3.stop();
            }
          }
        }, _callee3);
      }))();
    },

    /**
     * @return {Object}
     * @protected
     */
    getServices: function getServices() {
      var vm = this;
      return mergeDescriptors(olCmp.methods.getServices.call(this), {
        get geometryVm() {
          return vm;
        }

      });
    },

    /**
     * @returns {void}
     */
    subscribeAll: function subscribeAll() {
      olCmp.methods.subscribeAll.call(this);
      subscribeToGeometryEvents.call(this);
    },

    /**
     * @return {Promise<module:ol/geom/Geometry~Geometry>}
     */
    resolveGeometry: olCmp.methods.resolveOlObject
  }, /*#__PURE__*/pick(olCmp.methods, ['init', 'deinit', 'beforeMount', 'refresh', 'scheduleRefresh', 'remount', 'scheduleRemount', 'recreate', 'scheduleRecreate', 'resolveOlObject'])), /*#__PURE__*/pick(waitForMap.methods, ['beforeInit'])), {}, {
    /**
     * @returns {string|number}
     */
    getIdInternal: function getIdInternal() {
      return getGeometryId(this.$geometry);
    },

    /**
     * @param {string|number} id
     * @returns {void}
     */
    setIdInternal: function setIdInternal(id) {
      assert(id != null && id !== '', 'Invalid geometry id');
      if (id === this.getIdInternal()) return;
      setGeometryId(this.$geometry, id);
    },

    /**
     * @returns {Promise<string>}
     */
    getType: function getType() {
      var _this4 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee4() {
        return _regeneratorRuntime.wrap(function _callee4$(_context4) {
          while (1) {
            switch (_context4.prev = _context4.next) {
              case 0:
                _context4.next = 2;
                return _this4.resolveGeometry();

              case 2:
                return _context4.abrupt("return", _this4.getTypeInternal());

              case 3:
              case "end":
                return _context4.stop();
            }
          }
        }, _callee4);
      }))();
    },

    /**
     * @return {string}
     * @protected
     */
    getTypeInternal: function getTypeInternal() {
      return this.$geometry.getType();
    },

    /**
     * @param {number[]|undefined} [extent]
     * @param {boolean} [viewProj=false]
     * @returns {Promise<number[]>}
     */
    getExtent: function getExtent(extent) {
      var _arguments = arguments,
          _this5 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee5() {
        var viewProj;
        return _regeneratorRuntime.wrap(function _callee5$(_context5) {
          while (1) {
            switch (_context5.prev = _context5.next) {
              case 0:
                viewProj = _arguments.length > 1 && _arguments[1] !== undefined ? _arguments[1] : false;
                _context5.next = 3;
                return _this5.resolveGeometry();

              case 3:
                return _context5.abrupt("return", _this5.getExtentSync(extent, viewProj));

              case 4:
              case "end":
                return _context5.stop();
            }
          }
        }, _callee5);
      }))();
    },

    /**
     * @param {number[]|undefined} extent
     * @param {boolean} [viewProj=false]
     * @return {number[]}
     */
    getExtentSync: function getExtentSync(extent) {
      var viewProj = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;

      if (viewProj) {
        return roundExtent(this.$geometry.getExtent(extent));
      }

      return this.extentToDataProj(this.$geometry.getExtent(this.extentToViewProj(extent)));
    },

    /**
     * @param {number[]} point
     * @param {number[]} [closestPoint]
     * @param {boolean} [viewProj=false]
     * @returns {Promise<number[]>}
     */
    getClosestPoint: function getClosestPoint(point, closestPoint) {
      var _arguments2 = arguments,
          _this6 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee6() {
        var viewProj;
        return _regeneratorRuntime.wrap(function _callee6$(_context6) {
          while (1) {
            switch (_context6.prev = _context6.next) {
              case 0:
                viewProj = _arguments2.length > 2 && _arguments2[2] !== undefined ? _arguments2[2] : false;

                if (!viewProj) {
                  _context6.next = 7;
                  break;
                }

                _context6.t0 = roundPointCoords;
                _context6.next = 5;
                return _this6.resolveGeometry();

              case 5:
                _context6.t1 = _context6.sent.getClosestPoint(point, closestPoint);
                return _context6.abrupt("return", (0, _context6.t0)(_context6.t1));

              case 7:
                _context6.t2 = _this6;
                _context6.next = 10;
                return _this6.resolveGeometry();

              case 10:
                _context6.t3 = _context6.sent.getClosestPoint(_this6.pointToViewProj(point), _this6.pointToViewProj(closestPoint));
                return _context6.abrupt("return", _context6.t2.pointToDataProj.call(_context6.t2, _context6.t3));

              case 12:
              case "end":
                return _context6.stop();
            }
          }
        }, _callee6);
      }))();
    },

    /**
     * @param {number[]} coordinate
     * @param {boolean} [viewProj=false]
     * @returns {Promise<boolean>}
     */
    intersectsCoordinate: function intersectsCoordinate(coordinate) {
      var _arguments3 = arguments,
          _this7 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee7() {
        var viewProj;
        return _regeneratorRuntime.wrap(function _callee7$(_context7) {
          while (1) {
            switch (_context7.prev = _context7.next) {
              case 0:
                viewProj = _arguments3.length > 1 && _arguments3[1] !== undefined ? _arguments3[1] : false;

                if (!viewProj) {
                  coordinate = _this7.pointToViewProj(coordinate);
                }

                _context7.next = 4;
                return _this7.resolveGeometry();

              case 4:
                return _context7.abrupt("return", _context7.sent.intersectsCoordinate(coordinate));

              case 5:
              case "end":
                return _context7.stop();
            }
          }
        }, _callee7);
      }))();
    },

    /**
     * @param {number[]} extent
     * @param {boolean} [viewProj=false]
     * @returns {Promise<boolean>}
     */
    intersectsExtent: function intersectsExtent(extent) {
      var _arguments4 = arguments,
          _this8 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee8() {
        var viewProj;
        return _regeneratorRuntime.wrap(function _callee8$(_context8) {
          while (1) {
            switch (_context8.prev = _context8.next) {
              case 0:
                viewProj = _arguments4.length > 1 && _arguments4[1] !== undefined ? _arguments4[1] : false;

                if (!viewProj) {
                  extent = _this8.extentToViewProj(extent);
                }

                _context8.next = 4;
                return _this8.resolveGeometry();

              case 4:
                return _context8.abrupt("return", _context8.sent.intersectsExtent(extent));

              case 5:
              case "end":
                return _context8.stop();
            }
          }
        }, _callee8);
      }))();
    },

    /**
     * @param {number} angle Angle in radians
     * @param {number[]} anchor
     * @param {boolean} [viewProj=false]
     * @returns {Promise<void>}
     */
    rotate: function rotate(angle, anchor) {
      var _arguments5 = arguments,
          _this9 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee9() {
        var viewProj;
        return _regeneratorRuntime.wrap(function _callee9$(_context9) {
          while (1) {
            switch (_context9.prev = _context9.next) {
              case 0:
                viewProj = _arguments5.length > 2 && _arguments5[2] !== undefined ? _arguments5[2] : false;

                if (!viewProj) {
                  anchor = _this9.pointToViewProj(anchor);
                }

                _context9.next = 4;
                return _this9.resolveGeometry();

              case 4:
                _context9.sent.rotate(angle, anchor);

              case 5:
              case "end":
                return _context9.stop();
            }
          }
        }, _callee9);
      }))();
    },

    /**
     * @param {number} sx
     * @param {number} [sy]
     * @param {number[]} [anchor]
     * @param {boolean} [viewProj=false]
     * @returns {Promise<void>}
     */
    scale: function scale(sx, sy, anchor) {
      var _arguments6 = arguments,
          _this10 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee10() {
        var viewProj, _this10$pointToViewPr, _this10$pointToViewPr2, _this10$pointToViewPr3, _this10$pointToViewPr4;

        return _regeneratorRuntime.wrap(function _callee10$(_context10) {
          while (1) {
            switch (_context10.prev = _context10.next) {
              case 0:
                viewProj = _arguments6.length > 3 && _arguments6[3] !== undefined ? _arguments6[3] : false;

                if (!viewProj) {
                  anchor = _this10.pointToViewProj(anchor);
                  _this10$pointToViewPr = _this10.pointToViewProj([sx, 0]);
                  _this10$pointToViewPr2 = _slicedToArray(_this10$pointToViewPr, 1);
                  sx = _this10$pointToViewPr2[0];
                  _this10$pointToViewPr3 = _this10.pointToViewProj([0, sy]);
                  _this10$pointToViewPr4 = _slicedToArray(_this10$pointToViewPr3, 2);
                  sy = _this10$pointToViewPr4[1];
                }

                _context10.next = 4;
                return _this10.resolveGeometry();

              case 4:
                _context10.sent.scale(sx, sy, anchor);

              case 5:
              case "end":
                return _context10.stop();
            }
          }
        }, _callee10);
      }))();
    },

    /**
     * @param {number} tolerance
     * @returns {Promise<module:ol/geom/Geometry~Geometry>}
     */
    simplify: function simplify(tolerance) {
      var _this11 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee11() {
        return _regeneratorRuntime.wrap(function _callee11$(_context11) {
          while (1) {
            switch (_context11.prev = _context11.next) {
              case 0:
                _context11.next = 2;
                return _this11.resolveGeometry();

              case 2:
                return _context11.abrupt("return", _context11.sent.simplify(tolerance));

              case 3:
              case "end":
                return _context11.stop();
            }
          }
        }, _callee11);
      }))();
    },

    /**
     * @param dx
     * @param dy
     * @param {boolean} [viewProj=false]
     * @returns {Promise<*>}
     */
    translate: function translate(dx, dy) {
      var _arguments7 = arguments,
          _this12 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee12() {
        var viewProj, _this12$pointToViewPr, _this12$pointToViewPr2, _this12$pointToViewPr3, _this12$pointToViewPr4;

        return _regeneratorRuntime.wrap(function _callee12$(_context12) {
          while (1) {
            switch (_context12.prev = _context12.next) {
              case 0:
                viewProj = _arguments7.length > 2 && _arguments7[2] !== undefined ? _arguments7[2] : false;

                if (!viewProj) {
                  _this12$pointToViewPr = _this12.pointToViewProj([dx, 0]);
                  _this12$pointToViewPr2 = _slicedToArray(_this12$pointToViewPr, 1);
                  dx = _this12$pointToViewPr2[0];
                  _this12$pointToViewPr3 = _this12.pointToViewProj([0, dy]);
                  _this12$pointToViewPr4 = _slicedToArray(_this12$pointToViewPr3, 2);
                  dy = _this12$pointToViewPr4[1];
                }

                _context12.next = 4;
                return _this12.resolveGeometry();

              case 4:
                return _context12.abrupt("return", _context12.sent.translate(dx, dy));

              case 5:
              case "end":
                return _context12.stop();
            }
          }
        }, _callee12);
      }))();
    }
  })
};

function defineServices$7() {
  var _this13 = this;

  Object.defineProperties(this, {
    /**
     * @type {module:ol/geom/Geometry~Geometry|undefined}
     */
    $geometry: {
      enumerable: true,
      get: function get() {
        return _this13.$olObject;
      }
    },

    /**
     * @type {Object|undefined}
     */
    $mapVm: {
      enumerable: true,
      get: function get() {
        var _this13$$services;

        return (_this13$$services = _this13.$services) === null || _this13$$services === void 0 ? void 0 : _this13$$services.mapVm;
      }
    },

    /**
     * @type {Object|undefined}
     */
    $viewVm: {
      enumerable: true,
      get: function get() {
        var _this13$$services2;

        return (_this13$$services2 = _this13.$services) === null || _this13$$services2 === void 0 ? void 0 : _this13$$services2.viewVm;
      }
    },

    /**
     * @type {Object|undefined}
     */
    $geometryContainer: {
      enumerable: true,
      get: function get() {
        var _this13$$services3;

        return (_this13$$services3 = _this13.$services) === null || _this13$$services3 === void 0 ? void 0 : _this13$$services3.geometryContainer;
      }
    }
  });
}

function subscribeToGeometryEvents() {
  return _subscribeToGeometryEvents.apply(this, arguments);
}

function _subscribeToGeometryEvents() {
  _subscribeToGeometryEvents = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee13() {
    var _this14 = this;

    var prefixKey, propChanges;
    return _regeneratorRuntime.wrap(function _callee13$(_context13) {
      while (1) {
        switch (_context13.prev = _context13.next) {
          case 0:
            prefixKey = addPrefix('current');
            propChanges = fromOlChangeEvent(this.$geometry, ['id'], true, function (evt) {
              return _objectSpread$3(_objectSpread$3({}, evt), {}, {
                compareWith: _this14[prefixKey(evt.prop)]
              });
            }).pipe(skipWhile(function (_ref) {
              var compareWith = _ref.compareWith,
                  value = _ref.value;
              return isEqual(value, compareWith);
            }));
            this.subscribeTo(propChanges, function () {
              ++_this14.rev;
            });

          case 3:
          case "end":
            return _context13.stop();
        }
      }
    }, _callee13, this);
  }));
  return _subscribeToGeometryEvents.apply(this, arguments);
}

/**
 * Source container mixin.
 */

var sourceContainer = {
  computed: {
    currentSource: function currentSource() {
      if (!(this.rev && this.$source)) return;
      return getSourceId(this.$source);
    }
  },
  watch: {
    currentSource: debounce(function (value, prev) {
      if (value === prev) return;
      this.$emit('update:source', value);
    }, FRAME_TIME)
  },
  created: function created() {
    /**
     * @type {module:ol/source/Source~Source|undefined}
     * @private
     */
    this._source = null;
    /**
     * @type {Object|undefined}
     * @private
     */

    this._sourceVm = null;
    defineServices$8.call(this);
  },
  methods: {
    /**
     * @returns {{readonly sourceContainer: Object}}
     * @protected
     */
    getServices: function getServices() {
      var vm = this;
      return {
        get sourceContainer() {
          return vm;
        }

      };
    },

    /**
     * @return {Promise<SourceTarget|undefined>}
     * @protected
     */
    getSourceTarget: function getSourceTarget() {
      throw new Error("".concat(this.vmName, " not implemented method: getSourceTarget()"));
    },

    /**
     * @return {module:ol/source/Source~Source|undefined}
     */
    getSource: function getSource() {
      return this._source;
    },

    /**
     * @return {Object}
     */
    getSourceVm: function getSourceVm() {
      return this._sourceVm;
    },

    /**
     * @param {SourceLike|undefined} source
     * @return {void}
     */
    setSource: function setSource(source) {
      var _this = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
        var _source, _source2;

        var sourceTarget;
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                if (!isFunction((_source = source) === null || _source === void 0 ? void 0 : _source.resolveOlObject)) {
                  _context.next = 4;
                  break;
                }

                _context.next = 3;
                return source.resolveOlObject();

              case 3:
                source = _context.sent;

              case 4:
                source || (source = null);

                if (!(source === _this._source)) {
                  _context.next = 7;
                  break;
                }

                return _context.abrupt("return");

              case 7:
                _context.next = 9;
                return _this.getSourceTarget();

              case 9:
                sourceTarget = _context.sent;

                if (sourceTarget) {
                  _context.next = 12;
                  break;
                }

                return _context.abrupt("return");

              case 12:
                _this._source = source;
                _this._sourceVm = ((_source2 = source) === null || _source2 === void 0 ? void 0 : _source2.vm) && source.vm[0];
                _context.next = 16;
                return sourceTarget.setSource(source);

              case 16:
              case "end":
                return _context.stop();
            }
          }
        }, _callee);
      }))();
    }
  }
};

function defineServices$8() {
  Object.defineProperties(this, {
    $source: {
      enumerable: true,
      get: this.getSource
    },
    $sourceVm: {
      enumerable: true,
      get: this.getSourceVm
    }
  });
}

function ownKeys$4(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$4(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$4(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$4(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
/**
 * Base simple layer mixin.
 */

var layer = {
  mixins: [sourceContainer, baseLayer],
  props: {
    // ol/layer/Layer

    /**
     * @type {function|undefined}
     */
    render: Function,
    // custom

    /**
     * @type {boolean}
     */
    overlay: {
      type: Boolean,
      default: false
    }
  },
  watch: _objectSpread$4({}, /*#__PURE__*/makeWatchers(['render', 'overlay'], function (prop) {
    return /*#__PURE__*/sequential( /*#__PURE__*/function () {
      var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(val, prev) {
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                if (!isEqual(val, prev)) {
                  _context.next = 2;
                  break;
                }

                return _context.abrupt("return");

              case 2:
                if (process.env.VUELAYERS_DEBUG) {
                  this.$logger.log("".concat(prop, " changed, scheduling recreate..."));
                }

                _context.next = 5;
                return this.scheduleRecreate();

              case 5:
              case "end":
                return _context.stop();
            }
          }
        }, _callee, this);
      }));

      return function (_x, _x2) {
        return _ref.apply(this, arguments);
      };
    }());
  })),
  methods: _objectSpread$4(_objectSpread$4({
    /**
     * @return {Promise<void>}
     * @protected
     */
    mount: function mount() {
      var _this = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
        return _regeneratorRuntime.wrap(function _callee2$(_context2) {
          while (1) {
            switch (_context2.prev = _context2.next) {
              case 0:
                if (!(_this.overlay && _this.$mapVm)) {
                  _context2.next = 4;
                  break;
                }

                _context2.next = 3;
                return _this.setMap(_this.$mapVm);

              case 3:
                return _context2.abrupt("return");

              case 4:
                return _context2.abrupt("return", baseLayer.methods.mount.call(_this));

              case 5:
              case "end":
                return _context2.stop();
            }
          }
        }, _callee2);
      }))();
    },

    /**
     * @return {Promise<void>}
     * @protected
     */
    unmount: function unmount() {
      var _this2 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3() {
        return _regeneratorRuntime.wrap(function _callee3$(_context3) {
          while (1) {
            switch (_context3.prev = _context3.next) {
              case 0:
                if (!_this2.overlay) {
                  _context3.next = 4;
                  break;
                }

                _context3.next = 3;
                return _this2.setMap(null);

              case 3:
                return _context3.abrupt("return");

              case 4:
                return _context3.abrupt("return", baseLayer.methods.unmount.call(_this2));

              case 5:
              case "end":
                return _context3.stop();
            }
          }
        }, _callee3);
      }))();
    },

    /**
     * @returns {Object}
     * @protected
     */
    getServices: function getServices() {
      return mergeDescriptors(baseLayer.methods.getServices.call(this), sourceContainer.methods.getServices.call(this));
    },

    /**
     * @return {void}
     * @protected
     */
    subscribeAll: function subscribeAll() {
      baseLayer.methods.subscribeAll.call(this);
      subscribeToLayerEvents$1.call(this);
    }
  }, /*#__PURE__*/pick(baseLayer.methods, ['beforeInit', 'init', 'deinit', 'beforeMount', 'refresh', 'scheduleRefresh', 'remount', 'scheduleRemount', 'recreate', 'scheduleRecreate', 'resolveOlObject', 'resolveLayer'])), {}, {
    /**
     * @return {Promise<module:ol/layer/Base~BaseLayer>}
     * @protected
     */
    getSourceTarget: baseLayer.methods.resolveOlObject,

    /**
     * @returns {Promise<module:ol/renderer/Layer~LayerRenderer>}
     */
    getRenderer: function getRenderer() {
      var _this3 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee4() {
        return _regeneratorRuntime.wrap(function _callee4$(_context4) {
          while (1) {
            switch (_context4.prev = _context4.next) {
              case 0:
                _context4.next = 2;
                return _this3.resolveLayer();

              case 2:
                return _context4.abrupt("return", _context4.sent.getRenderer());

              case 3:
              case "end":
                return _context4.stop();
            }
          }
        }, _callee4);
      }))();
    },

    /**
     * @param {module:ol/Map~Map|Object|undefined} map
     * @return {Promise<void>}
     */
    setMap: function setMap(map) {
      var _this4 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee5() {
        return _regeneratorRuntime.wrap(function _callee5$(_context5) {
          while (1) {
            switch (_context5.prev = _context5.next) {
              case 0:
                if (!(map && isFunction(map.resolveOlObject))) {
                  _context5.next = 4;
                  break;
                }

                _context5.next = 3;
                return map.resolveOlObject();

              case 3:
                map = _context5.sent;

              case 4:
                _context5.next = 6;
                return _this4.resolveLayer();

              case 6:
                _context5.sent.setMap(map);

              case 7:
              case "end":
                return _context5.stop();
            }
          }
        }, _callee5);
      }))();
    }
  })
};

function subscribeToLayerEvents$1() {
  return _subscribeToLayerEvents$1.apply(this, arguments);
}

function _subscribeToLayerEvents$1() {
  _subscribeToLayerEvents$1 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee6() {
    var _this5 = this;

    var renderEvents;
    return _regeneratorRuntime.wrap(function _callee6$(_context6) {
      while (1) {
        switch (_context6.prev = _context6.next) {
          case 0:
            renderEvents = fromOlEvent(this.$layer, [RenderEventType.PRERENDER, RenderEventType.POSTRENDER]);
            this.subscribeTo(renderEvents, function (evt) {
              _this5.$emit(evt.type, evt);
            });

          case 2:
          case "end":
            return _context6.stop();
        }
      }
    }, _callee6, this);
  }));
  return _subscribeToLayerEvents$1.apply(this, arguments);
}

function ownKeys$5(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$5(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$5(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$5(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
var imageLayer = {
  mixins: [layer],
  methods: _objectSpread$5({}, /*#__PURE__*/pick(layer.methods, ['beforeInit', 'init', 'deinit', 'beforeMount', 'mount', 'unmount', 'refresh', 'scheduleRefresh', 'remount', 'scheduleRemount', 'recreate', 'scheduleRecreate', 'subscribeAll', 'getServices', 'resolveOlObject', 'resolveLayer']))
};

function ownKeys$6(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$6(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$6(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$6(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
/**
 * Base source mixin.
 */

var source = {
  mixins: [stubVNode, projTransforms, olCmp, waitForMap],
  stubVNode: {
    empty: function empty() {
      return this.vmId;
    }
  },
  props: {
    // ol/source/Source

    /**
     * @type {string|string[]|undefined}
     */
    attributions: {
      type: [String, Array],
      validator: function validator(value) {
        return isString(value) || isArray(value) && value.every(isString);
      }
    },

    /**
     * @type {boolean}
     */
    attributionsCollapsible: {
      type: Boolean,
      default: true
    },

    /**
     * @type {string|undefined}
     */
    projection: {
      type: String,
      validator: function validator(value) {
        return !!get(value);
      }
    },

    /**
     * @type {boolean}
     */
    wrapX: {
      type: Boolean,
      default: true
    },

    /**
     * @type {string|undefined}
     */
    state: String
  },
  data: function data() {
    return {
      viewProjection: EPSG_3857,
      dataProjection: EPSG_3857
    };
  },
  computed: {
    currentAttributions: function currentAttributions() {
      if (this.rev && this.$source) {
        return this.getAttributionsInternal();
      }

      return this.attributions;
    },
    currentState: function currentState() {
      if (this.rev && this.$source) {
        return this.getStateInternal();
      }

      return this.state;
    }
  },
  watch: {
    attributions: /*#__PURE__*/sequential( /*#__PURE__*/function () {
      var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(value) {
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                _context.next = 2;
                return this.setAttributions(value);

              case 2:
              case "end":
                return _context.stop();
            }
          }
        }, _callee, this);
      }));

      return function (_x) {
        return _ref.apply(this, arguments);
      };
    }()),
    currentAttributions: /*#__PURE__*/debounce(function (value) {
      if (isEqual(value, this.attributions)) return;
      this.$emit('update:attributions', value);
    }, FRAME_TIME),
    state: /*#__PURE__*/sequential( /*#__PURE__*/function () {
      var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(value) {
        return _regeneratorRuntime.wrap(function _callee2$(_context2) {
          while (1) {
            switch (_context2.prev = _context2.next) {
              case 0:
                _context2.next = 2;
                return this.setState(value);

              case 2:
              case "end":
                return _context2.stop();
            }
          }
        }, _callee2, this);
      }));

      return function (_x2) {
        return _ref2.apply(this, arguments);
      };
    }()),
    currentState: /*#__PURE__*/debounce(function (value) {
      this.$emit('update:state', value);
    }, FRAME_TIME),
    attributionsCollapsible: /*#__PURE__*/sequential( /*#__PURE__*/function () {
      var _ref3 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3(value) {
        return _regeneratorRuntime.wrap(function _callee3$(_context3) {
          while (1) {
            switch (_context3.prev = _context3.next) {
              case 0:
                _context3.t0 = value;
                _context3.next = 3;
                return this.getAttributionsCollapsible();

              case 3:
                _context3.t1 = _context3.sent;

                if (!(_context3.t0 === _context3.t1)) {
                  _context3.next = 6;
                  break;
                }

                return _context3.abrupt("return");

              case 6:
                if (process.env.VUELAYERS_DEBUG) {
                  this.$logger.log('attributionsCollapsible changed, scheduling recreate...');
                }

                _context3.next = 9;
                return this.scheduleRecreate();

              case 9:
              case "end":
                return _context3.stop();
            }
          }
        }, _callee3, this);
      }));

      return function (_x3) {
        return _ref3.apply(this, arguments);
      };
    }()),
    projection: /*#__PURE__*/sequential( /*#__PURE__*/function () {
      var _ref4 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee4(value) {
        var projection;
        return _regeneratorRuntime.wrap(function _callee4$(_context4) {
          while (1) {
            switch (_context4.prev = _context4.next) {
              case 0:
                _context4.next = 2;
                return this.getProjection();

              case 2:
                projection = _context4.sent;

                if (!(value === (projection === null || projection === void 0 ? void 0 : projection.getCode()))) {
                  _context4.next = 5;
                  break;
                }

                return _context4.abrupt("return");

              case 5:
                if (process.env.VUELAYERS_DEBUG) {
                  this.$logger.log('projection changed, scheduling recreate...');
                }

                _context4.next = 8;
                return this.scheduleRecreate();

              case 8:
              case "end":
                return _context4.stop();
            }
          }
        }, _callee4, this);
      }));

      return function (_x4) {
        return _ref4.apply(this, arguments);
      };
    }()),
    wrapX: /*#__PURE__*/sequential( /*#__PURE__*/function () {
      var _ref5 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee5(value) {
        return _regeneratorRuntime.wrap(function _callee5$(_context5) {
          while (1) {
            switch (_context5.prev = _context5.next) {
              case 0:
                _context5.t0 = value;
                _context5.next = 3;
                return this.getWrapX();

              case 3:
                _context5.t1 = _context5.sent;

                if (!(_context5.t0 === _context5.t1)) {
                  _context5.next = 6;
                  break;
                }

                return _context5.abrupt("return");

              case 6:
                if (process.env.VUELAYERS_DEBUG) {
                  this.$logger.log('wrapX changed, scheduling recreate...');
                }

                _context5.next = 9;
                return this.scheduleRecreate();

              case 9:
              case "end":
                return _context5.stop();
            }
          }
        }, _callee5, this);
      }));

      return function (_x5) {
        return _ref5.apply(this, arguments);
      };
    }())
  },
  created: function created() {
    defineServices$9.call(this);
  },
  methods: _objectSpread$6(_objectSpread$6(_objectSpread$6({
    /**
     * @return {Promise<module:ol/source/Source~Source>}
     * @protected
     */
    createOlObject: function createOlObject() {
      var _this = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee6() {
        return _regeneratorRuntime.wrap(function _callee6$(_context6) {
          while (1) {
            switch (_context6.prev = _context6.next) {
              case 0:
                _context6.t0 = initializeSource;
                _context6.next = 3;
                return _this.createSource();

              case 3:
                _context6.t1 = _context6.sent;
                _context6.t2 = _this.currentId;
                return _context6.abrupt("return", (0, _context6.t0)(_context6.t1, _context6.t2));

              case 6:
              case "end":
                return _context6.stop();
            }
          }
        }, _callee6);
      }))();
    },

    /**
     * @return {module:ol/source/Source~Source|Promise<module:ol/source/Source~Source>}
     * @protected
     * @abstract
     */
    createSource: function createSource() {
      throw new Error("".concat(this.vmName, " not implemented method: createSource()"));
    },

    /**
     * @return {Promise<void>}
     * @protected
     */
    mount: function mount() {
      var _this2 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee7() {
        return _regeneratorRuntime.wrap(function _callee7$(_context7) {
          while (1) {
            switch (_context7.prev = _context7.next) {
              case 0:
                if (!_this2.$sourceContainer) {
                  _context7.next = 3;
                  break;
                }

                _context7.next = 3;
                return _this2.$sourceContainer.setSource(_this2);

              case 3:
                return _context7.abrupt("return", olCmp.methods.mount.call(_this2));

              case 4:
              case "end":
                return _context7.stop();
            }
          }
        }, _callee7);
      }))();
    },

    /**
     * @return {Promise<void>}
     * @protected
     */
    unmount: function unmount() {
      var _this3 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee8() {
        return _regeneratorRuntime.wrap(function _callee8$(_context8) {
          while (1) {
            switch (_context8.prev = _context8.next) {
              case 0:
                if (!(_this3.$sourceContainer && _this3.$sourceContainer.getSourceVm() === _this3)) {
                  _context8.next = 3;
                  break;
                }

                _context8.next = 3;
                return _this3.$sourceContainer.setSource(null);

              case 3:
                return _context8.abrupt("return", olCmp.methods.unmount.call(_this3));

              case 4:
              case "end":
                return _context8.stop();
            }
          }
        }, _callee8);
      }))();
    },

    /**
     * @returns {Promise<void>}
     */
    refresh: function refresh() {
      var _this4 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee9() {
        var source;
        return _regeneratorRuntime.wrap(function _callee9$(_context9) {
          while (1) {
            switch (_context9.prev = _context9.next) {
              case 0:
                _context9.next = 2;
                return _this4.resolveSource();

              case 2:
                source = _context9.sent;
                return _context9.abrupt("return", new Promise(function (resolve) {
                  source.once('change', function () {
                    return resolve();
                  });
                  source.refresh();
                }));

              case 4:
              case "end":
                return _context9.stop();
            }
          }
        }, _callee9);
      }))();
    },

    /**
     * @return {Object}
     * @protected
     */
    getServices: function getServices() {
      var vm = this;
      return mergeDescriptors(olCmp.methods.getServices.call(this), {
        get sourceVm() {
          return vm;
        }

      });
    },

    /**
     * @returns {void}
     */
    subscribeAll: function subscribeAll() {
      olCmp.methods.subscribeAll.call(this);
      subscribeToSourceEvents.call(this);
    }
  }, /*#__PURE__*/pick(olCmp.methods, ['init', 'deinit', 'beforeMount', 'refresh', 'scheduleRefresh', 'remount', 'scheduleRemount', 'recreate', 'scheduleRecreate', 'resolveOlObject'])), /*#__PURE__*/pick(waitForMap.methods, ['beforeInit'])), {}, {
    /**
     * @return {Promise<module:ol/source/Source~Source>}
     */
    resolveSource: olCmp.methods.resolveOlObject,

    /**
     * @returns {string|number}
     */
    getIdInternal: function getIdInternal() {
      return getSourceId(this.$source);
    },

    /**
     * @param {string|number} id
     * @returns {void}
     */
    setIdInternal: function setIdInternal(id) {
      assert(id != null && id !== '', 'Invalid source id');
      if (id === this.getIdInternal()) return;
      setSourceId(this.$source, id);
    },

    /**
     * @returns {Promise<string|string[]|undefined>}
     */
    getAttributions: function getAttributions() {
      var _this5 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee10() {
        return _regeneratorRuntime.wrap(function _callee10$(_context10) {
          while (1) {
            switch (_context10.prev = _context10.next) {
              case 0:
                _context10.next = 2;
                return _this5.resolveSource();

              case 2:
                return _context10.abrupt("return", _this5.getAttributionsInternal());

              case 3:
              case "end":
                return _context10.stop();
            }
          }
        }, _callee10);
      }))();
    },

    /**
     * @return {string|string[]|undefined}
     * @protected
     */
    getAttributionsInternal: function getAttributionsInternal() {
      return this.$source.getAttributions();
    },

    /**
     * @param {string} attributions
     * @returns {Promise<void>}
     */
    setAttributions: function setAttributions(attributions) {
      var _this6 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee11() {
        return _regeneratorRuntime.wrap(function _callee11$(_context11) {
          while (1) {
            switch (_context11.prev = _context11.next) {
              case 0:
                _context11.t0 = isEqual;
                _context11.t1 = attributions;
                _context11.next = 4;
                return _this6.getAttributions();

              case 4:
                _context11.t2 = _context11.sent;

                if (!(0, _context11.t0)(_context11.t1, _context11.t2)) {
                  _context11.next = 7;
                  break;
                }

                return _context11.abrupt("return");

              case 7:
                _context11.next = 9;
                return _this6.resolveSource();

              case 9:
                _context11.sent.setAttributions(attributions);

              case 10:
              case "end":
                return _context11.stop();
            }
          }
        }, _callee11);
      }))();
    },

    /**
     * @returns {Promise<boolean>}
     */
    getAttributionsCollapsible: function getAttributionsCollapsible() {
      var _this7 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee12() {
        return _regeneratorRuntime.wrap(function _callee12$(_context12) {
          while (1) {
            switch (_context12.prev = _context12.next) {
              case 0:
                _context12.next = 2;
                return _this7.resolveSource();

              case 2:
                return _context12.abrupt("return", _context12.sent.getAttributionsCollapsible());

              case 3:
              case "end":
                return _context12.stop();
            }
          }
        }, _callee12);
      }))();
    },

    /**
     * @returns {Promise<module:ol/proj/Projection~Projection>}
     */
    getProjection: function getProjection() {
      var _this8 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee13() {
        return _regeneratorRuntime.wrap(function _callee13$(_context13) {
          while (1) {
            switch (_context13.prev = _context13.next) {
              case 0:
                _context13.next = 2;
                return _this8.resolveSource();

              case 2:
                return _context13.abrupt("return", _context13.sent.getProjection());

              case 3:
              case "end":
                return _context13.stop();
            }
          }
        }, _callee13);
      }))();
    },

    /**
     * @returns {Promise<string>}
     */
    getState: function getState() {
      var _this9 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee14() {
        return _regeneratorRuntime.wrap(function _callee14$(_context14) {
          while (1) {
            switch (_context14.prev = _context14.next) {
              case 0:
                _context14.next = 2;
                return _this9.resolveSource();

              case 2:
                return _context14.abrupt("return", _this9.getStateInternal());

              case 3:
              case "end":
                return _context14.stop();
            }
          }
        }, _callee14);
      }))();
    },

    /**
     * @return {string}
     * @protected
     */
    getStateInternal: function getStateInternal() {
      return this.$source.getState();
    },

    /**
     * @param {string} state
     * @return {Promise<void>}
     */
    setState: function setState(state) {
      var _this10 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee15() {
        return _regeneratorRuntime.wrap(function _callee15$(_context15) {
          while (1) {
            switch (_context15.prev = _context15.next) {
              case 0:
                _context15.t0 = state;
                _context15.next = 3;
                return _this10.getState();

              case 3:
                _context15.t1 = _context15.sent;

                if (!(_context15.t0 === _context15.t1)) {
                  _context15.next = 6;
                  break;
                }

                return _context15.abrupt("return");

              case 6:
                _context15.next = 8;
                return _this10.resolveSource();

              case 8:
                _context15.sent.setState(state);

              case 9:
              case "end":
                return _context15.stop();
            }
          }
        }, _callee15);
      }))();
    },

    /**
     * @returns {Promise<boolean>}
     */
    getWrapX: function getWrapX() {
      var _this11 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee16() {
        return _regeneratorRuntime.wrap(function _callee16$(_context16) {
          while (1) {
            switch (_context16.prev = _context16.next) {
              case 0:
                _context16.next = 2;
                return _this11.resolveSource();

              case 2:
                return _context16.abrupt("return", _context16.sent.getWrapX());

              case 3:
              case "end":
                return _context16.stop();
            }
          }
        }, _callee16);
      }))();
    },

    /**
     * @returns {Promise<number[]>}
     */
    getResolutions: function getResolutions() {
      var _this12 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee17() {
        return _regeneratorRuntime.wrap(function _callee17$(_context17) {
          while (1) {
            switch (_context17.prev = _context17.next) {
              case 0:
                _context17.next = 2;
                return _this12.resolveSource();

              case 2:
                return _context17.abrupt("return", _context17.sent.getResolutions());

              case 3:
              case "end":
                return _context17.stop();
            }
          }
        }, _callee17);
      }))();
    }
  })
};

function defineServices$9() {
  var _this13 = this;

  Object.defineProperties(this, {
    /**
     * @type {module:ol/source/Source~Source|undefined}
     */
    $source: {
      enumerable: true,
      get: function get() {
        return _this13.$olObject;
      }
    },

    /**
     * @type {Object|undefined}
     */
    $mapVm: {
      enumerable: true,
      get: function get() {
        var _this13$$services;

        return (_this13$$services = _this13.$services) === null || _this13$$services === void 0 ? void 0 : _this13$$services.mapVm;
      }
    },

    /**
     * @type {Object|undefined}
     */
    $viewVm: {
      enumerable: true,
      get: function get() {
        var _this13$$services2;

        return (_this13$$services2 = _this13.$services) === null || _this13$$services2 === void 0 ? void 0 : _this13$$services2.viewVm;
      }
    },

    /**
     * @type {Object|undefined}
     */
    $sourceContainer: {
      enumerable: true,
      get: function get() {
        var _this13$$services3;

        return (_this13$$services3 = _this13.$services) === null || _this13$$services3 === void 0 ? void 0 : _this13$$services3.sourceContainer;
      }
    }
  });
}

function subscribeToSourceEvents() {
  return _subscribeToSourceEvents.apply(this, arguments);
}

function _subscribeToSourceEvents() {
  _subscribeToSourceEvents = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee18() {
    var _this14 = this;

    var prefixKey, propChanges;
    return _regeneratorRuntime.wrap(function _callee18$(_context18) {
      while (1) {
        switch (_context18.prev = _context18.next) {
          case 0:
            prefixKey = addPrefix('current');
            propChanges = fromOlChangeEvent(this.$source, ['id'], true, function (evt) {
              return _objectSpread$6(_objectSpread$6({}, evt), {}, {
                compareWith: _this14[prefixKey(evt.prop)]
              });
            }).pipe(skipWhile(function (_ref6) {
              var value = _ref6.value,
                  compareWith = _ref6.compareWith;
              return isEqual(value, compareWith);
            }));
            this.subscribeTo(propChanges, function () {
              ++_this14.rev;
            });

          case 3:
          case "end":
            return _context18.stop();
        }
      }
    }, _callee18, this);
  }));
  return _subscribeToSourceEvents.apply(this, arguments);
}

function ownKeys$7(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$7(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$7(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$7(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
var ImageSourceEventType = {
  IMAGELOADSTART: 'imageloadstart',
  IMAGELOADEND: 'imageloadend',
  IMAGELOADERROR: 'imageloaderror'
};
/**
 * Base image source mixin.
 */

var imageSource = {
  mixins: [source],
  props: {
    // ol/source/Image

    /**
     * @type {string}
     */
    projection: {
      type: String,
      default: EPSG_3857,
      validator: function validator(value) {
        return get(value) != null;
      }
    },

    /**
     * @type {number[]|undefined}
     */
    resolutions: Array
  },
  watch: _objectSpread$7({}, /*#__PURE__*/makeWatchers(['resolutions'], function (prop) {
    return /*#__PURE__*/sequential( /*#__PURE__*/function () {
      var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(val, prev) {
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                if (!isEqual(val, prev)) {
                  _context.next = 2;
                  break;
                }

                return _context.abrupt("return");

              case 2:
                if (process.env.VUELAYERS_DEBUG) {
                  this.$logger.log("".concat(prop, " changed, scheduling recreate..."));
                }

                _context.next = 5;
                return this.scheduleRecreate();

              case 5:
              case "end":
                return _context.stop();
            }
          }
        }, _callee, this);
      }));

      return function (_x, _x2) {
        return _ref.apply(this, arguments);
      };
    }());
  })),
  methods: _objectSpread$7({
    /**
     * @return {void}
     * @protected
     */
    subscribeAll: function subscribeAll() {
      source.methods.subscribeAll.call(this);
      subscribeToSourceEvents$1.call(this);
    }
  }, /*#__PURE__*/pick(source.methods, ['beforeInit', 'init', 'deinit', 'beforeMount', 'mount', 'unmount', 'refresh', 'scheduleRefresh', 'remount', 'scheduleRemount', 'recreate', 'scheduleRecreate', 'getServices', 'resolveOlObject', 'resolveSource']))
};

function subscribeToSourceEvents$1() {
  return _subscribeToSourceEvents$1.apply(this, arguments);
}

function _subscribeToSourceEvents$1() {
  _subscribeToSourceEvents$1 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
    var _this = this;

    var events;
    return _regeneratorRuntime.wrap(function _callee2$(_context2) {
      while (1) {
        switch (_context2.prev = _context2.next) {
          case 0:
            events = fromOlEvent(this.$source, [ImageSourceEventType.IMAGELOADSTART, ImageSourceEventType.IMAGELOADEND, ImageSourceEventType.IMAGELOADERROR]);
            this.subscribeTo(events, function (evt) {
              return _this.$emit(evt.type, evt);
            });

          case 2:
          case "end":
            return _context2.stop();
        }
      }
    }, _callee2, this);
  }));
  return _subscribeToSourceEvents$1.apply(this, arguments);
}

function ownKeys$8(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$8(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$8(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$8(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
/**
 * Basic style mixin.
 */

var style = {
  mixins: [stubVNode, olCmp, waitForMap],
  stubVNode: {
    empty: function empty() {
      return this.vmId;
    }
  },
  data: function data() {
    return {
      viewProjection: EPSG_3857,
      dataProjection: EPSG_3857
    };
  },
  created: function created() {
    defineServices$a.call(this);
  },
  methods: _objectSpread$8(_objectSpread$8(_objectSpread$8({
    /**
     * @return {OlStyle|Promise<OlStyle>}
     * @protected
     */
    createOlObject: function createOlObject() {
      var _this = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                _context.t0 = initializeStyle;
                _context.next = 3;
                return _this.createStyle();

              case 3:
                _context.t1 = _context.sent;
                _context.t2 = _this.currentId;
                return _context.abrupt("return", (0, _context.t0)(_context.t1, _context.t2));

              case 6:
              case "end":
                return _context.stop();
            }
          }
        }, _callee);
      }))();
    },

    /**
     * @return {OlStyle|Promise<OlStyle>}
     * @protected
     * @abstract
     */
    createStyle: function createStyle() {
      throw new Error("".concat(this.vmName, " not implemented method: createStyle()"));
    },

    /**
     * @return {Promise<void>}
     */
    remount: function remount() {
      var _this2 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
        return _regeneratorRuntime.wrap(function _callee2$(_context2) {
          while (1) {
            switch (_context2.prev = _context2.next) {
              case 0:
                _context2.next = 2;
                return _this2.refresh();

              case 2:
                _context2.next = 4;
                return olCmp.methods.remount.call(_this2);

              case 4:
                if (!_this2.$mapVm) {
                  _context2.next = 7;
                  break;
                }

                _context2.next = 7;
                return _this2.$mapVm.render();

              case 7:
              case "end":
                return _context2.stop();
            }
          }
        }, _callee2);
      }))();
    },

    /**
     * @return {Object}
     * @protected
     */
    getServices: function getServices() {
      var vm = this;
      return mergeDescriptors(olCmp.methods.getServices.call(this), {
        get styleVm() {
          return vm;
        }

      });
    },

    /**
     * @return {Promise<OlStyle>}
     */
    resolveStyle: olCmp.methods.resolveOlObject
  }, /*#__PURE__*/pick(olCmp.methods, ['init', 'deinit', 'beforeMount', 'mount', 'unmount', 'refresh', 'scheduleRefresh', 'scheduleRemount', 'recreate', 'scheduleRecreate', 'subscribeAll', 'resolveOlObject'])), /*#__PURE__*/pick(waitForMap.methods, ['beforeInit'])), {}, {
    /**
     * @returns {string|number}
     */
    getIdInternal: function getIdInternal() {
      return getStyleId(this.$style);
    },

    /**
     * @param {string|number} id
     * @returns {void}
     */
    setIdInternal: function setIdInternal(id) {
      assert(id != null && id !== '', 'Invalid style id');
      if (id === this.getIdInternal()) return;
      setStyleId(this.$style, id);
    }
  })
};

function defineServices$a() {
  var _this3 = this;

  Object.defineProperties(this, {
    /**
     * @type {OlStyle|undefined}
     */
    $style: {
      enumerable: true,
      get: function get() {
        return _this3.$olObject;
      }
    },

    /**
     * @type {Object|undefined}
     */
    $mapVm: {
      enumerable: true,
      get: function get() {
        var _this3$$services;

        return (_this3$$services = _this3.$services) === null || _this3$$services === void 0 ? void 0 : _this3$$services.mapVm;
      }
    },

    /**
     * @type {Object|undefined}
     */
    $viewVm: {
      enumerable: true,
      get: function get() {
        var _this3$$services2;

        return (_this3$$services2 = _this3.$services) === null || _this3$$services2 === void 0 ? void 0 : _this3$$services2.viewVm;
      }
    },

    /**
     * @type {Object|undefined}
     */
    $styleContainer: {
      enumerable: true,
      get: function get() {
        var _this3$$services3;

        return (_this3$$services3 = _this3.$services) === null || _this3$$services3 === void 0 ? void 0 : _this3$$services3.styleContainer;
      }
    }
  });
}

function ownKeys$9(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$9(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$9(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$9(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
var imageStyle = {
  mixins: [style],
  stubVNode: {
    empty: false,
    attrs: function attrs() {
      return {
        id: this.vmId,
        class: this.vmClass
      };
    }
  },
  props: {
    // ol/style/Image

    /**
     * @type {number}
     */
    opacity: {
      type: Number,
      default: 1
    },

    /**
     * @type {boolean}
     */
    rotateWithView: Boolean,

    /**
     * @type {number}
     */
    rotation: {
      type: Number,
      default: 0
    },

    /**
     * @type {number}
     */
    scale: {
      type: Number,
      default: 1
    },
    displacement: {
      type: Array,
      default: function _default() {
        return [0, 0];
      }
    }
  },
  watch: {
    opacity: /*#__PURE__*/sequential( /*#__PURE__*/function () {
      var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(value) {
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                _context.next = 2;
                return this.setOpacity(value);

              case 2:
              case "end":
                return _context.stop();
            }
          }
        }, _callee, this);
      }));

      return function (_x) {
        return _ref.apply(this, arguments);
      };
    }()),
    rotateWithView: /*#__PURE__*/sequential( /*#__PURE__*/function () {
      var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(value) {
        return _regeneratorRuntime.wrap(function _callee2$(_context2) {
          while (1) {
            switch (_context2.prev = _context2.next) {
              case 0:
                _context2.next = 2;
                return this.setRotateWithView(value);

              case 2:
              case "end":
                return _context2.stop();
            }
          }
        }, _callee2, this);
      }));

      return function (_x2) {
        return _ref2.apply(this, arguments);
      };
    }()),
    rotation: /*#__PURE__*/sequential( /*#__PURE__*/function () {
      var _ref3 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3(value) {
        return _regeneratorRuntime.wrap(function _callee3$(_context3) {
          while (1) {
            switch (_context3.prev = _context3.next) {
              case 0:
                _context3.next = 2;
                return this.setRotation(value);

              case 2:
              case "end":
                return _context3.stop();
            }
          }
        }, _callee3, this);
      }));

      return function (_x3) {
        return _ref3.apply(this, arguments);
      };
    }()),
    scale: /*#__PURE__*/sequential( /*#__PURE__*/function () {
      var _ref4 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee4(value) {
        return _regeneratorRuntime.wrap(function _callee4$(_context4) {
          while (1) {
            switch (_context4.prev = _context4.next) {
              case 0:
                _context4.next = 2;
                return this.setScale(value);

              case 2:
              case "end":
                return _context4.stop();
            }
          }
        }, _callee4, this);
      }));

      return function (_x4) {
        return _ref4.apply(this, arguments);
      };
    }()),
    displacement: /*#__PURE__*/sequential( /*#__PURE__*/function () {
      var _ref5 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee5(value) {
        return _regeneratorRuntime.wrap(function _callee5$(_context5) {
          while (1) {
            switch (_context5.prev = _context5.next) {
              case 0:
                _context5.t0 = isEqual;
                _context5.t1 = value;
                _context5.next = 4;
                return this.getDisplacement();

              case 4:
                _context5.t2 = _context5.sent;

                if (!(0, _context5.t0)(_context5.t1, _context5.t2)) {
                  _context5.next = 7;
                  break;
                }

                return _context5.abrupt("return");

              case 7:
                if (process.env.VUELAYERS_DEBUG) {
                  this.$logger.log('displacement changed, scheduling recreate...');
                }

                _context5.next = 10;
                return this.scheduleRecreate();

              case 10:
              case "end":
                return _context5.stop();
            }
          }
        }, _callee5, this);
      }));

      return function (_x5) {
        return _ref5.apply(this, arguments);
      };
    }())
  },
  created: function created() {
    defineServices$b.call(this);
  },
  methods: _objectSpread$9(_objectSpread$9({
    /**
     * @return {Promise<void>}
     * @protected
     */
    mount: function mount() {
      var _this = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee6() {
        return _regeneratorRuntime.wrap(function _callee6$(_context6) {
          while (1) {
            switch (_context6.prev = _context6.next) {
              case 0:
                if (!_this.$imageStyleContainer) {
                  _context6.next = 3;
                  break;
                }

                _context6.next = 3;
                return _this.$imageStyleContainer.setImage(_this);

              case 3:
                return _context6.abrupt("return", style.methods.mount.call(_this));

              case 4:
              case "end":
                return _context6.stop();
            }
          }
        }, _callee6);
      }))();
    },

    /**
     * @return {Promise<void>}
     * @protected
     */
    unmount: function unmount() {
      var _this2 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee7() {
        return _regeneratorRuntime.wrap(function _callee7$(_context7) {
          while (1) {
            switch (_context7.prev = _context7.next) {
              case 0:
                if (!(_this2.$imageStyleContainer && _this2.$imageStyleContainer.getImageVm() === _this2)) {
                  _context7.next = 3;
                  break;
                }

                _context7.next = 3;
                return _this2.$imageStyleContainer.setImage(null);

              case 3:
                return _context7.abrupt("return", style.methods.unmount.call(_this2));

              case 4:
              case "end":
                return _context7.stop();
            }
          }
        }, _callee7);
      }))();
    },

    /**
     * @return {Promise}
     */
    refresh: function refresh() {
      var _this3 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee8() {
        return _regeneratorRuntime.wrap(function _callee8$(_context8) {
          while (1) {
            switch (_context8.prev = _context8.next) {
              case 0:
                style.methods.refresh.call(_this3);

                if (_this3.$imageStyleContainer) {
                  _this3.$imageStyleContainer.refresh();
                }

              case 2:
              case "end":
                return _context8.stop();
            }
          }
        }, _callee8);
      }))();
    }
  }, /*#__PURE__*/pick(style.methods, ['beforeInit', 'init', 'deinit', 'beforeMount', 'scheduleRefresh', 'remount', 'scheduleRemount', 'recreate', 'scheduleRecreate', 'getServices', 'subscribeAll', 'resolveOlObject', 'resolveStyle'])), {}, {
    /**
     * @returns {Promise<number>}
     */
    getOpacity: function getOpacity() {
      var _this4 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee9() {
        return _regeneratorRuntime.wrap(function _callee9$(_context9) {
          while (1) {
            switch (_context9.prev = _context9.next) {
              case 0:
                _context9.next = 2;
                return _this4.resolveStyle();

              case 2:
                return _context9.abrupt("return", _context9.sent.getOpacity());

              case 3:
              case "end":
                return _context9.stop();
            }
          }
        }, _callee9);
      }))();
    },

    /**
     * @param {number} opacity
     * @returns {Promise<void>}
     */
    setOpacity: function setOpacity(opacity) {
      var _this5 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee10() {
        return _regeneratorRuntime.wrap(function _callee10$(_context10) {
          while (1) {
            switch (_context10.prev = _context10.next) {
              case 0:
                _context10.t0 = opacity;
                _context10.next = 3;
                return _this5.getOpacity();

              case 3:
                _context10.t1 = _context10.sent;

                if (!(_context10.t0 === _context10.t1)) {
                  _context10.next = 6;
                  break;
                }

                return _context10.abrupt("return");

              case 6:
                _context10.next = 8;
                return _this5.resolveStyle();

              case 8:
                _context10.sent.setOpacity(opacity);

                _context10.next = 11;
                return _this5.scheduleRefresh();

              case 11:
              case "end":
                return _context10.stop();
            }
          }
        }, _callee10);
      }))();
    },

    /**
     * @returns {Promise<boolean>}
     */
    getRotateWithView: function getRotateWithView() {
      var _this6 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee11() {
        return _regeneratorRuntime.wrap(function _callee11$(_context11) {
          while (1) {
            switch (_context11.prev = _context11.next) {
              case 0:
                _context11.next = 2;
                return _this6.resolveStyle();

              case 2:
                return _context11.abrupt("return", _context11.sent.getRotateWithView());

              case 3:
              case "end":
                return _context11.stop();
            }
          }
        }, _callee11);
      }))();
    },

    /**
     * @param {boolean} rotateWithView
     * @returns {Promise<void>}
     */
    setRotateWithView: function setRotateWithView(rotateWithView) {
      var _this7 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee12() {
        return _regeneratorRuntime.wrap(function _callee12$(_context12) {
          while (1) {
            switch (_context12.prev = _context12.next) {
              case 0:
                _context12.t0 = rotateWithView;
                _context12.next = 3;
                return _this7.getRotateWithView();

              case 3:
                _context12.t1 = _context12.sent;

                if (!(_context12.t0 === _context12.t1)) {
                  _context12.next = 6;
                  break;
                }

                return _context12.abrupt("return");

              case 6:
                _context12.next = 8;
                return _this7.resolveStyle();

              case 8:
                _context12.sent.setRotateWithView(rotateWithView);

                _context12.next = 11;
                return _this7.scheduleRefresh();

              case 11:
              case "end":
                return _context12.stop();
            }
          }
        }, _callee12);
      }))();
    },

    /**
     * @returns {Promise<number>}
     */
    getRotation: function getRotation() {
      var _this8 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee13() {
        return _regeneratorRuntime.wrap(function _callee13$(_context13) {
          while (1) {
            switch (_context13.prev = _context13.next) {
              case 0:
                _context13.next = 2;
                return _this8.resolveStyle();

              case 2:
                return _context13.abrupt("return", _context13.sent.getRotation());

              case 3:
              case "end":
                return _context13.stop();
            }
          }
        }, _callee13);
      }))();
    },

    /**
     * @param {number} rotation
     * @returns {Promise<void>}
     */
    setRotation: function setRotation(rotation) {
      var _this9 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee14() {
        return _regeneratorRuntime.wrap(function _callee14$(_context14) {
          while (1) {
            switch (_context14.prev = _context14.next) {
              case 0:
                _context14.t0 = rotation;
                _context14.next = 3;
                return _this9.getRotation();

              case 3:
                _context14.t1 = _context14.sent;

                if (!(_context14.t0 === _context14.t1)) {
                  _context14.next = 6;
                  break;
                }

                return _context14.abrupt("return");

              case 6:
                _context14.next = 8;
                return _this9.resolveStyle();

              case 8:
                _context14.sent.setRotation(rotation);

                _context14.next = 11;
                return _this9.scheduleRefresh();

              case 11:
              case "end":
                return _context14.stop();
            }
          }
        }, _callee14);
      }))();
    },

    /**
     * @returns {Promise<number>}
     */
    getScale: function getScale() {
      var _this10 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee15() {
        return _regeneratorRuntime.wrap(function _callee15$(_context15) {
          while (1) {
            switch (_context15.prev = _context15.next) {
              case 0:
                _context15.next = 2;
                return _this10.resolveStyle();

              case 2:
                return _context15.abrupt("return", _context15.sent.getScale());

              case 3:
              case "end":
                return _context15.stop();
            }
          }
        }, _callee15);
      }))();
    },

    /**
     * @param {number} scale
     * @returns {Promise<void>}
     */
    setScale: function setScale(scale) {
      var _this11 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee16() {
        return _regeneratorRuntime.wrap(function _callee16$(_context16) {
          while (1) {
            switch (_context16.prev = _context16.next) {
              case 0:
                _context16.t0 = scale;
                _context16.next = 3;
                return _this11.getScale();

              case 3:
                _context16.t1 = _context16.sent;

                if (!(_context16.t0 === _context16.t1)) {
                  _context16.next = 6;
                  break;
                }

                return _context16.abrupt("return");

              case 6:
                _context16.next = 8;
                return _this11.resolveStyle();

              case 8:
                _context16.sent.setScale(scale);

                _context16.next = 11;
                return _this11.scheduleRefresh();

              case 11:
              case "end":
                return _context16.stop();
            }
          }
        }, _callee16);
      }))();
    },
    getDisplacement: function getDisplacement() {
      var _this12 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee17() {
        return _regeneratorRuntime.wrap(function _callee17$(_context17) {
          while (1) {
            switch (_context17.prev = _context17.next) {
              case 0:
                _context17.next = 2;
                return _this12.resolveStyle();

              case 2:
                return _context17.abrupt("return", _context17.sent.getDisplacement());

              case 3:
              case "end":
                return _context17.stop();
            }
          }
        }, _callee17);
      }))();
    }
  })
};

function defineServices$b() {
  var _this13 = this;

  Object.defineProperties(this, {
    /**
     * @type {Object|undefined}
     */
    $imageStyleContainer: {
      enumerable: true,
      get: function get() {
        var _this13$$services;

        return (_this13$$services = _this13.$services) === null || _this13$$services === void 0 ? void 0 : _this13$$services.imageStyleContainer;
      }
    }
  });
}

/**
 * @typedef {module:ol/style/Image~ImageStyle|Object|undefined} ImageStyleLike
 */

/**
 * @typedef {Object} ImageStyleTarget
 * @property {function(): module:ol/style/Image~ImageStyle|undefined} getImage
 * @property {function(module:ol/style/Image~ImageStyle|undefined): void} setImage
 */

/**
 * Image style container.
 */

var imageStyleContainer = {
  computed: {
    currentImage: function currentImage() {
      if (!(this.rev && this.$image)) return;
      return dumpImageStyle(this.$image);
    }
  },
  watch: {
    currentImage: {
      deep: true,
      handler: /*#__PURE__*/debounce(function (value, prev) {
        if (isEqual(value, prev)) return;
        this.$emit('update:image', value && clonePlainObject(value));
      }, FRAME_TIME)
    }
  },
  created: function created() {
    this._image = undefined;
    this._imageVm = undefined;
    defineServices$c.call(this);
  },
  methods: {
    /**
     * @returns {{readonly imageStyleContainer: Object}}
     */
    getServices: function getServices() {
      var vm = this;
      return {
        get imageStyleContainer() {
          return vm;
        }

      };
    },

    /**
     * @return {ImageStyleTarget}
     */
    getImageStyleTarget: function getImageStyleTarget() {
      throw new Error("".concat(this.vmName, " not implemented method: getImageStyleTarget()"));
    },

    /**
     * @returns {module:ol/style/Image~ImageStyle|undefined}
     */
    getImage: function getImage() {
      return this._image;
    },

    /**
     * @return {Object}
     */
    getImageVm: function getImageVm() {
      return this._imageVm;
    },

    /**
     * @param {module:ol/style/Image~ImageStyle|undefined} image
     * @returns {Promise<void>}
     */
    setImage: function setImage(image) {
      var _this = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
        var _image, _image2;

        var imageTarget;
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                if (!isFunction((_image = image) === null || _image === void 0 ? void 0 : _image.resolveOlObject)) {
                  _context.next = 4;
                  break;
                }

                _context.next = 3;
                return image.resolveOlObject();

              case 3:
                image = _context.sent;

              case 4:
                image || (image = undefined);

                if (!(image === _this._image)) {
                  _context.next = 7;
                  break;
                }

                return _context.abrupt("return");

              case 7:
                _context.next = 9;
                return _this.getImageStyleTarget();

              case 9:
                imageTarget = _context.sent;

                if (imageTarget) {
                  _context.next = 12;
                  break;
                }

                return _context.abrupt("return");

              case 12:
                _this._image = image;
                _this._imageVm = ((_image2 = image) === null || _image2 === void 0 ? void 0 : _image2.vm) && image.vm[0];
                _context.next = 16;
                return imageTarget.setImage(image);

              case 16:
              case "end":
                return _context.stop();
            }
          }
        }, _callee);
      }))();
    }
  }
};

function defineServices$c() {
  Object.defineProperties(this, {
    $image: {
      enumerable: true,
      get: this.getImage
    },
    $imageVm: {
      enumerable: true,
      get: this.getImageVm
    }
  });
}

function ownKeys$a(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$a(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$a(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$a(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
/**
 * Base interaction mixin.
 */

var interaction = {
  mixins: [stubVNode, projTransforms, olCmp, waitForMap],
  stubVNode: {
    empty: function empty() {
      return this.vmId;
    }
  },
  props: {
    /**
     * @type {boolean}
     */
    active: {
      type: Boolean,
      default: true
    },

    /**
     * Priority of interactions in the event handling stream.
     * The higher the value, the sooner it will handle map event.
     * @type {number}
     */
    priority: {
      type: Number,
      default: 0
    }
  },
  data: function data() {
    return {
      viewProjection: EPSG_3857,
      dataProjection: EPSG_3857
    };
  },
  computed: {
    currentActive: function currentActive() {
      if (this.rev && this.$interaction) {
        return this.getActiveInternal();
      }

      return this.active;
    },
    currentPriority: function currentPriority() {
      if (this.rev && this.$interaction) {
        return this.getPriorityInternal();
      }

      return this.priority;
    }
  },
  watch: {
    active: /*#__PURE__*/sequential( /*#__PURE__*/function () {
      var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(value) {
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                _context.next = 2;
                return this.setActive(value);

              case 2:
              case "end":
                return _context.stop();
            }
          }
        }, _callee, this);
      }));

      return function (_x) {
        return _ref.apply(this, arguments);
      };
    }()),
    currentActive: /*#__PURE__*/debounce(function (value) {
      if (value === this.active) return;
      this.$emit('update:active', value);
    }, FRAME_TIME),
    priority: /*#__PURE__*/sequential( /*#__PURE__*/function () {
      var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(value) {
        return _regeneratorRuntime.wrap(function _callee2$(_context2) {
          while (1) {
            switch (_context2.prev = _context2.next) {
              case 0:
                _context2.next = 2;
                return this.setPriority(value);

              case 2:
              case "end":
                return _context2.stop();
            }
          }
        }, _callee2, this);
      }));

      return function (_x2) {
        return _ref2.apply(this, arguments);
      };
    }()),
    currentPriority: /*#__PURE__*/debounce(function (value) {
      if (value === this.priority) return;
      this.$emit('update:priority', value);
    }, FRAME_TIME)
  },
  created: function created() {
    defineServices$d.call(this);
  },
  methods: _objectSpread$a(_objectSpread$a(_objectSpread$a({
    /**
     * @return {Promise<module:ol/interaction/Interaction~Interaction>}
     * @protected
     */
    createOlObject: function createOlObject() {
      var _this = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3() {
        var interaction;
        return _regeneratorRuntime.wrap(function _callee3$(_context3) {
          while (1) {
            switch (_context3.prev = _context3.next) {
              case 0:
                _context3.t0 = initializeInteraction;
                _context3.next = 3;
                return _this.createInteraction();

              case 3:
                _context3.t1 = _context3.sent;
                _context3.t2 = _this.currentId;
                _context3.t3 = _this.currentPriority;
                interaction = (0, _context3.t0)(_context3.t1, _context3.t2, _context3.t3);
                interaction.setActive(_this.active);
                return _context3.abrupt("return", interaction);

              case 9:
              case "end":
                return _context3.stop();
            }
          }
        }, _callee3);
      }))();
    },

    /**
     * @return {module:ol/interaction/Interaction~Interaction|Promise<module:ol/interaction/Interaction~Interaction>}
     * @protected
     * @abstract
     */
    createInteraction: function createInteraction() {
      throw new Error("".concat(this.vmName, " not implemented method: createInteraction()"));
    },

    /**
     * @return {void}
     * @protected
     */
    mount: function mount() {
      var _this2 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee4() {
        return _regeneratorRuntime.wrap(function _callee4$(_context4) {
          while (1) {
            switch (_context4.prev = _context4.next) {
              case 0:
                if (!_this2.$interactionsContainer) {
                  _context4.next = 3;
                  break;
                }

                _context4.next = 3;
                return _this2.$interactionsContainer.addInteraction(_this2);

              case 3:
                return _context4.abrupt("return", olCmp.methods.mount.call(_this2));

              case 4:
              case "end":
                return _context4.stop();
            }
          }
        }, _callee4);
      }))();
    },

    /**
     * @return {void}
     * @protected
     */
    unmount: function unmount() {
      var _this3 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee5() {
        return _regeneratorRuntime.wrap(function _callee5$(_context5) {
          while (1) {
            switch (_context5.prev = _context5.next) {
              case 0:
                if (!_this3.$interactionsContainer) {
                  _context5.next = 3;
                  break;
                }

                _context5.next = 3;
                return _this3.$interactionsContainer.removeInteraction(_this3);

              case 3:
                return _context5.abrupt("return", olCmp.methods.unmount.call(_this3));

              case 4:
              case "end":
                return _context5.stop();
            }
          }
        }, _callee5);
      }))();
    },

    /**
     * @returns {Object}
     * @protected
     */
    getServices: function getServices() {
      var vm = this;
      return mergeDescriptors(olCmp.methods.getServices.call(this), {
        get interactionVm() {
          return vm;
        }

      });
    },

    /**
     * @returns {void}
     */
    subscribeAll: function subscribeAll() {
      olCmp.methods.subscribeAll.call(this);
      subscribeToInteractionEvents.call(this);
    },

    /**
     * @return {Promise<module:ol/interaction/Interaction~Interaction>}
     */
    resolveInteraction: olCmp.methods.resolveOlObject
  }, /*#__PURE__*/pick(olCmp.methods, ['init', 'deinit', 'beforeMount', 'refresh', 'scheduleRefresh', 'recreate', 'scheduleRecreate', 'remount', 'scheduleRemount', 'resolveOlObject'])), /*#__PURE__*/pick(waitForMap.methods, ['beforeInit'])), {}, {
    /**
     * @returns {string|number}
     */
    getIdInternal: function getIdInternal() {
      return getInteractionId(this.$interaction);
    },

    /**
     * @param {string|number} id
     * @returns {void}
     */
    setIdInternal: function setIdInternal(id) {
      assert(id != null && id !== '', 'Invalid interaction id');
      if (id === this.getIdInternal()) return;
      setInteractionId(this.$interaction, id);
    },

    /**
     * @returns {Promise<boolean>}
     */
    getActive: function getActive() {
      var _this4 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee6() {
        return _regeneratorRuntime.wrap(function _callee6$(_context6) {
          while (1) {
            switch (_context6.prev = _context6.next) {
              case 0:
                _context6.next = 2;
                return _this4.resolveInteraction();

              case 2:
                return _context6.abrupt("return", _this4.getActiveInternal());

              case 3:
              case "end":
                return _context6.stop();
            }
          }
        }, _callee6);
      }))();
    },

    /**
     * @return {boolean}
     * @protected
     */
    getActiveInternal: function getActiveInternal() {
      return this.$interaction.getActive();
    },

    /**
     * @param {boolean} active
     * @returns {Promise<void>}
     */
    setActive: function setActive(active) {
      var _this5 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee7() {
        return _regeneratorRuntime.wrap(function _callee7$(_context7) {
          while (1) {
            switch (_context7.prev = _context7.next) {
              case 0:
                _context7.t0 = active;
                _context7.next = 3;
                return _this5.getActive();

              case 3:
                _context7.t1 = _context7.sent;

                if (!(_context7.t0 === _context7.t1)) {
                  _context7.next = 6;
                  break;
                }

                return _context7.abrupt("return");

              case 6:
                _context7.next = 8;
                return _this5.resolveInteraction();

              case 8:
                _context7.sent.setActive(active);

              case 9:
              case "end":
                return _context7.stop();
            }
          }
        }, _callee7);
      }))();
    },

    /**
     * @returns {Promise<number>}
     */
    getPriority: function getPriority() {
      var _this6 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee8() {
        return _regeneratorRuntime.wrap(function _callee8$(_context8) {
          while (1) {
            switch (_context8.prev = _context8.next) {
              case 0:
                _context8.next = 2;
                return _this6.resolveInteraction();

              case 2:
                return _context8.abrupt("return", _this6.getPriorityInternal());

              case 3:
              case "end":
                return _context8.stop();
            }
          }
        }, _callee8);
      }))();
    },
    getPriorityInternal: function getPriorityInternal() {
      return getInteractionPriority(this.$interaction);
    },

    /**
     * @param {number} priority
     * @returns {Promise<void>}
     */
    setPriority: function setPriority(priority) {
      var _this7 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee9() {
        var _this7$$interactionsC;

        return _regeneratorRuntime.wrap(function _callee9$(_context9) {
          while (1) {
            switch (_context9.prev = _context9.next) {
              case 0:
                _context9.t0 = priority;
                _context9.next = 3;
                return _this7.getPriority();

              case 3:
                _context9.t1 = _context9.sent;

                if (!(_context9.t0 === _context9.t1)) {
                  _context9.next = 6;
                  break;
                }

                return _context9.abrupt("return");

              case 6:
                _context9.t2 = setInteractionPriority;
                _context9.next = 9;
                return _this7.resolveInteraction();

              case 9:
                _context9.t3 = _context9.sent;
                _context9.t4 = priority;
                (0, _context9.t2)(_context9.t3, _context9.t4);
                // eslint-disable-next-line no-unused-expressions
                (_this7$$interactionsC = _this7.$interactionsContainer) === null || _this7$$interactionsC === void 0 ? void 0 : _this7$$interactionsC.sortInteractions();

              case 13:
              case "end":
                return _context9.stop();
            }
          }
        }, _callee9);
      }))();
    }
  })
};

function defineServices$d() {
  var _this8 = this;

  Object.defineProperties(this, {
    /**
     * @type {module:ol/interaction/Interaction~Interaction|undefined}
     */
    $interaction: {
      enumerable: true,
      get: function get() {
        return _this8.$olObject;
      }
    },

    /**
     * @type {Object|undefined}
     */
    $mapVm: {
      enumerable: true,
      get: function get() {
        var _this8$$services;

        return (_this8$$services = _this8.$services) === null || _this8$$services === void 0 ? void 0 : _this8$$services.mapVm;
      }
    },

    /**
     * @type {Object|undefined}
     */
    $viewVm: {
      enumerable: true,
      get: function get() {
        var _this8$$services2;

        return (_this8$$services2 = _this8.$services) === null || _this8$$services2 === void 0 ? void 0 : _this8$$services2.viewVm;
      }
    },

    /**
     * @type {Object|undefined}
     */
    $interactionsContainer: {
      enumerable: true,
      get: function get() {
        var _this8$$services3;

        return (_this8$$services3 = _this8.$services) === null || _this8$$services3 === void 0 ? void 0 : _this8$$services3.interactionsContainer;
      }
    }
  });
}

function subscribeToInteractionEvents() {
  return _subscribeToInteractionEvents.apply(this, arguments);
}

function _subscribeToInteractionEvents() {
  _subscribeToInteractionEvents = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee10() {
    var _this9 = this;

    var prefixKey, propChanges;
    return _regeneratorRuntime.wrap(function _callee10$(_context10) {
      while (1) {
        switch (_context10.prev = _context10.next) {
          case 0:
            prefixKey = addPrefix('current');
            propChanges = fromOlChangeEvent(this.$interaction, ['id', 'active', 'priority'], true, function (evt) {
              return _objectSpread$a(_objectSpread$a({}, evt), {}, {
                compareWith: _this9[prefixKey(evt.prop)]
              });
            }).pipe(skipWhile(function (_ref3) {
              var compareWith = _ref3.compareWith,
                  value = _ref3.value;
              return isEqual(value, compareWith);
            }));
            this.subscribeTo(propChanges, function () {
              ++_this9.rev;
            });

          case 3:
          case "end":
            return _context10.stop();
        }
      }
    }, _callee10, this);
  }));
  return _subscribeToInteractionEvents.apply(this, arguments);
}

/**
 * @typedef {module:ol/interaction/Interaction~Interaction|Object} InteractionLike
 */

/**
 * Interactions container
 */

var interactionsContainer = {
  mixins: [identMap, rxSubs],
  computed: {
    /**
     * @returns {string[]}
     */
    currentInteractions: function currentInteractions() {
      if (!this.rev) return [];
      return map$1(this.getInteractions(), getInteractionId);
    },

    /**
     * @returns {string|undefined}
     */
    interactionsCollectionIdent: function interactionsCollectionIdent() {
      if (!this.olObjIdent) return;
      return this.makeIdent(this.olObjIdent, 'interactions_collection');
    }
  },
  watch: {
    currentInteractions: /*#__PURE__*/debounce(function (value, prev) {
      if (isEqual(value, prev)) return;
      this.$emit('update:interactions', value.slice());
    }, FRAME_TIME),
    interactionsCollectionIdent: /*#__PURE__*/sequential(function (value, prevValue) {
      if (value && prevValue) {
        this.moveInstance(value, prevValue);
      } else if (value && !prevValue && this.$interactionsCollection) {
        this.setInstance(value, this.$interactionsCollection);
      } else if (!value && prevValue) {
        this.unsetInstance(prevValue);
      }
    })
  },
  created: function created() {
    /**
     * @type {module:ol/Collection~Collection<module:ol/interaction/Interaction~Interaction>}
     * @private
     */
    this._interactionsCollection = this.instanceFactoryCall(this.interactionsCollectionIdent, function () {
      return new Collection();
    });
    defineServices$e.call(this);
  },
  methods: {
    /**
     * @returns {{readonly interactionsContainer: Object}}
     * @protected
     */
    getServices: function getServices() {
      var vm = this;
      return {
        get interactionsContainer() {
          return vm;
        }

      };
    },

    /**
     * @return {void}
     * @protected
     */
    subscribeAll: function subscribeAll() {
      subscribeToCollectionEvents$2.call(this);
    },

    /**
     * @param {InteractionLike[]|module:ol/Collection~Collection<InteractionLike>} defaultInteractions
     * @returns {Promise<void>}
     */
    initDefaultInteractions: function initDefaultInteractions(defaultInteractions) {
      var _this = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
        var interactions;
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                _this.clearInteractions();

                if (isArray(defaultInteractions) || defaultInteractions instanceof Collection) {
                  interactions = defaultInteractions;
                } else if (defaultInteractions !== false) {
                  interactions = defaults$1(isPlainObject(defaultInteractions) ? _this.defaultInteractions : undefined);
                }

                if (!interactions) {
                  _context.next = 5;
                  break;
                }

                _context.next = 5;
                return _this.addInteractions(interactions);

              case 5:
              case "end":
                return _context.stop();
            }
          }
        }, _callee);
      }))();
    },

    /**
     * @param {InteractionLike} interaction
     * @return {Promise<Interaction>}
     */
    initializeInteraction: function initializeInteraction$1(interaction) {
      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
        return _regeneratorRuntime.wrap(function _callee2$(_context2) {
          while (1) {
            switch (_context2.prev = _context2.next) {
              case 0:
                if (!isFunction(interaction.resolveOlObject)) {
                  _context2.next = 4;
                  break;
                }

                _context2.next = 3;
                return interaction.resolveOlObject();

              case 3:
                interaction = _context2.sent;

              case 4:
                return _context2.abrupt("return", initializeInteraction(interaction));

              case 5:
              case "end":
                return _context2.stop();
            }
          }
        }, _callee2);
      }))();
    },

    /**
     * @param {InteractionLike[]|module:ol/Collection~Collection<InteractionLike>} interactions
     * @returns {Promise<void>}
     */
    addInteractions: function addInteractions(interactions) {
      var _this2 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3() {
        return _regeneratorRuntime.wrap(function _callee3$(_context3) {
          while (1) {
            switch (_context3.prev = _context3.next) {
              case 0:
                _context3.next = 2;
                return Promise.all(map$1(interactions, _this2.addInteraction.bind(_this2)));

              case 2:
              case "end":
                return _context3.stop();
            }
          }
        }, _callee3);
      }))();
    },

    /**
     * @param {InteractionLike} interaction
     * @return {void}
     */
    addInteraction: function addInteraction(interaction) {
      var _this3 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee4() {
        return _regeneratorRuntime.wrap(function _callee4$(_context4) {
          while (1) {
            switch (_context4.prev = _context4.next) {
              case 0:
                _context4.next = 2;
                return _this3.initializeInteraction(interaction);

              case 2:
                interaction = _context4.sent;
                instanceOf(interaction, Interaction);

                if (!_this3.getInteractionById(getInteractionId(interaction))) {
                  _context4.next = 6;
                  break;
                }

                return _context4.abrupt("return");

              case 6:
                _this3.$interactionsCollection.push(interaction);

                _this3.sortInteractions();

              case 8:
              case "end":
                return _context4.stop();
            }
          }
        }, _callee4);
      }))();
    },

    /**
     * @param {InteractionLike[]|module:ol/Collection~Collection<InteractionLike>} interactions
     * @returns {Promise<void>}
     */
    removeInteractions: function removeInteractions(interactions) {
      var _this4 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee5() {
        return _regeneratorRuntime.wrap(function _callee5$(_context5) {
          while (1) {
            switch (_context5.prev = _context5.next) {
              case 0:
                _context5.next = 2;
                return Promise.all(map$1(interactions, _this4.removeInteraction.bind(_this4)));

              case 2:
              case "end":
                return _context5.stop();
            }
          }
        }, _callee5);
      }))();
    },

    /**
     * @param {InteractionLike} interaction
     * @return {void}
     */
    removeInteraction: function removeInteraction(interaction) {
      var _this5 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee6() {
        return _regeneratorRuntime.wrap(function _callee6$(_context6) {
          while (1) {
            switch (_context6.prev = _context6.next) {
              case 0:
                if (!isFunction(interaction.resolveOlObject)) {
                  _context6.next = 4;
                  break;
                }

                _context6.next = 3;
                return interaction.resolveOlObject();

              case 3:
                interaction = _context6.sent;

              case 4:
                interaction = _this5.getInteractionById(getInteractionId(interaction));

                if (interaction) {
                  _context6.next = 7;
                  break;
                }

                return _context6.abrupt("return");

              case 7:
                _this5.$interactionsCollection.remove(interaction);

                _this5.sortInteractions();

              case 9:
              case "end":
                return _context6.stop();
            }
          }
        }, _callee6);
      }))();
    },

    /**
     * @return {void}
     */
    clearInteractions: function clearInteractions() {
      this.$interactionsCollection.clear();
    },

    /**
     * @return {module:ol/interaction/Interaction~Interaction[]}
     */
    getInteractions: function getInteractions() {
      return this.$interactionsCollection.getArray();
    },

    /**
     * @return {module:ol/Collection~Collection<module:ol/interaction/Interaction~Interaction>}
     */
    getInteractionsCollection: function getInteractionsCollection() {
      return this._interactionsCollection;
    },

    /**
     * @param {string|number} interactionId
     * @return {module:ol/interaction/Interaction~Interaction|undefined}
     */
    getInteractionById: function getInteractionById(interactionId) {
      return find(this.getInteractions(), function (interaction) {
        return getInteractionId(interaction) === interactionId;
      });
    },

    /**
     * @return {void}
     */
    sortInteractions: function sortInteractions(sorter) {
      sorter || (sorter = this.getDefaultInteractionsSorter());
      this.$interactionsCollection.getArray().sort(sorter);
    },

    /**
     * @return {function(): number}
     * @protected
     */
    getDefaultInteractionsSorter: function getDefaultInteractionsSorter() {
      // sort interactions by priority in asc order
      // the higher the priority, the earlier the interaction handles the event
      return function (a, b) {
        var ap = getInteractionPriority(a) || 0;
        var bp = getInteractionPriority(b) || 0;
        return ap === bp ? 0 : ap - bp;
      };
    }
  }
};

function defineServices$e() {
  Object.defineProperties(this, {
    $interactionsCollection: {
      enumerable: true,
      get: this.getInteractionsCollection
    }
  });
}

function subscribeToCollectionEvents$2() {
  var _this6 = this;

  var adds = fromOlEvent(this.$interactionsCollection, CollectionEventType.ADD).pipe(mergeMap(function (_ref) {
    var type = _ref.type,
        element = _ref.element;
    return from(_this6.initializeInteraction(element)).pipe(map(function (element) {
      return {
        type: type,
        element: element
      };
    }));
  }));
  var removes = fromOlEvent(this.$interactionsCollection, CollectionEventType.REMOVE);
  var events = merge(adds, removes).pipe(bufferDebounceTime(FRAME_TIME));
  this.subscribeTo(events, function (events) {
    ++_this6.rev;

    _this6.$nextTick(function () {
      forEach(events, function (_ref2) {
        var type = _ref2.type,
            element = _ref2.element;

        _this6.$emit(type + 'interaction', element); // todo remove in v0.13.x


        _this6.$emit(type + ':interaction', element);
      });
    });
  });
}

/**
 * @typedef {module:ol/layer/Base~BaseLayer|Object} LayerLike
 */

/**
 * Layers container mixin.
 */

var layersContainer = {
  mixins: [identMap, rxSubs],
  computed: {
    /**
     * @returns {string[]}
     */
    currentLayers: function currentLayers() {
      if (!this.rev) return [];
      return map$1(this.getLayers(), getLayerId);
    },

    /**
     * @returns {string|undefined}
     */
    layersCollectionIdent: function layersCollectionIdent() {
      if (!this.olObjIdent) return;
      return this.makeIdent(this.olObjIdent, 'layers_collection');
    }
  },
  watch: {
    currentLayers: /*#__PURE__*/debounce(function (value, prev) {
      if (isEqual(value, prev)) return;
      this.$emit('update:layers', value.slice());
    }, FRAME_TIME),
    layersCollectionIdent: /*#__PURE__*/sequential(function (value, prevValue) {
      if (value && prevValue) {
        this.moveInstance(value, prevValue);
      } else if (value && !prevValue && this.$layersCollection) {
        this.setInstance(value, this.$layersCollection);
      } else if (!value && prevValue) {
        this.unsetInstance(prevValue);
      }
    })
  },
  created: function created() {
    /**
     * @type {module:ol/Collection~Collection<module:ol/layer/Base~BaseLayer>}
     * @private
     */
    this._layersCollection = this.instanceFactoryCall(this.layersCollectionIdent, function () {
      return new Collection();
    });
    defineServices$f.call(this);
  },
  methods: {
    /**
     * @returns {{readonly layersContainer: Object}}
     * @protected
     */
    getServices: function getServices() {
      var vm = this;
      return {
        get layersContainer() {
          return vm;
        }

      };
    },

    /**
     * @return {void}
     * @protected
     */
    subscribeAll: function subscribeAll() {
      subscribeToCollectionEvents$3.call(this);
    },

    /**
     * @param {LayerLike} layer
     * @return {Promise<BaseLayer>}
     */
    initializeLayer: function initializeLayer$1(layer) {
      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                if (!isFunction(layer.resolveOlObject)) {
                  _context.next = 4;
                  break;
                }

                _context.next = 3;
                return layer.resolveOlObject();

              case 3:
                layer = _context.sent;

              case 4:
                return _context.abrupt("return", initializeLayer(layer));

              case 5:
              case "end":
                return _context.stop();
            }
          }
        }, _callee);
      }))();
    },

    /**
     * @param {LayerLike[]|module:ol/Collection~Collection<LayerLike>} layers
     * @returns {Promise<void>}
     */
    addLayers: function addLayers(layers) {
      var _this = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
        return _regeneratorRuntime.wrap(function _callee2$(_context2) {
          while (1) {
            switch (_context2.prev = _context2.next) {
              case 0:
                _context2.next = 2;
                return Promise.all(map$1(layers, _this.addLayer.bind(_this)));

              case 2:
              case "end":
                return _context2.stop();
            }
          }
        }, _callee2);
      }))();
    },

    /**
     * @param {LayerLike} layer
     * @return {Promise<void>}
     */
    addLayer: function addLayer(layer) {
      var _this2 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3() {
        return _regeneratorRuntime.wrap(function _callee3$(_context3) {
          while (1) {
            switch (_context3.prev = _context3.next) {
              case 0:
                _context3.next = 2;
                return _this2.initializeLayer(layer);

              case 2:
                layer = _context3.sent;
                instanceOf(layer, BaseLayer);

                if (!_this2.getLayerById(getLayerId(layer))) {
                  _context3.next = 6;
                  break;
                }

                return _context3.abrupt("return");

              case 6:
                _this2.$layersCollection.push(layer);

              case 7:
              case "end":
                return _context3.stop();
            }
          }
        }, _callee3);
      }))();
    },

    /**
     * @param {LayerLike[]|module:ol/Collection~Collection<LayerLike>} layers
     * @returns {Promise<void>}
     */
    removeLayers: function removeLayers(layers) {
      var _this3 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee4() {
        return _regeneratorRuntime.wrap(function _callee4$(_context4) {
          while (1) {
            switch (_context4.prev = _context4.next) {
              case 0:
                _context4.next = 2;
                return Promise.all(map$1(layers, _this3.removeLayer.bind(_this3)));

              case 2:
              case "end":
                return _context4.stop();
            }
          }
        }, _callee4);
      }))();
    },

    /**
     * @param {LayerLike} layer
     * @return {void}
     */
    removeLayer: function removeLayer(layer) {
      var _this4 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee5() {
        return _regeneratorRuntime.wrap(function _callee5$(_context5) {
          while (1) {
            switch (_context5.prev = _context5.next) {
              case 0:
                if (!isFunction(layer.resolveOlObject)) {
                  _context5.next = 4;
                  break;
                }

                _context5.next = 3;
                return layer.resolveOlObject();

              case 3:
                layer = _context5.sent;

              case 4:
                layer = _this4.getLayerById(getLayerId(layer));

                if (layer) {
                  _context5.next = 7;
                  break;
                }

                return _context5.abrupt("return");

              case 7:
                _this4.$layersCollection.remove(layer);

              case 8:
              case "end":
                return _context5.stop();
            }
          }
        }, _callee5);
      }))();
    },

    /**
     * @return {void}
     */
    clearLayers: function clearLayers() {
      this.$layersCollection.clear();
    },

    /**
     * @return {Array<module:ol/layer/Base~BaseLayer>}
     */
    getLayers: function getLayers() {
      return this.$layersCollection.getArray();
    },

    /**
     * @return {module:ol/Collection~Collection<module:ol/layer/Base~BaseLayer>}
     */
    getLayersCollection: function getLayersCollection() {
      return this._layersCollection;
    },

    /**
     * @param {string|number} layerId
     * @return {module:ol/layer/Base~BaseLayer|undefined}
     */
    getLayerById: function getLayerById(layerId) {
      return find(this.getLayers(), function (layer) {
        return getLayerId(layer) === layerId;
      });
    }
  }
};

function defineServices$f() {
  Object.defineProperties(this, {
    $layersCollection: {
      enumerable: true,
      get: this.getLayersCollection
    }
  });
}

function subscribeToCollectionEvents$3() {
  var _this5 = this;

  var adds = fromOlEvent(this.$layersCollection, CollectionEventType.ADD).pipe(mergeMap(function (_ref) {
    var type = _ref.type,
        element = _ref.element;
    return from(_this5.initializeLayer(element)).pipe(map(function (element) {
      return {
        type: type,
        element: element
      };
    }));
  }));
  var removes = fromOlEvent(this.$layersCollection, CollectionEventType.REMOVE);
  var events = merge(adds, removes).pipe(bufferDebounceTime(FRAME_TIME));
  this.subscribeTo(events, function (events) {
    ++_this5.rev;

    _this5.$nextTick(function () {
      forEach(events, function (_ref2) {
        var type = _ref2.type,
            element = _ref2.element;

        _this5.$emit(type + 'layer', element); // todo remove in v0.13.x


        _this5.$emit(type + ':layer', element);
      });
    });
  });
}

/**
 * @typedef {module:ol/Overlay~Overlay|Object} OverlayLike
 */

/**
 * Overlays container mixin.
 */

var overlaysContainer = {
  mixins: [identMap, rxSubs],
  computed: {
    /**
     * @returns {string[]}
     */
    currentOverlays: function currentOverlays() {
      if (!this.rev) return [];
      return map$1(this.getOverlays(), getOverlayId);
    },

    /**
     * @returns {string|undefined}
     */
    overlaysCollectionIdent: function overlaysCollectionIdent() {
      if (!this.olObjIdent) return;
      return this.makeIdent(this.olObjIdent, 'overlays_collection');
    }
  },
  watch: {
    currentOverlays: /*#__PURE__*/debounce(function (value, prev) {
      if (isEqual(value, prev)) return;
      this.$emit('update:overlays', value.slice());
    }, FRAME_TIME),
    overlaysCollectionIdent: /*#__PURE__*/sequential(function (value, prevValue) {
      if (value && prevValue) {
        this.moveInstance(value, prevValue);
      } else if (value && !prevValue && this.$overlaysCollection) {
        this.setInstance(value, this.$overlaysCollection);
      } else if (!value && prevValue) {
        this.unsetInstance(prevValue);
      }
    })
  },
  created: function created() {
    /**
     * @type {module:ol/Collection~Collection<module:ol/Overlay~Overlay>}
     * @private
     */
    this._overlaysCollection = this.instanceFactoryCall(this.overlaysCollectionIdent, function () {
      return new Collection();
    });
    defineServices$g.call(this);
  },
  methods: {
    /**
     * @returns {readonly overlaysContainer: Object}}
     * @protected
     */
    getServices: function getServices() {
      var vm = this;
      return {
        get overlaysContainer() {
          return vm;
        }

      };
    },

    /**
     * @return {void}
     * @protected
     */
    subscribeAll: function subscribeAll() {
      subscribeToCollectionEvents$4.call(this);
    },

    /**
     * @param {OverlayLike} overlay
     * @return {Promise<Overlay>}
     */
    initializeOverlay: function initializeOverlay$1(overlay) {
      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                if (!isFunction(overlay.resolveOlObject)) {
                  _context.next = 4;
                  break;
                }

                _context.next = 3;
                return overlay.resolveOlObject();

              case 3:
                overlay = _context.sent;

              case 4:
                return _context.abrupt("return", initializeOverlay(overlay));

              case 5:
              case "end":
                return _context.stop();
            }
          }
        }, _callee);
      }))();
    },

    /**
     * @param {OverlayLike[]|module:ol/Collection~Collection<OverlayLike>} overlays
     * @returns {Promise<void>}
     */
    addOverlays: function addOverlays(overlays) {
      var _this = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
        return _regeneratorRuntime.wrap(function _callee2$(_context2) {
          while (1) {
            switch (_context2.prev = _context2.next) {
              case 0:
                _context2.next = 2;
                return Promise.all(map$1(overlays, _this.addOverlay.bind(_this)));

              case 2:
              case "end":
                return _context2.stop();
            }
          }
        }, _callee2);
      }))();
    },

    /**
     * @param {OverlayLike} overlay
     * @return {void}
     */
    addOverlay: function addOverlay(overlay) {
      var _this2 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3() {
        return _regeneratorRuntime.wrap(function _callee3$(_context3) {
          while (1) {
            switch (_context3.prev = _context3.next) {
              case 0:
                _context3.next = 2;
                return _this2.initializeOverlay(overlay);

              case 2:
                overlay = _context3.sent;
                instanceOf(overlay, Overlay);

                if (!_this2.getOverlayById(getOverlayId(overlay))) {
                  _context3.next = 6;
                  break;
                }

                return _context3.abrupt("return");

              case 6:
                _this2.$overlaysCollection.push(overlay);

              case 7:
              case "end":
                return _context3.stop();
            }
          }
        }, _callee3);
      }))();
    },

    /**
     * @param {OverlayLike[]|module:ol/Collection~Collection<OverlayLike>} overlays
     * @returns {Promise<void>}
     */
    removeOverlays: function removeOverlays(overlays) {
      var _this3 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee4() {
        return _regeneratorRuntime.wrap(function _callee4$(_context4) {
          while (1) {
            switch (_context4.prev = _context4.next) {
              case 0:
                _context4.next = 2;
                return Promise.all(map$1(overlays, _this3.removeOverlay.bind(_this3)));

              case 2:
              case "end":
                return _context4.stop();
            }
          }
        }, _callee4);
      }))();
    },

    /**
     * @param {OverlayLike} overlay
     * @return {void}
     */
    removeOverlay: function removeOverlay(overlay) {
      var _this4 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee5() {
        return _regeneratorRuntime.wrap(function _callee5$(_context5) {
          while (1) {
            switch (_context5.prev = _context5.next) {
              case 0:
                if (!isFunction(overlay.resolveOlObject)) {
                  _context5.next = 4;
                  break;
                }

                _context5.next = 3;
                return overlay.resolveOlObject();

              case 3:
                overlay = _context5.sent;

              case 4:
                overlay = _this4.getOverlayById(getOverlayId(overlay));

                if (overlay) {
                  _context5.next = 7;
                  break;
                }

                return _context5.abrupt("return");

              case 7:
                _this4.$overlaysCollection.remove(overlay);

              case 8:
              case "end":
                return _context5.stop();
            }
          }
        }, _callee5);
      }))();
    },

    /**
     * @return {void}
     */
    clearOverlays: function clearOverlays() {
      this.$overlaysCollection.clear();
    },

    /**
     * @return {Array<module:ol/Overlay~Overlay>}
     */
    getOverlays: function getOverlays() {
      return this.$overlaysCollection.getArray();
    },

    /**
     * @return {module:ol/Collection~Collection<module:ol/Overlay~Overlay>}
     */
    getOverlaysCollection: function getOverlaysCollection() {
      return this._overlaysCollection;
    },

    /**
     * @param {string|number} overlayId
     * @return {module:ol/Overlay~Overlay|undefined}
     */
    getOverlayById: function getOverlayById(overlayId) {
      return find(this.getOverlays(), function (overlay) {
        return getOverlayId(overlay) === overlayId;
      });
    }
  }
};

function defineServices$g() {
  Object.defineProperties(this, {
    $overlaysCollection: {
      enumerable: true,
      get: this.getOverlaysCollection
    }
  });
}

function subscribeToCollectionEvents$4() {
  var _this5 = this;

  var adds = fromOlEvent(this.$overlaysCollection, CollectionEventType.ADD).pipe(mergeMap(function (_ref) {
    var type = _ref.type,
        element = _ref.element;
    return from(_this5.initializeOverlay(element)).pipe(map(function (element) {
      return {
        type: type,
        element: element
      };
    }));
  }));
  var removes = fromOlEvent(this.$overlaysCollection, CollectionEventType.REMOVE);
  var events = merge(adds, removes).pipe(bufferDebounceTime(FRAME_TIME));
  this.subscribeTo(events, function (events) {
    ++_this5.rev;

    _this5.$nextTick(function () {
      forEach(events, function (_ref2) {
        var type = _ref2.type,
            element = _ref2.element;

        _this5.$emit(type + 'overlay', element); // todo remove in v0.13.x


        _this5.$emit(type + ':overlay', element);
      });
    });
  });
}

/**
 * @typedef {module:ol/style/Stroke~Stroke|Object|undefined} StrokeStyleLike
 */

/**
 * @typedef {Object} StrokeStyleTarget
 * @property {function(): module:ol/style/Stroke~Stroke|undefined} getStroke
 * @property {function(module:ol/style/Stroke~Stroke|undefined): void} setStroke
 */

/**
 * Stroke style container.
 */

var strokeStyleContainer = {
  computed: {
    currentStroke: function currentStroke() {
      if (!(this.rev && this.$stroke)) return;
      return dumpStrokeStyle(this.$stroke);
    }
  },
  watch: {
    currentStroke: {
      deep: true,
      handler: /*#__PURE__*/debounce(function (value, prev) {
        if (isEqual(value, prev)) return;
        this.$emit('update:stroke', value && clonePlainObject(value));
      }, FRAME_TIME)
    }
  },
  created: function created() {
    this._stroke = undefined;
    this._strokeVm = undefined;
    defineServices$h.call(this);
  },
  methods: {
    /**
     * @returns {{readonly strokeStyleContainer: Object}}
     */
    getServices: function getServices() {
      var vm = this;
      return {
        get strokeStyleContainer() {
          return vm;
        }

      };
    },
    getStrokeStyleTarget: function getStrokeStyleTarget() {
      throw new Error("".concat(this.vmName, " not implemented method: getStrokeStyleTarget()"));
    },

    /**
     * @returns {module:ol/style/Stroke~Stroke|undefined}
     */
    getStroke: function getStroke() {
      return this._stroke;
    },

    /**
     * @return {Object}
     */
    getStrokeVm: function getStrokeVm() {
      return this._strokeVm;
    },

    /**
     * @param {module:ol/style/Stroke~Stroke|undefined} stroke
     * @returns {Promise<void>}
     */
    setStroke: function setStroke(stroke) {
      var _this = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
        var _stroke, _stroke2;

        var strokeTarget;
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                if (!isFunction((_stroke = stroke) === null || _stroke === void 0 ? void 0 : _stroke.resolveOlObject)) {
                  _context.next = 4;
                  break;
                }

                _context.next = 3;
                return stroke.resolveOlObject();

              case 3:
                stroke = _context.sent;

              case 4:
                stroke || (stroke = undefined);

                if (!(stroke === _this._stroke)) {
                  _context.next = 7;
                  break;
                }

                return _context.abrupt("return");

              case 7:
                _context.next = 9;
                return _this.getStrokeStyleTarget();

              case 9:
                strokeTarget = _context.sent;

                if (strokeTarget) {
                  _context.next = 12;
                  break;
                }

                return _context.abrupt("return");

              case 12:
                _this._stroke = stroke;
                _this._strokeVm = ((_stroke2 = stroke) === null || _stroke2 === void 0 ? void 0 : _stroke2.vm) && stroke.vm[0];
                _context.next = 16;
                return strokeTarget.setStroke(stroke);

              case 16:
              case "end":
                return _context.stop();
            }
          }
        }, _callee);
      }))();
    }
  }
};

function defineServices$h() {
  Object.defineProperties(this, {
    $stroke: {
      enumerable: true,
      get: this.getStroke
    },
    $strokeVm: {
      enumerable: true,
      get: this.getStrokeVm
    }
  });
}

function ownKeys$b(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$b(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$b(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$b(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
var regShapeStyle = {
  mixins: [fillStyleContainer, strokeStyleContainer, imageStyle],
  props: {
    // ol/style/RegularShape
    points: Number,
    radius: Number,
    radius1: Number,
    radius2: Number,
    angle: {
      type: Number,
      default: 0
    }
  },
  watch: _objectSpread$b({}, /*#__PURE__*/makeWatchers(['points', 'radius', 'radius1', 'radius2', 'angle'], function (prop) {
    return /*#__PURE__*/sequential( /*#__PURE__*/function () {
      var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(val, prev) {
        var handler;
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                handler = this["on".concat(upperFirst(prop), "Changed")];

                if (!isFunction(handler)) {
                  _context.next = 3;
                  break;
                }

                return _context.abrupt("return", handler(val, prev));

              case 3:
                if (!isEqual(val, prev)) {
                  _context.next = 5;
                  break;
                }

                return _context.abrupt("return");

              case 5:
                if (process.env.VUELAYERS_DEBUG) {
                  this.$logger.log("".concat(prop, " changed, scheduling recreate..."));
                }

                _context.next = 8;
                return this.scheduleRecreate();

              case 8:
              case "end":
                return _context.stop();
            }
          }
        }, _callee, this);
      }));

      return function (_x, _x2) {
        return _ref.apply(this, arguments);
      };
    }());
  })),
  methods: _objectSpread$b(_objectSpread$b({
    /**
     * @returns {Object}
     * @protected
     */
    getServices: function getServices() {
      return mergeDescriptors(imageStyle.methods.getServices.call(this), fillStyleContainer.methods.getServices.call(this), strokeStyleContainer.methods.getServices.call(this));
    }
  }, /*#__PURE__*/pick(imageStyle.methods, ['init', 'deinit', 'mount', 'unmount', 'refresh', 'scheduleRefresh', 'remount', 'scheduleRemount', 'recreate', 'scheduleRecreate', 'subscribeAll', 'resolveOlObject', 'resolveStyle'])), {}, {
    getFillStyleTarget: function getFillStyleTarget() {
      var _this = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3() {
        return _regeneratorRuntime.wrap(function _callee3$(_context3) {
          while (1) {
            switch (_context3.prev = _context3.next) {
              case 0:
                return _context3.abrupt("return", {
                  setFill: function () {
                    var _setFill = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
                      return _regeneratorRuntime.wrap(function _callee2$(_context2) {
                        while (1) {
                          switch (_context2.prev = _context2.next) {
                            case 0:
                              ++_this.rev;

                              if (process.env.VUELAYERS_DEBUG) {
                                _this.$logger.log('fill changed, scheduling recreate...');
                              }

                              _context2.next = 4;
                              return _this.scheduleRecreate();

                            case 4:
                            case "end":
                              return _context2.stop();
                          }
                        }
                      }, _callee2);
                    }));

                    function setFill() {
                      return _setFill.apply(this, arguments);
                    }

                    return setFill;
                  }()
                });

              case 1:
              case "end":
                return _context3.stop();
            }
          }
        }, _callee3);
      }))();
    },
    getStrokeStyleTarget: function getStrokeStyleTarget() {
      var _this2 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee5() {
        return _regeneratorRuntime.wrap(function _callee5$(_context5) {
          while (1) {
            switch (_context5.prev = _context5.next) {
              case 0:
                return _context5.abrupt("return", {
                  setStroke: function () {
                    var _setStroke = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee4() {
                      return _regeneratorRuntime.wrap(function _callee4$(_context4) {
                        while (1) {
                          switch (_context4.prev = _context4.next) {
                            case 0:
                              ++_this2.rev;

                              if (process.env.VUELAYERS_DEBUG) {
                                _this2.$logger.log('stroke changed, scheduling recreate...');
                              }

                              _context4.next = 4;
                              return _this2.scheduleRecreate();

                            case 4:
                            case "end":
                              return _context4.stop();
                          }
                        }
                      }, _callee4);
                    }));

                    function setStroke() {
                      return _setStroke.apply(this, arguments);
                    }

                    return setStroke;
                  }()
                });

              case 1:
              case "end":
                return _context5.stop();
            }
          }
        }, _callee5);
      }))();
    },
    getAnchor: function getAnchor() {
      var _this3 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee6() {
        return _regeneratorRuntime.wrap(function _callee6$(_context6) {
          while (1) {
            switch (_context6.prev = _context6.next) {
              case 0:
                _context6.next = 2;
                return _this3.resolveStyle();

              case 2:
                return _context6.abrupt("return", _context6.sent.getAnchor());

              case 3:
              case "end":
                return _context6.stop();
            }
          }
        }, _callee6);
      }))();
    },
    getAngle: function getAngle() {
      var _this4 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee7() {
        return _regeneratorRuntime.wrap(function _callee7$(_context7) {
          while (1) {
            switch (_context7.prev = _context7.next) {
              case 0:
                _context7.next = 2;
                return _this4.resolveStyle();

              case 2:
                return _context7.abrupt("return", _context7.sent.getAngle());

              case 3:
              case "end":
                return _context7.stop();
            }
          }
        }, _callee7);
      }))();
    },
    getImage: function getImage() {
      var _this5 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee8() {
        return _regeneratorRuntime.wrap(function _callee8$(_context8) {
          while (1) {
            switch (_context8.prev = _context8.next) {
              case 0:
                _context8.next = 2;
                return _this5.resolveStyle();

              case 2:
                return _context8.abrupt("return", _context8.sent.getImage());

              case 3:
              case "end":
                return _context8.stop();
            }
          }
        }, _callee8);
      }))();
    },
    getOrigin: function getOrigin() {
      var _this6 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee9() {
        return _regeneratorRuntime.wrap(function _callee9$(_context9) {
          while (1) {
            switch (_context9.prev = _context9.next) {
              case 0:
                _context9.next = 2;
                return _this6.resolveStyle();

              case 2:
                return _context9.abrupt("return", _context9.sent.getOrigin());

              case 3:
              case "end":
                return _context9.stop();
            }
          }
        }, _callee9);
      }))();
    },
    getPoints: function getPoints() {
      var _this7 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee10() {
        return _regeneratorRuntime.wrap(function _callee10$(_context10) {
          while (1) {
            switch (_context10.prev = _context10.next) {
              case 0:
                _context10.next = 2;
                return _this7.resolveStyle();

              case 2:
                return _context10.abrupt("return", _context10.sent.getPoints());

              case 3:
              case "end":
                return _context10.stop();
            }
          }
        }, _callee10);
      }))();
    },
    getRadius: function getRadius() {
      var _this8 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee11() {
        return _regeneratorRuntime.wrap(function _callee11$(_context11) {
          while (1) {
            switch (_context11.prev = _context11.next) {
              case 0:
                _context11.next = 2;
                return _this8.resolveStyle();

              case 2:
                return _context11.abrupt("return", _context11.sent.getRadius());

              case 3:
              case "end":
                return _context11.stop();
            }
          }
        }, _callee11);
      }))();
    },
    getRadius2: function getRadius2() {
      var _this9 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee12() {
        return _regeneratorRuntime.wrap(function _callee12$(_context12) {
          while (1) {
            switch (_context12.prev = _context12.next) {
              case 0:
                _context12.next = 2;
                return _this9.resolveStyle();

              case 2:
                return _context12.abrupt("return", _context12.sent.getRadius2());

              case 3:
              case "end":
                return _context12.stop();
            }
          }
        }, _callee12);
      }))();
    },
    getSize: function getSize() {
      var _this10 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee13() {
        return _regeneratorRuntime.wrap(function _callee13$(_context13) {
          while (1) {
            switch (_context13.prev = _context13.next) {
              case 0:
                _context13.next = 2;
                return _this10.resolveStyle();

              case 2:
                return _context13.abrupt("return", _context13.sent.getSize());

              case 3:
              case "end":
                return _context13.stop();
            }
          }
        }, _callee13);
      }))();
    }
  })
};

function ownKeys$c(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$c(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$c(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$c(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
/**
 * Base simple geometry with coordinates mixin.
 */

var simpleGeometry = {
  mixins: [geometry],
  props: {
    // ol/geom/SimpleGeometry

    /**
     * @type {number[]} Coordinates in map data projection
     */
    coordinates: {
      type: Array,
      required: true,
      validator: /*#__PURE__*/negate(isEmpty)
    } // todo add support of coord layout
    // /**
    //  * @type {string}
    //  */
    // layout: {
    //   type: String,
    //   default: GeometryLayout.XY,
    //   validator: value => Object.values(GeometryLayout).includes(value.toUpperCase()),
    // },

  },
  computed: {
    coordinatesDataProj: function coordinatesDataProj() {
      return roundCoords(this.type, this.coordinates);
    },
    coordinatesViewProj: function coordinatesViewProj() {
      return this.coordinatesToViewProj(this.coordinates);
    },
    extentDataProj: function extentDataProj() {
      return boundingExtent(flatCoords(this.type, this.coordinatesDataProj));
    },
    extentViewProj: function extentViewProj() {
      return this.extentToViewProj(this.extentDataProj);
    },
    currentCoordinatesDataProj: function currentCoordinatesDataProj() {
      if (this.rev && this.$geometry) {
        return this.getCoordinatesInternal();
      }

      return this.coordinatesDataProj;
    },
    currentCoordinatesViewProj: function currentCoordinatesViewProj() {
      if (this.rev && this.$geometry) {
        return this.getCoordinatesInternal(true);
      }

      return this.coordinatesViewProj;
    },
    currentPointDataProj: function currentPointDataProj() {
      if (!(this.rev && this.$geometry)) return;
      return this.findPointOnSurfaceInternal();
    },
    currentPointViewProj: function currentPointViewProj() {
      if (!(this.rev && this.$geometry)) return;
      return this.findPointOnSurfaceInternal(true);
    } // layoutUpCase () {
    //   return this.layout.toUpperCase()
    // },

  },
  watch: {
    coordinatesViewProj: {
      deep: true,
      handler: /*#__PURE__*/sequential( /*#__PURE__*/function () {
        var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(value) {
          return _regeneratorRuntime.wrap(function _callee$(_context) {
            while (1) {
              switch (_context.prev = _context.next) {
                case 0:
                  _context.next = 2;
                  return this.setCoordinates(value, true);

                case 2:
                case "end":
                  return _context.stop();
              }
            }
          }, _callee, this);
        }));

        return function (_x) {
          return _ref.apply(this, arguments);
        };
      }())
    },
    currentCoordinatesDataProj: {
      deep: true,
      handler: debounce(function (value) {
        if (isEqualCoord({
          coordinates: value,
          extent: boundingExtent(flatCoords(this.type, value))
        }, {
          coordinates: this.coordinatesDataProj,
          extent: this.extentDataProj
        })) return;
        this.$emit('update:coordinates', clonePlainObject(value));
      }, FRAME_TIME)
    } // ...makeWatchers([
    //   'layoutUpCase',
    // ], () => geometry.methods.scheduleRecreate),

  },
  methods: _objectSpread$c(_objectSpread$c({}, /*#__PURE__*/pick(geometry.methods, ['beforeInit', 'init', 'deinit', 'beforeMount', 'mount', 'unmount', 'refresh', 'scheduleRefresh', 'remount', 'scheduleRemount', 'recreate', 'scheduleRecreate', 'getServices', 'subscribeAll', 'resolveOlObject', 'resolveGeometry'])), {}, {
    /**
     * @returns {function}
     */
    getCoordinatesTransformFunction: function getCoordinatesTransformFunction() {
      return transforms[this.type].transform;
    },

    /**
     * @param {number[]} coordinates
     * @returns {Promise<number[]>}
     */
    coordinatesToDataProj: function coordinatesToDataProj(coordinates) {
      var transform = this.getCoordinatesTransformFunction();
      return transform(coordinates, this.resolvedViewProjection, this.resolvedDataProjection);
    },

    /**
     * @param {number[]} coordinates
     * @returns {Promise<number[]>}
     */
    coordinatesToViewProj: function coordinatesToViewProj(coordinates) {
      var transform = this.getCoordinatesTransformFunction();
      return transform(coordinates, this.resolvedDataProjection, this.resolvedViewProjection);
    },

    /**
     * @param {boolean} [viewProj=false]
     * @return {Promise<number[]>}
     */
    getCoordinates: function getCoordinates() {
      var _arguments = arguments,
          _this = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
        var viewProj;
        return _regeneratorRuntime.wrap(function _callee2$(_context2) {
          while (1) {
            switch (_context2.prev = _context2.next) {
              case 0:
                viewProj = _arguments.length > 0 && _arguments[0] !== undefined ? _arguments[0] : false;
                _context2.next = 3;
                return _this.resolveGeometry();

              case 3:
                return _context2.abrupt("return", _this.getCoordinatesInternal(viewProj));

              case 4:
              case "end":
                return _context2.stop();
            }
          }
        }, _callee2);
      }))();
    },

    /**
     * @param {boolean} [viewProj=false]
     * @return {number[]}
     * @protected
     */
    getCoordinatesInternal: function getCoordinatesInternal() {
      var viewProj = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;

      if (viewProj) {
        return roundCoords(this.getTypeInternal(), this.$geometry.getCoordinates());
      }

      return this.coordinatesToDataProj(this.$geometry.getCoordinates());
    },

    /**
     * @param {number[]} coordinates
     * @param {boolean} [viewProj=false]
     */
    setCoordinates: function setCoordinates(coordinates) {
      var _arguments2 = arguments,
          _this2 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3() {
        var viewProj;
        return _regeneratorRuntime.wrap(function _callee3$(_context3) {
          while (1) {
            switch (_context3.prev = _context3.next) {
              case 0:
                viewProj = _arguments2.length > 1 && _arguments2[1] !== undefined ? _arguments2[1] : false;
                _context3.next = 3;
                return _this2.resolveGeometry();

              case 3:
                _this2.setCoordinatesInternal(coordinates, viewProj);

              case 4:
              case "end":
                return _context3.stop();
            }
          }
        }, _callee3);
      }))();
    },

    /**
     * @param {number[]} coordinates
     * @param {boolean} [viewProj=false]
     * @protected
     */
    setCoordinatesInternal: function setCoordinatesInternal(coordinates) {
      var viewProj = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
      coordinates = roundCoords(this.getTypeInternal(), coordinates);
      var currentCoordinates = this.getCoordinatesInternal(viewProj);
      if (isEqualCoord({
        coordinates: coordinates,
        extent: boundingExtent(flatCoords(this.getTypeInternal(), coordinates))
      }, {
        coordinates: currentCoordinates,
        extent: boundingExtent(flatCoords(this.getTypeInternal(), currentCoordinates))
      })) return;

      if (!viewProj) {
        coordinates = this.coordinatesToViewProj(coordinates);
      }

      this.$geometry.setCoordinates(coordinates);
    },

    /**
     * @param {boolean} [viewProj=false]
     * @returns {number[]>}
     */
    getFirstCoordinate: function getFirstCoordinate() {
      var _arguments3 = arguments,
          _this3 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee4() {
        var viewProj, coordinate;
        return _regeneratorRuntime.wrap(function _callee4$(_context4) {
          while (1) {
            switch (_context4.prev = _context4.next) {
              case 0:
                viewProj = _arguments3.length > 0 && _arguments3[0] !== undefined ? _arguments3[0] : false;
                _context4.next = 3;
                return _this3.resolveGeometry();

              case 3:
                coordinate = _context4.sent.getFirstCoordinate();

                if (!viewProj) {
                  _context4.next = 6;
                  break;
                }

                return _context4.abrupt("return", roundPointCoords(coordinate));

              case 6:
                return _context4.abrupt("return", _this3.pointToDataProj(coordinate));

              case 7:
              case "end":
                return _context4.stop();
            }
          }
        }, _callee4);
      }))();
    },

    /**
     * @param {boolean} [viewProj=false]
     * @returns {Promise<number[]>}
     */
    getLastCoordinate: function getLastCoordinate() {
      var _arguments4 = arguments,
          _this4 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee5() {
        var viewProj, coordinate;
        return _regeneratorRuntime.wrap(function _callee5$(_context5) {
          while (1) {
            switch (_context5.prev = _context5.next) {
              case 0:
                viewProj = _arguments4.length > 0 && _arguments4[0] !== undefined ? _arguments4[0] : false;
                _context5.next = 3;
                return _this4.resolveGeometry();

              case 3:
                coordinate = _context5.sent.getLastCoordinate();

                if (!viewProj) {
                  _context5.next = 6;
                  break;
                }

                return _context5.abrupt("return", roundPointCoords(coordinate));

              case 6:
                return _context5.abrupt("return", _this4.pointToDataProj(coordinate));

              case 7:
              case "end":
                return _context5.stop();
            }
          }
        }, _callee5);
      }))();
    },

    /**
     * @returns {Promise<string>}
     */
    getLayout: function getLayout() {
      var _this5 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee6() {
        return _regeneratorRuntime.wrap(function _callee6$(_context6) {
          while (1) {
            switch (_context6.prev = _context6.next) {
              case 0:
                _context6.next = 2;
                return _this5.resolveGeometry();

              case 2:
                return _context6.abrupt("return", _context6.sent.getLayout());

              case 3:
              case "end":
                return _context6.stop();
            }
          }
        }, _callee6);
      }))();
    },

    /**
     * @param {boolean} [viewProj=false]
     * @return {Promise<number[]|undefined>}
     */
    findPointOnSurface: function findPointOnSurface() {
      var _arguments5 = arguments,
          _this6 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee7() {
        var viewProj;
        return _regeneratorRuntime.wrap(function _callee7$(_context7) {
          while (1) {
            switch (_context7.prev = _context7.next) {
              case 0:
                viewProj = _arguments5.length > 0 && _arguments5[0] !== undefined ? _arguments5[0] : false;
                _context7.next = 3;
                return _this6.resolveGeometry();

              case 3:
                return _context7.abrupt("return", _this6.findPointOnSurfaceInternal(viewProj));

              case 4:
              case "end":
                return _context7.stop();
            }
          }
        }, _callee7);
      }))();
    },

    /**
     * @param {boolean} [viewProj=false]
     * @return {number[]|undefined}
     * @protected
     */
    findPointOnSurfaceInternal: function findPointOnSurfaceInternal() {
      var viewProj = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
      return findPointOnSurface({
        type: this.$geometry.getType(),
        coordinates: this.getCoordinatesInternal(viewProj)
      });
    }
  })
};

/**
 * @typedef {module:ol/style/Text~Text|Object|undefined} TextStyleLike
 */

/**
 * @typedef {Object} TextStyleTarget
 * @property {function(): module:ol/style/Text~Text|undefined} getText
 * @property {function(module:ol/style/Text~Text|undefined): void} setText
 */

/**
 * Text style container.
 */

var textStyleContainer = {
  computed: {
    currentText: function currentText() {
      if (!(this.rev && this.$text)) return;
      return dumpTextStyle(this.$text);
    }
  },
  watch: {
    currentText: {
      deep: true,
      handler: /*#__PURE__*/debounce(function (value, prev) {
        if (isEqual(value, prev)) return;
        this.$emit('update:text', value && clonePlainObject(value));
      }, FRAME_TIME)
    }
  },
  created: function created() {
    this._text = undefined;
    this._textVm = undefined;
    defineServices$i.call(this);
  },
  methods: {
    /**
     * @returns {{readonly textStyleContainer: Object}}
     */
    getServices: function getServices() {
      var vm = this;
      return {
        get textStyleContainer() {
          return vm;
        }

      };
    },

    /**
     * @return {Promise<module:ol/style/Text~Text|undefined>}
     */
    getTextStyleTarget: function getTextStyleTarget() {
      throw new Error("".concat(this.vmName, " not implemented method: getTextStyleTarget()"));
    },

    /**
     * @returns {module:ol/style/Text~Text|null}
     */
    getText: function getText() {
      return this._text;
    },

    /**
     * @return {Object}
     */
    getTextVm: function getTextVm() {
      return this._textVm;
    },

    /**
     * @param {module:ol/style/Text~Text|undefined} text
     * @returns {Promise<void>}
     */
    setText: function setText(text) {
      var _this = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
        var _text, _text2;

        var textTarget;
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                if (!isFunction((_text = text) === null || _text === void 0 ? void 0 : _text.resolveOlObject)) {
                  _context.next = 4;
                  break;
                }

                _context.next = 3;
                return text.resolveOlObject();

              case 3:
                text = _context.sent;

              case 4:
                text || (text = null);

                if (!(text === _this._text)) {
                  _context.next = 7;
                  break;
                }

                return _context.abrupt("return");

              case 7:
                _context.next = 9;
                return _this.getTextStyleTarget();

              case 9:
                textTarget = _context.sent;

                if (textTarget) {
                  _context.next = 12;
                  break;
                }

                return _context.abrupt("return");

              case 12:
                _this._text = text;
                _this._textVm = ((_text2 = text) === null || _text2 === void 0 ? void 0 : _text2.vm) && text.vm[0];
                _context.next = 16;
                return textTarget.setText(text);

              case 16:
              case "end":
                return _context.stop();
            }
          }
        }, _callee);
      }))();
    }
  }
};

function defineServices$i() {
  Object.defineProperties(this, {
    $text: {
      enumerable: true,
      get: this.getText
    },
    $textVm: {
      enumerable: true,
      get: this.getTextVm
    }
  });
}

function ownKeys$d(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$d(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$d(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$d(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
var tileSource = {
  mixins: [source],
  props: {
    // ol/source/Tile

    /**
     * @type {number|undefined}
     */
    cacheSize: Number,

    /**
     * @type {boolean|undefined}
     */
    opaque: Boolean,

    /**
     * @type {number}
     */
    tilePixelRatio: {
      type: Number,
      default: 1
    },

    /**
     * @type {string}
     */
    projection: {
      type: String,
      default: EPSG_3857,
      validator: function validator(value) {
        return get(value) != null;
      }
    },

    /**
     * @type {function|undefined}
     */
    tileGridFactory: Function,

    /**
     * @type {number}
     */
    transition: Number,

    /**
     * @type {string|undefined}
     */
    tileKey: String,

    /**
     * @type {number}
     */
    zDirection: {
      type: Number,
      default: 0
    }
  },
  data: function data() {
    return {
      /**
       * @type {module:ol/tilegrid/TileGrid~TileGrid|undefined}
       */
      tileGrid: undefined
    };
  },
  computed: {
    /**
     * @type {string|undefined}
     */
    tileGridIdent: function tileGridIdent() {
      if (!this.olObjIdent) return;
      return this.makeIdent(this.olObjIdent, 'tile_grid');
    },

    /**
     * @returns {function|undefined}
     */
    derivedTileGridFactory: function derivedTileGridFactory() {
      return this.tileGridFactory;
    },

    /**
     * @returns {function|undefined}
     */
    sealTileGridFactory: function sealTileGridFactory() {
      if (!isFunction(this.derivedTileGridFactory)) return;
      return sealFactory(this.derivedTileGridFactory.bind(this));
    },

    /**
     * @return {string|undefined}
     */
    currentTileKey: function currentTileKey() {
      if (this.rev && this.$source) {
        return this.getTileKeyInternal();
      }

      return this.tileKey;
    },

    /**
     * @returns {number[]|undefined}
     */
    currentResolutions: function currentResolutions() {
      if (!(this.rev && this.$source && this.$source.getTileGrid())) return;
      return this.$source.getResolutions();
    }
  },
  watch: _objectSpread$d({
    tileKey: /*#__PURE__*/sequential( /*#__PURE__*/function () {
      var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(value) {
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                _context.next = 2;
                return this.setTileKey(value);

              case 2:
              case "end":
                return _context.stop();
            }
          }
        }, _callee, this);
      }));

      return function (_x) {
        return _ref.apply(this, arguments);
      };
    }()),
    currentTileKey: /*#__PURE__*/debounce(function (value) {
      if (value === this.tileKey) return;
      this.$emit('update:tileKey', value);
    }, FRAME_TIME),
    currentResolutions: /*#__PURE__*/debounce(function (value) {
      this.$emit('update:resolutions', clonePlainObject(value));
    }, FRAME_TIME),
    tileGridIdent: /*#__PURE__*/sequential(function (value, prevValue) {
      if (value && prevValue) {
        this.moveInstance(value, prevValue);
      } else if (value && !prevValue && this.tileGrid) {
        this.setInstance(value, this.tileGrid);
      } else if (!value && prevValue) {
        this.unsetInstance(prevValue);
      }
    }),
    sealTileGridFactory: /*#__PURE__*/sequential( /*#__PURE__*/function () {
      var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(value) {
        return _regeneratorRuntime.wrap(function _callee2$(_context2) {
          while (1) {
            switch (_context2.prev = _context2.next) {
              case 0:
                while (this.hasInstance(this.tileGridIdent)) {
                  this.unsetInstance(this.tileGridIdent);
                }

                if (isFunction(value)) {
                  this.tileGrid = this.instanceFactoryCall(this.tileGridIdent, value.bind(this));
                } else {
                  this.tileGrid = undefined;
                }

                if (process.env.VUELAYERS_DEBUG) {
                  this.$logger.log('sealTileGridFactory changed, scheduling recreate...');
                }

                _context2.next = 5;
                return this.scheduleRecreate();

              case 5:
              case "end":
                return _context2.stop();
            }
          }
        }, _callee2, this);
      }));

      return function (_x2) {
        return _ref2.apply(this, arguments);
      };
    }()),
    opaque: /*#__PURE__*/sequential( /*#__PURE__*/function () {
      var _ref3 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3(value) {
        return _regeneratorRuntime.wrap(function _callee3$(_context3) {
          while (1) {
            switch (_context3.prev = _context3.next) {
              case 0:
                _context3.t0 = value;
                _context3.next = 3;
                return this.getOpaque();

              case 3:
                _context3.t1 = _context3.sent;

                if (!(_context3.t0 === _context3.t1)) {
                  _context3.next = 6;
                  break;
                }

                return _context3.abrupt("return");

              case 6:
                if (process.env.VUELAYERS_DEBUG) {
                  this.$logger.log('opaque changed, scheduling recreate...');
                }

                _context3.next = 9;
                return this.scheduleRecreate();

              case 9:
              case "end":
                return _context3.stop();
            }
          }
        }, _callee3, this);
      }));

      return function (_x3) {
        return _ref3.apply(this, arguments);
      };
    }()),
    tilePixelRatio: /*#__PURE__*/sequential( /*#__PURE__*/function () {
      var _ref4 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee4(value) {
        return _regeneratorRuntime.wrap(function _callee4$(_context4) {
          while (1) {
            switch (_context4.prev = _context4.next) {
              case 0:
                _context4.t0 = value;
                _context4.next = 3;
                return this.getTilePixelRatio(value);

              case 3:
                _context4.t1 = _context4.sent;

                if (!(_context4.t0 === _context4.t1)) {
                  _context4.next = 6;
                  break;
                }

                return _context4.abrupt("return");

              case 6:
                if (process.env.VUELAYERS_DEBUG) {
                  this.$logger.log('tilePixelRatio changed, scheduling recreate...');
                }

                _context4.next = 9;
                return this.scheduleRecreate();

              case 9:
              case "end":
                return _context4.stop();
            }
          }
        }, _callee4, this);
      }));

      return function (_x4) {
        return _ref4.apply(this, arguments);
      };
    }())
  }, /*#__PURE__*/makeWatchers(['cacheSize', 'transition', 'zDirection'], function (prop) {
    return /*#__PURE__*/sequential( /*#__PURE__*/function () {
      var _ref5 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee5(val, prev) {
        return _regeneratorRuntime.wrap(function _callee5$(_context5) {
          while (1) {
            switch (_context5.prev = _context5.next) {
              case 0:
                if (!isEqual(val, prev)) {
                  _context5.next = 2;
                  break;
                }

                return _context5.abrupt("return");

              case 2:
                if (process.env.VUELAYERS_DEBUG) {
                  this.$logger.log("".concat(prop, " changed, scheduling recreate..."));
                }

                _context5.next = 5;
                return this.scheduleRecreate();

              case 5:
              case "end":
                return _context5.stop();
            }
          }
        }, _callee5, this);
      }));

      return function (_x5, _x6) {
        return _ref5.apply(this, arguments);
      };
    }());
  })),
  created: function created() {
    if (isFunction(this.sealTileGridFactory)) {
      this.tileGrid = this.instanceFactoryCall(this.tileGridIdent, this.sealTileGridFactory.bind(this)); // this.$watch('tileGrid', async () => {
      //   if (process.env.VUELAYERS_DEBUG) {
      //     this.$logger.log(`tilegrid changed, scheduling recreate...`)
      //   }
      //
      //   await this.scheduleRecreate()
      // })
    }
  },
  methods: _objectSpread$d(_objectSpread$d({}, /*#__PURE__*/pick(source.methods, ['beforeInit', 'init', 'deinit', 'beforeMount', 'mount', 'unmount', 'refresh', 'scheduleRefresh', 'remount', 'scheduleRemount', 'recreate', 'scheduleRecreate', 'getServices', 'subscribeAll', 'resolveOlObject', 'resolveSource'])), {}, {
    /**
     * @returns {Promise<string|undefined>}
     */
    getTileKey: function getTileKey() {
      var _this = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee6() {
        return _regeneratorRuntime.wrap(function _callee6$(_context6) {
          while (1) {
            switch (_context6.prev = _context6.next) {
              case 0:
                _context6.next = 2;
                return _this.resolveSource();

              case 2:
                return _context6.abrupt("return", _this.getTileKeyInternal());

              case 3:
              case "end":
                return _context6.stop();
            }
          }
        }, _callee6);
      }))();
    },

    /**
     * @return {string|undefined}
     * @protected
     */
    getTileKeyInternal: function getTileKeyInternal() {
      return this.$source.getKey();
    },

    /**
     * @param {string|undefined} key
     * @returns {Promise<void>}
     */
    setTileKey: function setTileKey(key) {
      var _this2 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee7() {
        return _regeneratorRuntime.wrap(function _callee7$(_context7) {
          while (1) {
            switch (_context7.prev = _context7.next) {
              case 0:
                _context7.t0 = key;
                _context7.next = 3;
                return _this2.getTileKey();

              case 3:
                _context7.t1 = _context7.sent;

                if (!(_context7.t0 === _context7.t1)) {
                  _context7.next = 6;
                  break;
                }

                return _context7.abrupt("return");

              case 6:
                _context7.next = 8;
                return _this2.resolveSource();

              case 8:
                _context7.sent.setKey(key);

              case 9:
              case "end":
                return _context7.stop();
            }
          }
        }, _callee7);
      }))();
    },

    /**
     * @returns {Promise<boolean>}
     */
    getOpaque: function getOpaque() {
      var _this3 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee8() {
        return _regeneratorRuntime.wrap(function _callee8$(_context8) {
          while (1) {
            switch (_context8.prev = _context8.next) {
              case 0:
                _context8.next = 2;
                return _this3.resolveSource();

              case 2:
                return _context8.abrupt("return", _context8.sent.getOpaque());

              case 3:
              case "end":
                return _context8.stop();
            }
          }
        }, _callee8);
      }))();
    },

    /**
     * @returns {module:ol/tilegrid/TileGrid~TileGrid|undefined}
     */
    getTileGrid: function getTileGrid() {
      return this.tileGrid;
    }
  })
};

function ownKeys$e(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$e(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$e(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$e(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
var isNotEmptyString = /*#__PURE__*/and(isString, negate(isEmpty));
var urlTileSource = {
  mixins: [tileSource],
  props: {
    // ol/source/UrlTile

    /**
     * @type {function|undefined}
     */
    tileLoadFunc: Function,

    /**
     * @deprecated Use `tileLoadFunc` instead.
     * @todo remove in v0.13.x
     */
    tileLoadFunction: Function,

    /**
     * @type {function|undefined}
     */
    tileUrlFunc: Function,

    /**
     * @type {string|undefined}
     */
    url: {
      type: String,
      validator: isNotEmptyString
    },

    /**
     * @type {string[]|undefined}
     */
    urls: {
      type: Array,
      validator: function validator(value) {
        return value.every(isNotEmptyString);
      }
    }
  },
  computed: {
    urlTokens: function urlTokens() {
      return [];
    },
    parsedUrl: function parsedUrl() {
      if (!this.url) return;
      return replaceTokens(this.url, pick(this, this.urlTokens));
    },
    parsedUrls: function parsedUrls() {
      var urls = [];

      if (this.url) {
        urls.push(this.url);
      }

      if (this.urls && this.urls.length > 0) {
        urls.push.apply(urls, _toConsumableArray(this.urls));
      }

      var tokens = pick(this, this.urlTokens);
      return urls.map(function (url) {
        return replaceTokens(url, tokens);
      });
    },
    expandedUrls: function expandedUrls() {
      return this.parsedUrls.reduce(function (urls, url) {
        return urls.concat.apply(urls, _toConsumableArray(expandUrl(url)));
      }, []);
    },
    resolvedTileUrlFunc: function resolvedTileUrlFunc() {
      return this.tileUrlFunc;
    },
    resolvedTileLoadFunc: function resolvedTileLoadFunc() {
      return this.tileLoadFunc || this.tileLoadFunction;
    }
  },
  watch: {
    parsedUrl: /*#__PURE__*/sequential( /*#__PURE__*/function () {
      var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(value) {
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                _context.next = 2;
                return this.onParsedUrlChanged(value);

              case 2:
              case "end":
                return _context.stop();
            }
          }
        }, _callee, this);
      }));

      return function (_x) {
        return _ref.apply(this, arguments);
      };
    }()),
    expandedUrls: /*#__PURE__*/sequential( /*#__PURE__*/function () {
      var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(value) {
        return _regeneratorRuntime.wrap(function _callee2$(_context2) {
          while (1) {
            switch (_context2.prev = _context2.next) {
              case 0:
                _context2.next = 2;
                return this.onExpandedUrlsChanged(value);

              case 2:
              case "end":
                return _context2.stop();
            }
          }
        }, _callee2, this);
      }));

      return function (_x2) {
        return _ref2.apply(this, arguments);
      };
    }()),
    resolvedTileLoadFunc: /*#__PURE__*/sequential( /*#__PURE__*/function () {
      var _ref3 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3(value) {
        return _regeneratorRuntime.wrap(function _callee3$(_context3) {
          while (1) {
            switch (_context3.prev = _context3.next) {
              case 0:
                _context3.next = 2;
                return this.onTileLoadFuncChanged(value);

              case 2:
              case "end":
                return _context3.stop();
            }
          }
        }, _callee3, this);
      }));

      return function (_x3) {
        return _ref3.apply(this, arguments);
      };
    }()),
    resolvedTileUrlFunc: /*#__PURE__*/sequential( /*#__PURE__*/function () {
      var _ref4 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee4(value) {
        return _regeneratorRuntime.wrap(function _callee4$(_context4) {
          while (1) {
            switch (_context4.prev = _context4.next) {
              case 0:
                _context4.next = 2;
                return this.onTileUrlFuncChanged(value);

              case 2:
              case "end":
                return _context4.stop();
            }
          }
        }, _callee4, this);
      }));

      return function (_x4) {
        return _ref4.apply(this, arguments);
      };
    }())
  },
  created: function created() {
    if (process.env.NODE_ENV !== 'production') {
      if (this.tileLoadFunction) {
        this.$logger.warn("'tileLoadFunction' prop is deprecated. Use 'tileLoadFunc' prop instead.");
      }
    }
  },
  updated: function updated() {
    if (process.env.NODE_ENV !== 'production') {
      if (this.tileLoadFunction) {
        this.$logger.warn("'tileLoadFunction' prop is deprecated. Use 'tileLoadFunc' prop instead.");
      }
    }
  },
  methods: _objectSpread$e(_objectSpread$e({
    /**
     * @returns {void}
     */
    subscribeAll: function subscribeAll() {
      tileSource.methods.subscribeAll.call(this);
      subscribeToSourceEvents$2.call(this);
    }
  }, /*#__PURE__*/pick(tileSource.methods, ['beforeInit', 'init', 'deinit', 'beforeMount', 'mount', 'unmount', 'refresh', 'scheduleRefresh', 'remount', 'scheduleRemount', 'recreate', 'scheduleRecreate', 'getServices', 'resolveSource', 'resolveOlObject'])), {}, {
    /**
     * @returns {Promise<module:ol/Tile.LoadFunction>}
     */
    getTileLoadFunction: function getTileLoadFunction() {
      var _this = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee5() {
        return _regeneratorRuntime.wrap(function _callee5$(_context5) {
          while (1) {
            switch (_context5.prev = _context5.next) {
              case 0:
                _context5.next = 2;
                return _this.resolveSource();

              case 2:
                return _context5.abrupt("return", _context5.sent.getTileLoadFunction());

              case 3:
              case "end":
                return _context5.stop();
            }
          }
        }, _callee5);
      }))();
    },

    /**
     * @param {module:ol/Tile.LoadFunction} tileLoadFunction
     * @returns {Promise<void>}
     */
    setTileLoadFunction: function setTileLoadFunction(tileLoadFunction) {
      var _this2 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee6() {
        return _regeneratorRuntime.wrap(function _callee6$(_context6) {
          while (1) {
            switch (_context6.prev = _context6.next) {
              case 0:
                if (tileLoadFunction) {
                  _context6.next = 2;
                  break;
                }

                return _context6.abrupt("return");

              case 2:
                _context6.t0 = tileLoadFunction;
                _context6.next = 5;
                return _this2.getTileLoadFunction();

              case 5:
                _context6.t1 = _context6.sent;

                if (!(_context6.t0 === _context6.t1)) {
                  _context6.next = 8;
                  break;
                }

                return _context6.abrupt("return");

              case 8:
                _context6.next = 10;
                return _this2.resolveSource();

              case 10:
                _context6.sent.setTileLoadFunction(tileLoadFunction);

              case 11:
              case "end":
                return _context6.stop();
            }
          }
        }, _callee6);
      }))();
    },

    /**
     * @returns {Promise<module:ol/Tile.UrlFunction>}
     */
    getTileUrlFunction: function getTileUrlFunction() {
      var _this3 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee7() {
        return _regeneratorRuntime.wrap(function _callee7$(_context7) {
          while (1) {
            switch (_context7.prev = _context7.next) {
              case 0:
                _context7.next = 2;
                return _this3.resolveSource();

              case 2:
                return _context7.abrupt("return", _context7.sent.getTileUrlFunction());

              case 3:
              case "end":
                return _context7.stop();
            }
          }
        }, _callee7);
      }))();
    },

    /**
     * @param {module:ol/Tile.UrlFunction} tileUrlFunction
     * @param {number} [tileKey]
     * @returns {Promise<void>}
     */
    setTileUrlFunction: function setTileUrlFunction(tileUrlFunction, tileKey) {
      var _this4 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee8() {
        return _regeneratorRuntime.wrap(function _callee8$(_context8) {
          while (1) {
            switch (_context8.prev = _context8.next) {
              case 0:
                if (tileUrlFunction) {
                  _context8.next = 2;
                  break;
                }

                return _context8.abrupt("return");

              case 2:
                _context8.t0 = tileUrlFunction;
                _context8.next = 5;
                return _this4.getTileUrlFunction();

              case 5:
                _context8.t1 = _context8.sent;

                if (!(_context8.t0 === _context8.t1)) {
                  _context8.next = 8;
                  break;
                }

                return _context8.abrupt("return");

              case 8:
                _context8.next = 10;
                return _this4.resolveSource();

              case 10:
                _context8.sent.setTileUrlFunction(tileUrlFunction, tileKey);

              case 11:
              case "end":
                return _context8.stop();
            }
          }
        }, _callee8);
      }))();
    },

    /**
     * @returns {Promise<string[]|undefined>}
     */
    getUrls: function getUrls() {
      var _this5 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee9() {
        return _regeneratorRuntime.wrap(function _callee9$(_context9) {
          while (1) {
            switch (_context9.prev = _context9.next) {
              case 0:
                _context9.next = 2;
                return _this5.resolveSource();

              case 2:
                return _context9.abrupt("return", _context9.sent.getUrls());

              case 3:
              case "end":
                return _context9.stop();
            }
          }
        }, _callee9);
      }))();
    },

    /**
     * @param {string[]} urls
     * @returns {Promise<void>}
     */
    setUrls: function setUrls(urls) {
      var _this6 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee10() {
        return _regeneratorRuntime.wrap(function _callee10$(_context10) {
          while (1) {
            switch (_context10.prev = _context10.next) {
              case 0:
                _context10.t0 = isEqual;
                _context10.t1 = urls;
                _context10.next = 4;
                return _this6.getUrls();

              case 4:
                _context10.t2 = _context10.sent;

                if (!(0, _context10.t0)(_context10.t1, _context10.t2)) {
                  _context10.next = 7;
                  break;
                }

                return _context10.abrupt("return");

              case 7:
                _context10.next = 9;
                return _this6.resolveSource();

              case 9:
                _context10.sent.setUrls(urls);

              case 10:
              case "end":
                return _context10.stop();
            }
          }
        }, _callee10);
      }))();
    },

    /**
     * @param {string} url
     * @returns {Promise<void>}
     */
    setUrl: function setUrl(url) {
      var _this7 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee11() {
        return _regeneratorRuntime.wrap(function _callee11$(_context11) {
          while (1) {
            switch (_context11.prev = _context11.next) {
              case 0:
                _context11.next = 2;
                return _this7.setUrls(expandUrl(url));

              case 2:
              case "end":
                return _context11.stop();
            }
          }
        }, _callee11);
      }))();
    },
    onParsedUrlChanged: function onParsedUrlChanged(url) {
      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee12() {
        return _regeneratorRuntime.wrap(function _callee12$(_context12) {
          while (1) {
            switch (_context12.prev = _context12.next) {
              case 0:
              case "end":
                return _context12.stop();
            }
          }
        }, _callee12);
      }))();
    },
    onExpandedUrlsChanged: function onExpandedUrlsChanged(urls) {
      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee13() {
        return _regeneratorRuntime.wrap(function _callee13$(_context13) {
          while (1) {
            switch (_context13.prev = _context13.next) {
              case 0:
              case "end":
                return _context13.stop();
            }
          }
        }, _callee13);
      }))();
    },
    onTileLoadFuncChanged: function onTileLoadFuncChanged(tileLoadFunc) {
      var _this8 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee14() {
        return _regeneratorRuntime.wrap(function _callee14$(_context14) {
          while (1) {
            switch (_context14.prev = _context14.next) {
              case 0:
                _context14.next = 2;
                return _this8.setTileLoadFunction(tileLoadFunc);

              case 2:
              case "end":
                return _context14.stop();
            }
          }
        }, _callee14);
      }))();
    },
    onTileUrlFuncChanged: function onTileUrlFuncChanged(tileUrlFunc) {
      var _this9 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee15() {
        return _regeneratorRuntime.wrap(function _callee15$(_context15) {
          while (1) {
            switch (_context15.prev = _context15.next) {
              case 0:
                _context15.next = 2;
                return _this9.setTileUrlFunction(tileUrlFunc, _this9.rev);

              case 2:
              case "end":
                return _context15.stop();
            }
          }
        }, _callee15);
      }))();
    }
  })
};

function subscribeToSourceEvents$2() {
  return _subscribeToSourceEvents$2.apply(this, arguments);
}

function _subscribeToSourceEvents$2() {
  _subscribeToSourceEvents$2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee16() {
    var _this10 = this;

    var events;
    return _regeneratorRuntime.wrap(function _callee16$(_context16) {
      while (1) {
        switch (_context16.prev = _context16.next) {
          case 0:
            events = fromOlEvent(this.$source, [TileEventType.TILELOADSTART, TileEventType.TILELOADEND, TileEventType.TILELOADERROR]);
            this.subscribeTo(events, function (evt) {
              return _this10.$emit(evt.type, evt);
            });

          case 2:
          case "end":
            return _context16.stop();
        }
      }
    }, _callee16, this);
  }));
  return _subscribeToSourceEvents$2.apply(this, arguments);
}

function ownKeys$f(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$f(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$f(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$f(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
/**
 * Base tile image source mixin.
 */

var tileImageSource = {
  mixins: [urlTileSource],
  props: {
    // ol/source/TileImage

    /**
     * @type {string|undefined}
     */
    crossOrigin: String,

    /**
     * @type {number|undefined}
     */
    reprojectionErrorThreshold: Number,

    /**
     * @type {string|undefined}
     */
    tileClass: String
  },
  watch: _objectSpread$f({}, /*#__PURE__*/makeWatchers(['crossOrigin', 'reprojectionErrorThreshold', 'tileClass'], function (prop) {
    return /*#__PURE__*/sequential( /*#__PURE__*/function () {
      var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(val, prev) {
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                if (!isEqual(val, prev)) {
                  _context.next = 2;
                  break;
                }

                return _context.abrupt("return");

              case 2:
                if (process.env.VUELAYERS_DEBUG) {
                  this.$logger.log("".concat(prop, " changed, scheduling recreate..."));
                }

                _context.next = 5;
                return this.scheduleRecreate();

              case 5:
              case "end":
                return _context.stop();
            }
          }
        }, _callee, this);
      }));

      return function (_x, _x2) {
        return _ref.apply(this, arguments);
      };
    }());
  })),
  methods: _objectSpread$f({}, /*#__PURE__*/pick(urlTileSource.methods, ['beforeInit', 'init', 'deinit', 'beforeMount', 'mount', 'unmount', 'refresh', 'scheduleRefresh', 'remount', 'scheduleRemount', 'recreate', 'scheduleRecreate', 'getServices', 'subscribeAll', 'resolveSource', 'resolveOlObject']))
};

function ownKeys$g(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$g(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$g(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$g(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
/**
 * Base tile layer mixin.
 */

var tileLayer = {
  mixins: [layer],
  props: {
    // ol/layer/BaseTile

    /**
     * @type {number}
     */
    preload: {
      type: Number,
      default: 0
    },

    /**
     * @type {boolean}
     */
    useInterimTilesOnError: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    currentPreload: function currentPreload() {
      if (this.rev && this.$layer) {
        return this.getPreloadInternal();
      }

      return this.preload;
    },
    currentUseInterimTilesOnError: function currentUseInterimTilesOnError() {
      if (this.rev && this.$layer) {
        return this.getUseInterimTilesOnErrorInternal();
      }

      return this.useInterimTilesOnError;
    }
  },
  watch: {
    preload: /*#__PURE__*/sequential( /*#__PURE__*/function () {
      var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(value) {
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                _context.next = 2;
                return this.setPreload(value);

              case 2:
              case "end":
                return _context.stop();
            }
          }
        }, _callee, this);
      }));

      return function (_x) {
        return _ref.apply(this, arguments);
      };
    }()),
    currentPreload: /*#__PURE__*/debounce(function (value) {
      if (value === this.preload) return;
      this.$emit('update:preload', value);
    }, FRAME_TIME),
    useInterimTilesOnError: /*#__PURE__*/sequential( /*#__PURE__*/function () {
      var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(value) {
        return _regeneratorRuntime.wrap(function _callee2$(_context2) {
          while (1) {
            switch (_context2.prev = _context2.next) {
              case 0:
                _context2.next = 2;
                return this.setUseInterimTilesOnError(value);

              case 2:
              case "end":
                return _context2.stop();
            }
          }
        }, _callee2, this);
      }));

      return function (_x2) {
        return _ref2.apply(this, arguments);
      };
    }()),
    currentUseInterimTilesOnError: /*#__PURE__*/debounce(function (value) {
      if (value === this.useInterimTilesOnError) return;
      this.$emit('update:useInterimTilesOnError', value);
    }, FRAME_TIME)
  },
  methods: _objectSpread$g(_objectSpread$g({
    /**
     * @returns {void}
     */
    subscribeAll: function subscribeAll() {
      layer.methods.subscribeAll.call(this);
      subscribeToLayerEvents$2.call(this);
    }
  }, /*#__PURE__*/pick(layer.methods, ['beforeInit', 'init', 'deinit', 'beforeMount', 'mount', 'unmount', 'refresh', 'scheduleRefresh', 'recreate', 'scheduleRecreate', 'remount', 'scheduleRemount', 'getServices', 'resolveOlObject', 'resolveLayer'])), {}, {
    /**
     * @returns {Promise<number>}
     */
    getPreload: function getPreload() {
      var _this = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3() {
        return _regeneratorRuntime.wrap(function _callee3$(_context3) {
          while (1) {
            switch (_context3.prev = _context3.next) {
              case 0:
                _context3.next = 2;
                return _this.resolveLayer();

              case 2:
                return _context3.abrupt("return", _this.getPreloadInternal());

              case 3:
              case "end":
                return _context3.stop();
            }
          }
        }, _callee3);
      }))();
    },

    /**
     * @return {number}
     * @protected
     */
    getPreloadInternal: function getPreloadInternal() {
      return this.$layer.getPreload();
    },

    /**
     * @param {number} preload
     * @returns {Promise<void>}
     */
    setPreload: function setPreload(preload) {
      var _this2 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee4() {
        return _regeneratorRuntime.wrap(function _callee4$(_context4) {
          while (1) {
            switch (_context4.prev = _context4.next) {
              case 0:
                _context4.t0 = preload;
                _context4.next = 3;
                return _this2.getPreload();

              case 3:
                _context4.t1 = _context4.sent;

                if (!(_context4.t0 === _context4.t1)) {
                  _context4.next = 6;
                  break;
                }

                return _context4.abrupt("return");

              case 6:
                _context4.next = 8;
                return _this2.resolveLayer();

              case 8:
                _context4.sent.setPreload(preload);

              case 9:
              case "end":
                return _context4.stop();
            }
          }
        }, _callee4);
      }))();
    },

    /**
     * @returns {Promise<boolean>}
     */
    getUseInterimTilesOnError: function getUseInterimTilesOnError() {
      var _this3 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee5() {
        return _regeneratorRuntime.wrap(function _callee5$(_context5) {
          while (1) {
            switch (_context5.prev = _context5.next) {
              case 0:
                _context5.next = 2;
                return _this3.resolveLayer();

              case 2:
                return _context5.abrupt("return", _this3.getUseInterimTilesOnErrorInternal());

              case 3:
              case "end":
                return _context5.stop();
            }
          }
        }, _callee5);
      }))();
    },

    /**
     * @return {boolean}
     * @protected
     */
    getUseInterimTilesOnErrorInternal: function getUseInterimTilesOnErrorInternal() {
      return this.$layer.getUseInterimTilesOnError();
    },

    /**
     * @param {boolean} useInterimTilesOnError
     * @returns {Promise<void>}
     */
    setUseInterimTilesOnError: function setUseInterimTilesOnError(useInterimTilesOnError) {
      var _this4 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee6() {
        return _regeneratorRuntime.wrap(function _callee6$(_context6) {
          while (1) {
            switch (_context6.prev = _context6.next) {
              case 0:
                _context6.t0 = useInterimTilesOnError;
                _context6.next = 3;
                return _this4.getUseInterimTilesOnError();

              case 3:
                _context6.t1 = _context6.sent;

                if (!(_context6.t0 === _context6.t1)) {
                  _context6.next = 6;
                  break;
                }

                return _context6.abrupt("return");

              case 6:
                _context6.next = 8;
                return _this4.resolveLayer();

              case 8:
                _context6.sent.setUseInterimTilesOnError(useInterimTilesOnError);

              case 9:
              case "end":
                return _context6.stop();
            }
          }
        }, _callee6);
      }))();
    }
  })
};

function subscribeToLayerEvents$2() {
  return _subscribeToLayerEvents$2.apply(this, arguments);
}

function _subscribeToLayerEvents$2() {
  _subscribeToLayerEvents$2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee7() {
    var _this5 = this;

    var prefixKey, propChanges;
    return _regeneratorRuntime.wrap(function _callee7$(_context7) {
      while (1) {
        switch (_context7.prev = _context7.next) {
          case 0:
            prefixKey = addPrefix('current');
            propChanges = fromOlChangeEvent(this.$layer, ['preload', 'useInterimTilesOnError'], true, function (evt) {
              return _objectSpread$g(_objectSpread$g({}, evt), {}, {
                compareWith: _this5[prefixKey(evt.prop)]
              });
            }).pipe(skipWhile(function (_ref3) {
              var value = _ref3.value,
                  compareWith = _ref3.compareWith;
              return isEqual(value, compareWith);
            }));
            this.subscribeTo(propChanges, function () {
              ++_this5.rev;
            });

          case 3:
          case "end":
            return _context7.stop();
        }
      }
    }, _callee7, this);
  }));
  return _subscribeToLayerEvents$2.apply(this, arguments);
}

function ownKeys$h(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$h(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$h(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$h(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
var vectorLayer = {
  mixins: [styleContainer, layer],
  props: {
    // ol/layer/BaseVector

    /**
     * @type {function|undefined}
     */
    renderOrder: Function,

    /**
     * @type {number}
     */
    renderBuffer: {
      type: Number,
      default: 100
    },

    /**
     * @type {boolean}
     */
    declutter: Boolean,

    /**
     * When set to `true`, feature batches will be recreated during animations.
     * @type {boolean}
     */
    updateWhileAnimating: Boolean,

    /**
     * When set to `true`, feature batches will be recreated during interactions.
     * @type {boolean}
     */
    updateWhileInteracting: Boolean
  },
  computed: {
    currentRenderOrder: function currentRenderOrder() {
      if (this.rev && this.$layer) {
        return this.getRenderOrderInternal();
      }

      return this.renderOrder;
    }
  },
  watch: {
    renderOrder: /*#__PURE__*/sequential( /*#__PURE__*/function () {
      var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(value) {
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                _context.next = 2;
                return this.setRenderOrder(value);

              case 2:
              case "end":
                return _context.stop();
            }
          }
        }, _callee, this);
      }));

      return function (_x) {
        return _ref.apply(this, arguments);
      };
    }()),
    currentRenderOrder: /*#__PURE__*/debounce(function (value) {
      if (value === this.renderOrder) return;
      this.$emit('update:renderOrder', value);
    }, FRAME_TIME),
    renderBuffer: /*#__PURE__*/sequential( /*#__PURE__*/function () {
      var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(value) {
        return _regeneratorRuntime.wrap(function _callee2$(_context2) {
          while (1) {
            switch (_context2.prev = _context2.next) {
              case 0:
                _context2.t0 = value;
                _context2.next = 3;
                return this.getRenderBuffer();

              case 3:
                _context2.t1 = _context2.sent;

                if (!(_context2.t0 === _context2.t1)) {
                  _context2.next = 6;
                  break;
                }

                return _context2.abrupt("return");

              case 6:
                if (process.env.VUELAYERS_DEBUG) {
                  this.$logger.log('renderBuffer changed, scheduling recreate...');
                }

                _context2.next = 9;
                return this.scheduleRecreate();

              case 9:
              case "end":
                return _context2.stop();
            }
          }
        }, _callee2, this);
      }));

      return function (_x2) {
        return _ref2.apply(this, arguments);
      };
    }()),
    declutter: /*#__PURE__*/sequential( /*#__PURE__*/function () {
      var _ref3 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3(value) {
        return _regeneratorRuntime.wrap(function _callee3$(_context3) {
          while (1) {
            switch (_context3.prev = _context3.next) {
              case 0:
                _context3.t0 = value;
                _context3.next = 3;
                return this.getDeclutter();

              case 3:
                _context3.t1 = _context3.sent;

                if (!(_context3.t0 === _context3.t1)) {
                  _context3.next = 6;
                  break;
                }

                return _context3.abrupt("return");

              case 6:
                if (process.env.VUELAYERS_DEBUG) {
                  this.$logger.log('declutter changed, scheduling recreate...');
                }

                _context3.next = 9;
                return this.scheduleRecreate();

              case 9:
              case "end":
                return _context3.stop();
            }
          }
        }, _callee3, this);
      }));

      return function (_x3) {
        return _ref3.apply(this, arguments);
      };
    }()),
    updateWhileAnimating: /*#__PURE__*/sequential( /*#__PURE__*/function () {
      var _ref4 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee4(value) {
        return _regeneratorRuntime.wrap(function _callee4$(_context4) {
          while (1) {
            switch (_context4.prev = _context4.next) {
              case 0:
                _context4.t0 = value;
                _context4.next = 3;
                return this.getUpdateWhileAnimating();

              case 3:
                _context4.t1 = _context4.sent;

                if (!(_context4.t0 === _context4.t1)) {
                  _context4.next = 6;
                  break;
                }

                return _context4.abrupt("return");

              case 6:
                if (process.env.VUELAYERS_DEBUG) {
                  this.$logger.log('updateWhileAnimating changed, scheduling recreate...');
                }

                _context4.next = 9;
                return this.scheduleRecreate();

              case 9:
              case "end":
                return _context4.stop();
            }
          }
        }, _callee4, this);
      }));

      return function (_x4) {
        return _ref4.apply(this, arguments);
      };
    }()),
    updateWhileInteracting: /*#__PURE__*/sequential( /*#__PURE__*/function () {
      var _ref5 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee5(value) {
        return _regeneratorRuntime.wrap(function _callee5$(_context5) {
          while (1) {
            switch (_context5.prev = _context5.next) {
              case 0:
                _context5.t0 = value;
                _context5.next = 3;
                return this.getUpdateWhileInteracting();

              case 3:
                _context5.t1 = _context5.sent;

                if (!(_context5.t0 === _context5.t1)) {
                  _context5.next = 6;
                  break;
                }

                return _context5.abrupt("return");

              case 6:
                if (process.env.VUELAYERS_DEBUG) {
                  this.$logger.log('updateWhileInteracting changed, scheduling recreate...');
                }

                _context5.next = 9;
                return this.scheduleRecreate();

              case 9:
              case "end":
                return _context5.stop();
            }
          }
        }, _callee5, this);
      }));

      return function (_x5) {
        return _ref5.apply(this, arguments);
      };
    }())
  },
  methods: _objectSpread$h(_objectSpread$h({
    /**
     * @returns {Object}
     * @protected
     */
    getServices: function getServices() {
      return mergeDescriptors(layer.methods.getServices.call(this), styleContainer.methods.getServices.call(this));
    },

    /**
     * @returns {void}
     */
    subscribeAll: function subscribeAll() {
      layer.methods.subscribeAll.call(this);
      subscribeToLayerEvents$3.call(this);
    }
  }, /*#__PURE__*/pick(layer.methods, ['beforeInit', 'init', 'deinit', 'beforeMount', 'mount', 'unmount', 'refresh', 'scheduleRefresh', 'remount', 'scheduleRemount', 'recreate', 'scheduleRecreate', 'resolveOlObject', 'resolveLayer'])), {}, {
    /**
     * @return {Promise<StyleTarget|undefined>}
     */
    getStyleTarget: layer.methods.resolveLayer,

    /**
     * @returns {Promise<boolean>}
     */
    getDeclutter: function getDeclutter() {
      var _this = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee6() {
        return _regeneratorRuntime.wrap(function _callee6$(_context6) {
          while (1) {
            switch (_context6.prev = _context6.next) {
              case 0:
                _context6.next = 2;
                return _this.resolveLayer();

              case 2:
                return _context6.abrupt("return", _context6.sent.getDeclutter());

              case 3:
              case "end":
                return _context6.stop();
            }
          }
        }, _callee6);
      }))();
    },

    /**
     * @returns {Promise<number>}
     */
    getRenderBuffer: function getRenderBuffer() {
      var _this2 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee7() {
        return _regeneratorRuntime.wrap(function _callee7$(_context7) {
          while (1) {
            switch (_context7.prev = _context7.next) {
              case 0:
                _context7.next = 2;
                return _this2.resolveLayer();

              case 2:
                return _context7.abrupt("return", _context7.sent.getRenderBuffer());

              case 3:
              case "end":
                return _context7.stop();
            }
          }
        }, _callee7);
      }))();
    },

    /**
     * @returns {Promise<function>}
     */
    getRenderOrder: function getRenderOrder() {
      var _this3 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee8() {
        return _regeneratorRuntime.wrap(function _callee8$(_context8) {
          while (1) {
            switch (_context8.prev = _context8.next) {
              case 0:
                _context8.next = 2;
                return _this3.resolveLayer();

              case 2:
                return _context8.abrupt("return", _this3.getRenderOrderInternal());

              case 3:
              case "end":
                return _context8.stop();
            }
          }
        }, _callee8);
      }))();
    },

    /**
     * @return {function}
     * @protected
     */
    getRenderOrderInternal: function getRenderOrderInternal() {
      return this.$layer.getRenderOrder();
    },

    /**
     * @param {function} renderOrder
     * @returns {Promise<void>}
     */
    setRenderOrder: function setRenderOrder(renderOrder) {
      var _this4 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee9() {
        return _regeneratorRuntime.wrap(function _callee9$(_context9) {
          while (1) {
            switch (_context9.prev = _context9.next) {
              case 0:
                _context9.t0 = renderOrder;
                _context9.next = 3;
                return _this4.getRenderOrder();

              case 3:
                _context9.t1 = _context9.sent;

                if (!(_context9.t0 === _context9.t1)) {
                  _context9.next = 6;
                  break;
                }

                return _context9.abrupt("return");

              case 6:
                _context9.next = 8;
                return _this4.resolveLayer();

              case 8:
                _context9.sent.setRenderOrder(renderOrder);

              case 9:
              case "end":
                return _context9.stop();
            }
          }
        }, _callee9);
      }))();
    },

    /**
     * @returns {Promise<boolean>}
     */
    getUpdateWhileAnimating: function getUpdateWhileAnimating() {
      var _this5 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee10() {
        return _regeneratorRuntime.wrap(function _callee10$(_context10) {
          while (1) {
            switch (_context10.prev = _context10.next) {
              case 0:
                _context10.next = 2;
                return _this5.resolveLayer();

              case 2:
                return _context10.abrupt("return", _context10.sent.getUpdateWhileAnimating());

              case 3:
              case "end":
                return _context10.stop();
            }
          }
        }, _callee10);
      }))();
    },

    /**
     * @returns {Promise<boolean>}
     */
    getUpdateWhileInteracting: function getUpdateWhileInteracting() {
      var _this6 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee11() {
        return _regeneratorRuntime.wrap(function _callee11$(_context11) {
          while (1) {
            switch (_context11.prev = _context11.next) {
              case 0:
                _context11.next = 2;
                return _this6.resolveLayer();

              case 2:
                return _context11.abrupt("return", _context11.sent.getUpdateWhileInteracting());

              case 3:
              case "end":
                return _context11.stop();
            }
          }
        }, _callee11);
      }))();
    }
  })
};

function subscribeToLayerEvents$3() {
  return _subscribeToLayerEvents$3.apply(this, arguments);
}

function _subscribeToLayerEvents$3() {
  _subscribeToLayerEvents$3 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee12() {
    var _this7 = this;

    var prefixKey, propChanges;
    return _regeneratorRuntime.wrap(function _callee12$(_context12) {
      while (1) {
        switch (_context12.prev = _context12.next) {
          case 0:
            prefixKey = addPrefix('current');
            propChanges = fromOlChangeEvent(this.$layer, ['renderOrder'], true, function (evt) {
              return _objectSpread$h(_objectSpread$h({}, evt), {}, {
                compareWith: _this7[prefixKey(evt.prop)]
              });
            }).pipe(skipWhile(function (_ref6) {
              var compareWith = _ref6.compareWith,
                  value = _ref6.value;
              return isEqual(value, compareWith);
            }));
            this.subscribeTo(propChanges, function () {
              ++_this7.rev;
            });

          case 3:
          case "end":
            return _context12.stop();
        }
      }
    }, _callee12, this);
  }));
  return _subscribeToLayerEvents$3.apply(this, arguments);
}

function ownKeys$i(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$i(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$i(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$i(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
var isNotEmptyString$1 = /*#__PURE__*/and(isString, negate(isEmpty));
/**
 * Basic vector source mixin.
 */

var vectorSource = {
  mixins: [featureHelper, source],
  stubVNode: {
    empty: false,
    attrs: function attrs() {
      return {
        id: this.vmId,
        class: this.vmClass
      };
    }
  },
  props: {
    // ol/source/Vector

    /**
     * @type {Object[]}
     */
    features: {
      type: Array,
      default: stubArray,
      validator: function validator(value) {
        return every(value, isGeoJSONFeature);
      }
    },

    /**
     * Source format factory
     * @type {function} formatFactory
     */
    formatFactory: {
      type: Function,
      default: getGeoJsonFmt
    },

    /**
     * Feature loader.
     * Feature loader should load features from some remote service, decode them and pas to `features` prop to render.
     * @type {module:ol/featureloader~FeatureLoader|undefined} loader
     */
    loader: Function,

    /**
     * @deprecated Use `loader` prop instead
     * @todo remove in v0.13.x
     */
    loaderFactory: Function,

    /**
     * Loading strategy factory.
     * Extent here in map view projection.
     * @type {function} strategyFactory
     */
    loadingStrategy: {
      type: Function // default: loadAll,

    },

    /**
     * @deprecated Use `loadingStrategy` prop instead
     * @todo remove in v0.13.x
     */
    strategyFactory: Function,

    /**
     * String or url factory
     * @type {string|function} url
     */
    url: {
      type: [String, Function],
      validator: function validator(value) {
        return isFunction(value) || isNotEmptyString$1(value);
      }
    },

    /**
     * @type {boolean}
     */
    overlaps: {
      type: Boolean,
      default: true
    },

    /**
     * @type {boolean}
     */
    useSpatialIndex: {
      type: Boolean,
      default: true
    }
  },
  data: function data() {
    return {
      /**
       * @returns {module:ol/format/Feature~FeatureFormat|null}
       */
      format: null
    };
  },
  computed: {
    featuresDataProj: function featuresDataProj() {
      return map$1(this.features, function (feature) {
        return initializeFeature(clonePlainObject(feature));
      });
    },
    featuresViewProj: function featuresViewProj() {
      var _this = this;

      return map$1(this.featuresDataProj, function (feature) {
        return _this.writeFeatureInViewProj(_this.readFeatureInDataProj(feature));
      });
    },
    currentFeaturesDataProj: function currentFeaturesDataProj() {
      var _this2 = this;

      if (!(this.rev && this.$source)) return [];
      return map$1(this.getFeaturesInternal(), function (feature) {
        return _this2.writeFeatureInDataProj(feature);
      });
    },
    currentFeaturesViewProj: function currentFeaturesViewProj() {
      var _this3 = this;

      if (!(this.rev && this.$source)) return [];
      return map$1(this.getFeaturesInternal(), function (feature) {
        return _this3.writeFeatureInViewProj(feature);
      });
    },
    currentExtentDataProj: function currentExtentDataProj() {
      if (!(this.rev && this.$source)) return;
      return this.getExtentInternal();
    },
    currentExtentViewProj: function currentExtentViewProj() {
      if (!(this.rev && this.$source)) return;
      return this.getExtentInternal(null, true);
    },
    urlFunc: function urlFunc() {
      return this.createUrlFunc(this.url);
    },
    loaderFunc: function loaderFunc() {
      var loader = this.loader;

      if (!loader && this.loaderFactory) {
        loader = this.loaderFactory();
      }

      return this.createLoaderFunc(loader);
    },
    formatIdent: function formatIdent() {
      if (!this.olObjIdent) return;
      return this.makeIdent(this.olObjIdent, 'format');
    },
    sealFormatFactory: function sealFormatFactory() {
      return sealFactory(this.formatFactory.bind(this));
    },
    loadingStrategyFunc: function loadingStrategyFunc() {
      var loadingStrategy = this.loadingStrategy;

      if (!loadingStrategy && this.strategyFactory) {
        loadingStrategy = this.strategyFactory();
      }

      return loadingStrategy || all;
    }
  },
  watch: _objectSpread$i({
    featuresViewProj: {
      deep: true,
      handler: /*#__PURE__*/sequential( /*#__PURE__*/function () {
        var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(features) {
          var p;
          return _regeneratorRuntime.wrap(function _callee$(_context) {
            while (1) {
              switch (_context.prev = _context.next) {
                case 0:
                  if (!isEqual(features, this.currentFeaturesViewProj)) {
                    _context.next = 2;
                    break;
                  }

                  return _context.abrupt("return");

                case 2:
                  // add new features
                  p = this.resolvedDataProjection;
                  _context.next = 5;
                  return this.addFeatures(features, true);

                case 5:
                  console.log('done', p); // remove non-matched features

                  _context.next = 8;
                  return this.removeFeatures(difference(this.currentFeaturesViewProj, features, function (a, b) {
                    return getFeatureId(a) === getFeatureId(b);
                  }));

                case 8:
                case "end":
                  return _context.stop();
              }
            }
          }, _callee, this);
        }));

        return function (_x) {
          return _ref.apply(this, arguments);
        };
      }())
    },
    currentFeaturesDataProj: {
      deep: true,
      handler: /*#__PURE__*/debounce(function (value) {
        if (isEqual(value, this.featuresDataProj)) return;
        this.$emit('update:features', clonePlainObject(value));
      }, FRAME_TIME)
    },
    urlFunc: /*#__PURE__*/sequential( /*#__PURE__*/function () {
      var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(value) {
        return _regeneratorRuntime.wrap(function _callee2$(_context2) {
          while (1) {
            switch (_context2.prev = _context2.next) {
              case 0:
                _context2.next = 2;
                return this.setUrlInternal(value);

              case 2:
              case "end":
                return _context2.stop();
            }
          }
        }, _callee2, this);
      }));

      return function (_x2) {
        return _ref2.apply(this, arguments);
      };
    }()),
    loaderFunc: /*#__PURE__*/sequential( /*#__PURE__*/function () {
      var _ref3 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3(value) {
        return _regeneratorRuntime.wrap(function _callee3$(_context3) {
          while (1) {
            switch (_context3.prev = _context3.next) {
              case 0:
                _context3.next = 2;
                return this.setLoaderInternal(value);

              case 2:
              case "end":
                return _context3.stop();
            }
          }
        }, _callee3, this);
      }));

      return function (_x3) {
        return _ref3.apply(this, arguments);
      };
    }()),
    formatIdent: /*#__PURE__*/sequential(function (value, prevValue) {
      if (value && prevValue) {
        this.moveInstance(value, prevValue);
      } else if (value && !prevValue && this.format) {
        this.setInstance(value, this.format);
      } else if (!value && prevValue) {
        this.unsetInstance(prevValue);
      }
    }),
    sealFormatFactory: /*#__PURE__*/sequential( /*#__PURE__*/function () {
      var _ref4 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee4(value) {
        return _regeneratorRuntime.wrap(function _callee4$(_context4) {
          while (1) {
            switch (_context4.prev = _context4.next) {
              case 0:
                while (this.hasInstance(this.formatIdent)) {
                  this.unsetInstance(this.formatIdent);
                }

                if (isFunction(value)) {
                  this.format = this.instanceFactoryCall(this.formatIdent, value.bind(this));
                } else {
                  this.format = undefined;
                }

                if (process.env.VUELAYERS_DEBUG) {
                  this.$logger.log('sealDataFormatFactory changed, scheduling recreate...');
                }

                _context4.next = 5;
                return this.scheduleRecreate();

              case 5:
              case "end":
                return _context4.stop();
            }
          }
        }, _callee4, this);
      }));

      return function (_x4) {
        return _ref4.apply(this, arguments);
      };
    }()),
    overlaps: /*#__PURE__*/sequential( /*#__PURE__*/function () {
      var _ref5 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee5(value) {
        return _regeneratorRuntime.wrap(function _callee5$(_context5) {
          while (1) {
            switch (_context5.prev = _context5.next) {
              case 0:
                _context5.t0 = value;
                _context5.next = 3;
                return this.getOverlaps();

              case 3:
                _context5.t1 = _context5.sent;

                if (!(_context5.t0 === _context5.t1)) {
                  _context5.next = 6;
                  break;
                }

                return _context5.abrupt("return");

              case 6:
                if (process.env.VUELAYERS_DEBUG) {
                  this.$logger.log('overlaps changed, scheduling recreate...');
                }

                _context5.next = 9;
                return this.scheduleRecreate();

              case 9:
              case "end":
                return _context5.stop();
            }
          }
        }, _callee5, this);
      }));

      return function (_x5) {
        return _ref5.apply(this, arguments);
      };
    }())
  }, /*#__PURE__*/makeWatchers(['loadingStrategyFunc', 'useSpatialIndex'], function (prop) {
    return /*#__PURE__*/sequential( /*#__PURE__*/function () {
      var _ref6 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee6(val, prev) {
        return _regeneratorRuntime.wrap(function _callee6$(_context6) {
          while (1) {
            switch (_context6.prev = _context6.next) {
              case 0:
                if (!isEqual(val, prev)) {
                  _context6.next = 2;
                  break;
                }

                return _context6.abrupt("return");

              case 2:
                if (process.env.VUELAYERS_DEBUG) {
                  this.$logger.log("".concat(prop, " changed, scheduling recreate..."));
                }

                _context6.next = 5;
                return this.scheduleRecreate();

              case 5:
              case "end":
                return _context6.stop();
            }
          }
        }, _callee6, this);
      }));

      return function (_x6, _x7) {
        return _ref6.apply(this, arguments);
      };
    }());
  })),
  created: function created() {
    if (process.env.NODE_ENV !== 'production') {
      if (this.loaderFactory) {
        this.$logger.warn("'loaderFactory' prop is deprecated. Use 'loader' prop instead.");
      }

      if (this.strategyFactory) {
        this.$logger.warn("'strategyFactory' prop is deprecated. Use 'loadingStrategy' prop instead.");
      }
    }

    if (isFunction(this.sealFormatFactory)) {
      this.format = this.instanceFactoryCall(this.formatIdent, this.sealFormatFactory.bind(this)); // this.$watch('format', async () => {
      //   if (process.env.VUELAYERS_DEBUG) {
      //     this.$logger.log('format changed, scheduling recreate...')
      //   }
      //
      //   await this.scheduleRecreate()
      // })
    }
  },
  updated: function updated() {
    if (process.env.NODE_ENV !== 'production') {
      if (this.loaderFactory) {
        this.$logger.warn("'loaderFactory' prop is deprecated. Use 'loader' prop instead.");
      }

      if (this.strategyFactory) {
        this.$logger.warn("'strategyFactory' prop is deprecated. Use 'loadingStrategy' prop instead.");
      }
    }
  },
  methods: _objectSpread$i(_objectSpread$i({
    getServices: function getServices() {
      var vm = this;
      return mergeDescriptors(source.methods.getServices.call(this), {
        get featuresContainer() {
          return vm;
        }

      });
    },
    subscribeAll: function subscribeAll() {
      source.methods.subscribeAll.call(this);
      subscribeToSourceEvents$3.call(this);
    }
  }, /*#__PURE__*/pick(source.methods, ['beforeInit', 'init', 'deinit', 'beforeMount', 'unmount', 'scheduleRefresh', 'remount', 'scheduleRemount', 'recreate', 'scheduleRecreate', 'resolveOlObject', 'resolveSource'])), {}, {
    addFeatures: function addFeatures(features) {
      var _arguments = arguments,
          _this4 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee8() {
        var viewProj, newFeatures;
        return _regeneratorRuntime.wrap(function _callee8$(_context8) {
          while (1) {
            switch (_context8.prev = _context8.next) {
              case 0:
                viewProj = _arguments.length > 1 && _arguments[1] !== undefined ? _arguments[1] : false;
                newFeatures = [];
                _context8.next = 4;
                return Promise.all(map$1(features || [], /*#__PURE__*/function () {
                  var _ref7 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee7(feature) {
                    var foundFeature;
                    return _regeneratorRuntime.wrap(function _callee7$(_context7) {
                      while (1) {
                        switch (_context7.prev = _context7.next) {
                          case 0:
                            _context7.next = 2;
                            return _this4.initializeFeature(feature, viewProj);

                          case 2:
                            feature = _context7.sent;
                            instanceOf(feature, Feature);
                            _context7.next = 6;
                            return _this4.getFeatureById(getFeatureId(feature));

                          case 6:
                            foundFeature = _context7.sent;

                            if (foundFeature) {
                              _this4.updateFeature(foundFeature, feature);
                            } else {
                              newFeatures.push(feature);
                            }

                          case 8:
                          case "end":
                            return _context7.stop();
                        }
                      }
                    }, _callee7);
                  }));

                  return function (_x8) {
                    return _ref7.apply(this, arguments);
                  };
                }()));

              case 4:
                _context8.next = 6;
                return _this4.resolveSource();

              case 6:
                _context8.sent.addFeatures(newFeatures);

              case 7:
              case "end":
                return _context8.stop();
            }
          }
        }, _callee8);
      }))();
    },
    addFeature: function addFeature(feature) {
      var _arguments2 = arguments,
          _this5 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee9() {
        var viewProj;
        return _regeneratorRuntime.wrap(function _callee9$(_context9) {
          while (1) {
            switch (_context9.prev = _context9.next) {
              case 0:
                viewProj = _arguments2.length > 1 && _arguments2[1] !== undefined ? _arguments2[1] : false;
                _context9.next = 3;
                return _this5.addFeatures([feature], viewProj);

              case 3:
              case "end":
                return _context9.stop();
            }
          }
        }, _callee9);
      }))();
    },
    removeFeatures: function removeFeatures(features) {
      var _this6 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee10() {
        return _regeneratorRuntime.wrap(function _callee10$(_context10) {
          while (1) {
            switch (_context10.prev = _context10.next) {
              case 0:
                _context10.next = 2;
                return Promise.all(map$1(features, _this6.removeFeature.bind(_this6)));

              case 2:
              case "end":
                return _context10.stop();
            }
          }
        }, _callee10);
      }))();
    },
    removeFeature: function removeFeature(feature) {
      var _this7 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee11() {
        return _regeneratorRuntime.wrap(function _callee11$(_context11) {
          while (1) {
            switch (_context11.prev = _context11.next) {
              case 0:
                if (!isFunction(feature.resolveOlObject)) {
                  _context11.next = 4;
                  break;
                }

                _context11.next = 3;
                return feature.resolveOlObject();

              case 3:
                feature = _context11.sent;

              case 4:
                _context11.next = 6;
                return _this7.getFeatureById(getFeatureId(feature));

              case 6:
                feature = _context11.sent;

                if (feature) {
                  _context11.next = 9;
                  break;
                }

                return _context11.abrupt("return");

              case 9:
                _context11.next = 11;
                return _this7.resolveSource();

              case 11:
                _context11.sent.removeFeature(feature);

              case 12:
              case "end":
                return _context11.stop();
            }
          }
        }, _callee11);
      }))();
    },
    getFeatureById: function getFeatureById(featureId) {
      var _this8 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee12() {
        return _regeneratorRuntime.wrap(function _callee12$(_context12) {
          while (1) {
            switch (_context12.prev = _context12.next) {
              case 0:
                _context12.next = 2;
                return _this8.resolveSource();

              case 2:
                return _context12.abrupt("return", _context12.sent.getFeatureById(featureId));

              case 3:
              case "end":
                return _context12.stop();
            }
          }
        }, _callee12);
      }))();
    },
    getFeatures: function getFeatures() {
      var _this9 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee13() {
        return _regeneratorRuntime.wrap(function _callee13$(_context13) {
          while (1) {
            switch (_context13.prev = _context13.next) {
              case 0:
                _context13.next = 2;
                return _this9.resolveSource();

              case 2:
                return _context13.abrupt("return", _this9.getFeaturesInternal());

              case 3:
              case "end":
                return _context13.stop();
            }
          }
        }, _callee13);
      }))();
    },
    getFeaturesInternal: function getFeaturesInternal() {
      return this.$source.getFeatures();
    },
    getFeaturesCollection: function getFeaturesCollection() {
      var _this10 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee14() {
        return _regeneratorRuntime.wrap(function _callee14$(_context14) {
          while (1) {
            switch (_context14.prev = _context14.next) {
              case 0:
                _context14.next = 2;
                return _this10.resolveSource();

              case 2:
                return _context14.abrupt("return", _this10.getFeaturesCollectionInternal());

              case 3:
              case "end":
                return _context14.stop();
            }
          }
        }, _callee14);
      }))();
    },
    getFeaturesCollectionInternal: function getFeaturesCollectionInternal() {
      return this.$source.getFeaturesCollection();
    },
    clear: function clear() {
      var _this11 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee15() {
        var source;
        return _regeneratorRuntime.wrap(function _callee15$(_context15) {
          while (1) {
            switch (_context15.prev = _context15.next) {
              case 0:
                _context15.next = 2;
                return _this11.resolveSource();

              case 2:
                source = _context15.sent;
                return _context15.abrupt("return", new Promise(function (resolve) {
                  source.once('change', function () {
                    return resolve();
                  });
                  source.clear();
                }));

              case 4:
              case "end":
                return _context15.stop();
            }
          }
        }, _callee15);
      }))();
    },
    isEmpty: function isEmpty() {
      var _this12 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee16() {
        return _regeneratorRuntime.wrap(function _callee16$(_context16) {
          while (1) {
            switch (_context16.prev = _context16.next) {
              case 0:
                _context16.next = 2;
                return _this12.resolveSource();

              case 2:
                return _context16.abrupt("return", _context16.sent.isEmpty());

              case 3:
              case "end":
                return _context16.stop();
            }
          }
        }, _callee16);
      }))();
    },

    /**
     * @param {function} callback
     * @returns {Promise<*>}
     */
    forEachFeature: function forEachFeature(callback) {
      var _this13 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee17() {
        return _regeneratorRuntime.wrap(function _callee17$(_context17) {
          while (1) {
            switch (_context17.prev = _context17.next) {
              case 0:
                _context17.next = 2;
                return _this13.resolveSource();

              case 2:
                return _context17.abrupt("return", _context17.sent.forEachFeature(callback));

              case 3:
              case "end":
                return _context17.stop();
            }
          }
        }, _callee17);
      }))();
    },

    /**
     * @param {number[]} extent
     * @param {function} callback
     * @param {boolean} [viewProj=false]
     * @returns {Promise<*>}
     */
    forEachFeatureInExtent: function forEachFeatureInExtent(extent, callback) {
      var _arguments3 = arguments,
          _this14 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee18() {
        var viewProj;
        return _regeneratorRuntime.wrap(function _callee18$(_context18) {
          while (1) {
            switch (_context18.prev = _context18.next) {
              case 0:
                viewProj = _arguments3.length > 2 && _arguments3[2] !== undefined ? _arguments3[2] : false;

                if (!viewProj) {
                  extent = _this14.extentToViewProj(extent);
                }

                _context18.next = 4;
                return _this14.resolveSource();

              case 4:
                return _context18.abrupt("return", _context18.sent.forEachFeatureInExtent(extent, callback));

              case 5:
              case "end":
                return _context18.stop();
            }
          }
        }, _callee18);
      }))();
    },

    /**
     * @param {number[]} extent
     * @param {function} callback
     * @param {boolean} [viewProj=false]
     * @returns {Promise<*>}
     */
    forEachFeatureIntersectingExtent: function forEachFeatureIntersectingExtent(extent, callback) {
      var _arguments4 = arguments,
          _this15 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee19() {
        var viewProj;
        return _regeneratorRuntime.wrap(function _callee19$(_context19) {
          while (1) {
            switch (_context19.prev = _context19.next) {
              case 0:
                viewProj = _arguments4.length > 2 && _arguments4[2] !== undefined ? _arguments4[2] : false;

                if (!viewProj) {
                  extent = _this15.extentToViewProj(extent);
                }

                _context19.next = 4;
                return _this15.resolveSource();

              case 4:
                return _context19.abrupt("return", _context19.sent.forEachFeatureIntersectingExtent(extent, callback));

              case 5:
              case "end":
                return _context19.stop();
            }
          }
        }, _callee19);
      }))();
    },

    /**
     * @param {number[]} coordinate
     * @param {boolean} [viewProj=false]
     * @returns {Promise<Array<module:ol/Feature~Feature>>}
     */
    getFeaturesAtCoordinate: function getFeaturesAtCoordinate(coordinate) {
      var _arguments5 = arguments,
          _this16 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee20() {
        var viewProj;
        return _regeneratorRuntime.wrap(function _callee20$(_context20) {
          while (1) {
            switch (_context20.prev = _context20.next) {
              case 0:
                viewProj = _arguments5.length > 1 && _arguments5[1] !== undefined ? _arguments5[1] : false;

                if (!viewProj) {
                  coordinate = _this16.pointToViewProj(coordinate);
                }

                _context20.next = 4;
                return _this16.resolveSource();

              case 4:
                return _context20.abrupt("return", _context20.sent.getFeaturesAtCoordinate(coordinate));

              case 5:
              case "end":
                return _context20.stop();
            }
          }
        }, _callee20);
      }))();
    },

    /**
     * @param {number[]} extent
     * @param {boolean} [viewProj=false]
     * @returns {Promise<Array<module:ol/Feature~Feature>>}
     */
    getFeaturesInExtent: function getFeaturesInExtent(extent) {
      var _arguments6 = arguments,
          _this17 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee21() {
        var viewProj;
        return _regeneratorRuntime.wrap(function _callee21$(_context21) {
          while (1) {
            switch (_context21.prev = _context21.next) {
              case 0:
                viewProj = _arguments6.length > 1 && _arguments6[1] !== undefined ? _arguments6[1] : false;

                if (!viewProj) {
                  extent = _this17.extentToViewProj(extent);
                }

                _context21.next = 4;
                return _this17.resolveSource();

              case 4:
                return _context21.abrupt("return", _context21.sent.getFeaturesInExtent(extent));

              case 5:
              case "end":
                return _context21.stop();
            }
          }
        }, _callee21);
      }))();
    },

    /**
     * @param {number[]} coordinate
     * @param {function} [filter]
     * @param {boolean} [viewProj=false]
     * @returns {Promise<module:ol/Feature~Feature>}
     */
    getClosestFeatureToCoordinate: function getClosestFeatureToCoordinate(coordinate, filter) {
      var _arguments7 = arguments,
          _this18 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee22() {
        var viewProj;
        return _regeneratorRuntime.wrap(function _callee22$(_context22) {
          while (1) {
            switch (_context22.prev = _context22.next) {
              case 0:
                viewProj = _arguments7.length > 2 && _arguments7[2] !== undefined ? _arguments7[2] : false;

                if (!viewProj) {
                  coordinate = _this18.pointToViewProj(coordinate);
                }

                _context22.next = 4;
                return _this18.resolveSource();

              case 4:
                return _context22.abrupt("return", _context22.sent.getClosestFeatureToCoordinate(coordinate, filter));

              case 5:
              case "end":
                return _context22.stop();
            }
          }
        }, _callee22);
      }))();
    },

    /**
     * @param {number[]} [extent]
     * @param {boolean} [viewProj=false]
     * @returns {Promise<number[]>}
     */
    getExtent: function getExtent(extent) {
      var _arguments8 = arguments,
          _this19 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee23() {
        var viewProj;
        return _regeneratorRuntime.wrap(function _callee23$(_context23) {
          while (1) {
            switch (_context23.prev = _context23.next) {
              case 0:
                viewProj = _arguments8.length > 1 && _arguments8[1] !== undefined ? _arguments8[1] : false;
                _context23.next = 3;
                return _this19.resolveSource();

              case 3:
                return _context23.abrupt("return", _this19.getExtentInternal(extent, viewProj));

              case 4:
              case "end":
                return _context23.stop();
            }
          }
        }, _callee23);
      }))();
    },
    getExtentInternal: function getExtentInternal(extent) {
      var viewProj = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;

      if (viewProj) {
        return this.$source.getExtent(extent);
      }

      return this.extentToDataProj(this.$source.getExtent(this.extentToViewProj(extent)));
    },

    /**
     * @returns {module:ol/format/Feature~FeatureFormat|undefined}
     */
    getFormat: function getFormat() {
      return this.format;
    },

    /**
     * @returns {Promise<boolean>}
     */
    getOverlaps: function getOverlaps() {
      var _this20 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee24() {
        return _regeneratorRuntime.wrap(function _callee24$(_context24) {
          while (1) {
            switch (_context24.prev = _context24.next) {
              case 0:
                _context24.next = 2;
                return _this20.resolveSource();

              case 2:
                return _context24.abrupt("return", _context24.sent.getOverlaps());

              case 3:
              case "end":
                return _context24.stop();
            }
          }
        }, _callee24);
      }))();
    },

    /**
     * @returns {Promise<string|function|undefined>}
     */
    getUrl: function getUrl() {
      var _this21 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee25() {
        return _regeneratorRuntime.wrap(function _callee25$(_context25) {
          while (1) {
            switch (_context25.prev = _context25.next) {
              case 0:
                _context25.next = 2;
                return _this21.resolveSource();

              case 2:
                return _context25.abrupt("return", _context25.sent.getUrl());

              case 3:
              case "end":
                return _context25.stop();
            }
          }
        }, _callee25);
      }))();
    },

    /**
     * @param {string|function} url
     * @returns {Promise<void>}
     */
    setUrl: function setUrl(url) {
      var _this22 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee26() {
        return _regeneratorRuntime.wrap(function _callee26$(_context26) {
          while (1) {
            switch (_context26.prev = _context26.next) {
              case 0:
                _context26.next = 2;
                return _this22.setUrlInternal(_this22.createUrlFunc(url));

              case 2:
              case "end":
                return _context26.stop();
            }
          }
        }, _callee26);
      }))();
    },

    /**
     * @param {string|function} url
     * @returns {Promise<void>}
     * @protected
     */
    setUrlInternal: function setUrlInternal(url) {
      var _this23 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee27() {
        return _regeneratorRuntime.wrap(function _callee27$(_context27) {
          while (1) {
            switch (_context27.prev = _context27.next) {
              case 0:
                _context27.next = 2;
                return _this23.resolveSource();

              case 2:
                _context27.sent.setUrl(url);

                _context27.next = 5;
                return _this23.refresh();

              case 5:
              case "end":
                return _context27.stop();
            }
          }
        }, _callee27);
      }))();
    },

    /**
     * @param {function} loader
     * @returns {Promise<void>}
     */
    setLoader: function setLoader(loader) {
      var _this24 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee28() {
        return _regeneratorRuntime.wrap(function _callee28$(_context28) {
          while (1) {
            switch (_context28.prev = _context28.next) {
              case 0:
                _context28.next = 2;
                return _this24.setLoaderInternal(_this24.wrapLoaderFunc(loader));

              case 2:
              case "end":
                return _context28.stop();
            }
          }
        }, _callee28);
      }))();
    },

    /**
     * @param {function} loader
     * @returns {Promise<void>}
     * @protected
     */
    setLoaderInternal: function setLoaderInternal(loader) {
      var _this25 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee29() {
        return _regeneratorRuntime.wrap(function _callee29$(_context29) {
          while (1) {
            switch (_context29.prev = _context29.next) {
              case 0:
                _context29.next = 2;
                return _this25.resolveSource();

              case 2:
                _context29.sent.setLoader(loader);

                _context29.next = 5;
                return _this25.refresh();

              case 5:
              case "end":
                return _context29.stop();
            }
          }
        }, _callee29);
      }))();
    },

    /**
     * @param {number[]} extent
     * @param {boolean} [viewProj=false]
     * @returns {Promise<void>}
     */
    removeLoadedExtent: function removeLoadedExtent(extent) {
      var _arguments9 = arguments,
          _this26 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee30() {
        var viewProj;
        return _regeneratorRuntime.wrap(function _callee30$(_context30) {
          while (1) {
            switch (_context30.prev = _context30.next) {
              case 0:
                viewProj = _arguments9.length > 1 && _arguments9[1] !== undefined ? _arguments9[1] : false;

                if (!viewProj) {
                  extent = _this26.extentToViewProj(extent);
                }

                _context30.next = 4;
                return _this26.resolveSource();

              case 4:
                _context30.sent.removeLoadedExtent(extent);

              case 5:
              case "end":
                return _context30.stop();
            }
          }
        }, _callee30);
      }))();
    },

    /**
     * @param {string|function|undefined} url
     * @return {function|undefined}
     * @protected
     */
    createUrlFunc: function createUrlFunc(url) {
      if (!url) return;

      if (!isFunction(url)) {
        url = constant(url);
      }

      return this.wrapUrlFunc(url);
    },

    /**
     * @param {function} urlFunc
     * @returns {function}
     * @protected
     */
    wrapUrlFunc: function wrapUrlFunc(urlFunc) {
      var _this27 = this;

      return function (extent, resolution, projection) {
        extent = transformExtent(extent, projection, _this27.resolvedDataProjection);
        projection = _this27.resolvedDataProjection;
        return urlFunc(extent, resolution, projection);
      };
    },

    /**
     * @param {function|undefined} loader
     * @return {function|undefined}
     * @protected
     */
    createLoaderFunc: function createLoaderFunc(loader) {
      if (!isFunction(loader)) return;
      return this.wrapLoaderFunc(loader);
    },

    /**
     * @param {function} loaderFunc
     * @returns {Function}
     * @protected
     */
    wrapLoaderFunc: function wrapLoaderFunc(loaderFunc) {
      var _this28 = this;

      return /*#__PURE__*/function () {
        var _ref8 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee31(extent, resolution, projection) {
          var features;
          return _regeneratorRuntime.wrap(function _callee31$(_context31) {
            while (1) {
              switch (_context31.prev = _context31.next) {
                case 0:
                  _context31.next = 2;
                  return loaderFunc(transformExtent(extent, projection, _this28.resolvedDataProjection), resolution, _this28.resolvedDataProjection);

                case 2:
                  features = _context31.sent;

                  if (!(isArray(features) && features[0] instanceof Feature)) {
                    features = _this28.readSource(features);
                  }

                  if (!isArray(features)) {
                    _context31.next = 7;
                    break;
                  }

                  _context31.next = 7;
                  return _this28.addFeatures(features);

                case 7:
                case "end":
                  return _context31.stop();
              }
            }
          }, _callee31);
        }));

        return function (_x9, _x10, _x11) {
          return _ref8.apply(this, arguments);
        };
      }();
    },

    /**
     * @param {*} data
     * @returns {Array<module:ol/Feature~Feature>}
     */
    readSource: function readSource(data) {
      return this.format.readFeatures(data, {
        featureProjection: this.resolvedViewProjection,
        dataProjection: this.resolvedDataProjection
      });
    }
  })
};

function subscribeToSourceEvents$3() {
  var _this29 = this;

  var vm = this;
  var adds = fromOlEvent(this.$source, VectorEventType.ADDFEATURE).pipe(mergeMap(function (_ref9) {
    var type = _ref9.type,
        feature = _ref9.feature;
    return from(_this29.initializeFeature(feature)).pipe(map(function (feature) {
      return {
        type: type,
        feature: feature,
        oldType: 'add:feature'
      };
    }));
  }));
  var removes = fromOlEvent(this.$source, VectorEventType.REMOVEFEATURE).pipe(map(function (evt) {
    return _objectSpread$i(_objectSpread$i({}, evt), {}, {
      oldType: 'remove:feature'
    });
  }));
  var events = merge(adds, removes).pipe(map(function (_ref10) {
    var type = _ref10.type,
        feature = _ref10.feature,
        oldType = _ref10.oldType;
    return {
      type: type,
      feature: feature,
      oldType: oldType,

      get json() {
        if (!this._json) {
          this._json = vm.writeFeatureInDataProj(this.feature);
        }

        return this._json;
      }

    };
  }), bufferDebounceTime(FRAME_TIME));
  this.subscribeTo(events, function (events) {
    _this29.$nextTick(function () {
      forEach(events, function (evt) {
        var oldType = evt.oldType;
        delete evt.oldType;

        _this29.$emit(evt.type, evt); // todo remove in v0.13.x


        _this29.$emit(oldType, evt.feature);
      });
    });
  });
}

function transformExtent(extent, sourceProj, destProj) {
  extent = extent.slice();

  if (isFinite(extent[0]) && isFinite(extent[1])) {
    var _transform = transform([extent[0], extent[1]], sourceProj, destProj);

    var _transform2 = _slicedToArray(_transform, 2);

    extent[0] = _transform2[0];
    extent[1] = _transform2[1];
  }

  if (isFinite(extent[2]) && isFinite(extent[3])) {
    var _transform3 = transform([extent[2], extent[3]], sourceProj, destProj);

    var _transform4 = _slicedToArray(_transform3, 2);

    extent[2] = _transform4[0];
    extent[3] = _transform4[1];
  }

  return extent;
}

function ownKeys$j(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$j(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$j(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$j(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
/**
 * Basic WMS params and methods mixin.
 */

var wmsSource = {
  props: {
    /**
     * @type {boolean}
     */
    hidpi: {
      type: Boolean,
      default: true
    },

    /**
     * @type {string|undefined}
     */
    serverType: {
      type: String,
      validator: function validator(value) {
        return Object.values(WMSServerType).includes(value);
      }
    },

    /**
     * @type {string|string[]}
     */
    layers: {
      type: String,
      required: true
    },

    /**
     * WMS Request styles
     * @type {string|string[]|undefined}
     */
    styles: String,

    /**
     * @type {string}
     */
    version: {
      type: String,
      default: '1.3.0'
    },

    /**
     * @type {boolean}
     */
    transparent: {
      type: Boolean,
      default: true
    },

    /**
     * @type {string}
     */
    format: {
      type: String,
      default: 'image/png'
    },

    /**
     * @type {string|undefined}
     */
    bgColor: String,

    /**
     * @type {string|undefined}
     */
    time: String,

    /**
     * Additional WMS request parameters
     * @type {Object|undefined}
     */
    params: Object
  },
  computed: {
    /**
     * @returns {string}
     */
    layersStr: function layersStr() {
      return isArray(this.layers) ? this.layers.join(',') : this.layers;
    },

    /**
     * @returns {string|undefined}
     */
    stylesStr: function stylesStr() {
      return isArray(this.styles) ? this.styles.join(',') : this.styles;
    },

    /**
     * @returns {Object|null}
     */
    customParams: function customParams() {
      return this.params ? cleanSourceParams(this.params, ['LAYERS', 'VERSION', 'STYLES', 'FORMAT', 'TRANSPARENT', 'BGCOLOR', 'TIME']) : null;
    },

    /**
     * @returns {Object}
     */
    allParams: function allParams() {
      return _objectSpread$j(_objectSpread$j({}, this.customParams || {}), {}, {
        LAYERS: this.layersStr,
        STYLES: this.stylesStr,
        VERSION: this.version,
        FORMAT: this.format,
        TRANSPARENT: this.transparent,
        BGCOLOR: this.bgColor,
        TIME: this.time
      });
    }
  },
  watch: _objectSpread$j(_objectSpread$j({
    allParams: /*#__PURE__*/sequential( /*#__PURE__*/function () {
      var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(value) {
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                _context.next = 2;
                return this.updateParams(value);

              case 2:
              case "end":
                return _context.stop();
            }
          }
        }, _callee, this);
      }));

      return function (_x) {
        return _ref.apply(this, arguments);
      };
    }()),
    layersStr: /*#__PURE__*/sequential( /*#__PURE__*/function () {
      var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(value) {
        return _regeneratorRuntime.wrap(function _callee2$(_context2) {
          while (1) {
            switch (_context2.prev = _context2.next) {
              case 0:
                _context2.next = 2;
                return this.updateParam('layers', value);

              case 2:
              case "end":
                return _context2.stop();
            }
          }
        }, _callee2, this);
      }));

      return function (_x2) {
        return _ref2.apply(this, arguments);
      };
    }()),
    stylesStr: /*#__PURE__*/sequential( /*#__PURE__*/function () {
      var _ref3 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3(value) {
        return _regeneratorRuntime.wrap(function _callee3$(_context3) {
          while (1) {
            switch (_context3.prev = _context3.next) {
              case 0:
                _context3.next = 2;
                return this.updateParam('styles', value);

              case 2:
              case "end":
                return _context3.stop();
            }
          }
        }, _callee3, this);
      }));

      return function (_x3) {
        return _ref3.apply(this, arguments);
      };
    }())
  }, /*#__PURE__*/makeWatchers(['version', 'transparent', 'format', 'bgColor', 'time'], function (prop) {
    return /*#__PURE__*/sequential( /*#__PURE__*/function () {
      var _ref4 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee4(value) {
        return _regeneratorRuntime.wrap(function _callee4$(_context4) {
          while (1) {
            switch (_context4.prev = _context4.next) {
              case 0:
                _context4.next = 2;
                return this.updateParam(prop, value);

              case 2:
              case "end":
                return _context4.stop();
            }
          }
        }, _callee4, this);
      }));

      return function (_x4) {
        return _ref4.apply(this, arguments);
      };
    }());
  })), /*#__PURE__*/makeWatchers(['hidpi', 'serverType'], function (prop) {
    return /*#__PURE__*/sequential( /*#__PURE__*/function () {
      var _ref5 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee5(val, prev) {
        return _regeneratorRuntime.wrap(function _callee5$(_context5) {
          while (1) {
            switch (_context5.prev = _context5.next) {
              case 0:
                if (!isEqual(val, prev)) {
                  _context5.next = 2;
                  break;
                }

                return _context5.abrupt("return");

              case 2:
                if (process.env.VUELAYERS_DEBUG) {
                  this.$logger.log("".concat(prop, " changed, scheduling recreate..."));
                }

                _context5.next = 5;
                return this.scheduleRecreate();

              case 5:
              case "end":
                return _context5.stop();
            }
          }
        }, _callee5, this);
      }));

      return function (_x5, _x6) {
        return _ref5.apply(this, arguments);
      };
    }());
  })),
  methods: {
    /**
     * @param {number[]} coordinate
     * @param {number} [resolution]
     * @param {string} [projection]
     * @param {Object|undefined} [params] GetFeatureInfo params. `info_format` at least should be provided.
     *                          If `query_layers` is not provided then the layers specified in the `layers` prop will be used.
     *                          `version` should not be specified here (value from `version` prop will be used).
     * @return {Promise<string|undefined>}
     */
    getFeatureInfoUrl: function getFeatureInfoUrl(coordinate, resolution, projection) {
      var _arguments = arguments,
          _this = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee6() {
        var params;
        return _regeneratorRuntime.wrap(function _callee6$(_context6) {
          while (1) {
            switch (_context6.prev = _context6.next) {
              case 0:
                params = _arguments.length > 3 && _arguments[3] !== undefined ? _arguments[3] : {};
                _context6.t0 = resolution;

                if (_context6.t0) {
                  _context6.next = 6;
                  break;
                }

                _context6.next = 5;
                return _this.$viewVm.getResolution();

              case 5:
                resolution = _context6.sent;

              case 6:
                projection || (projection = _this.projection);
                _context6.next = 9;
                return _this.resolveSource();

              case 9:
                return _context6.abrupt("return", _context6.sent.getFeatureInfoUrl(coordinate, resolution, projection, params));

              case 10:
              case "end":
                return _context6.stop();
            }
          }
        }, _callee6);
      }))();
    },

    /**
     * @param {number} resolution
     * @param {Object|undefined} [params]
     * @return {Promise<string|undefined>}
     */
    getLegendUrl: function getLegendUrl(resolution) {
      var _arguments2 = arguments,
          _this2 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee7() {
        var params;
        return _regeneratorRuntime.wrap(function _callee7$(_context7) {
          while (1) {
            switch (_context7.prev = _context7.next) {
              case 0:
                params = _arguments2.length > 1 && _arguments2[1] !== undefined ? _arguments2[1] : {};
                _context7.next = 3;
                return _this2.resolveSource();

              case 3:
                return _context7.abrupt("return", _context7.sent.getLegendUrl(resolution, params));

              case 4:
              case "end":
                return _context7.stop();
            }
          }
        }, _callee7);
      }))();
    },

    /**
     * @returns {Promise<Object>}
     */
    getParams: function getParams() {
      var _this3 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee8() {
        return _regeneratorRuntime.wrap(function _callee8$(_context8) {
          while (1) {
            switch (_context8.prev = _context8.next) {
              case 0:
                _context8.next = 2;
                return _this3.resolveSource();

              case 2:
                return _context8.abrupt("return", _context8.sent.getParams());

              case 3:
              case "end":
                return _context8.stop();
            }
          }
        }, _callee8);
      }))();
    },

    /**
     * @param {Object} params
     * @returns {Promise<void>}
     */
    updateParams: function updateParams(params) {
      var _this4 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee9() {
        return _regeneratorRuntime.wrap(function _callee9$(_context9) {
          while (1) {
            switch (_context9.prev = _context9.next) {
              case 0:
                params = _objectSpread$j(_objectSpread$j({}, _this4.allParams), params);
                _context9.t0 = isEqual;
                _context9.t1 = params;
                _context9.next = 5;
                return _this4.getParams();

              case 5:
                _context9.t2 = _context9.sent;

                if (!(0, _context9.t0)(_context9.t1, _context9.t2)) {
                  _context9.next = 8;
                  break;
                }

                return _context9.abrupt("return");

              case 8:
                _context9.next = 10;
                return _this4.resolveSource();

              case 10:
                _context9.sent.updateParams(params);

              case 11:
              case "end":
                return _context9.stop();
            }
          }
        }, _callee9);
      }))();
    },

    /**
     * @param {string} param
     * @param {*} value
     * @returns {Promise<void>}
     */
    updateParam: function updateParam(param, value) {
      var _this5 = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee10() {
        return _regeneratorRuntime.wrap(function _callee10$(_context10) {
          while (1) {
            switch (_context10.prev = _context10.next) {
              case 0:
                _context10.next = 2;
                return _this5.updateParams(_defineProperty({}, param.toUpperCase(), value));

              case 2:
              case "end":
                return _context10.stop();
            }
          }
        }, _callee10);
      }))();
    }
  }
};

function ownKeys$k(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$k(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$k(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$k(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
/**
 * Base XYZ source mixin.
 */

var xyzSource = {
  mixins: [tileImageSource],
  props: {
    // ol/source/XYZ
    maxZoom: {
      type: Number,
      default: 42
    },
    minZoom: {
      type: Number,
      default: 0,
      validator: function validator(value) {
        return value >= 0;
      }
    },
    maxResolution: Number,
    tileSize: {
      type: [Number, Array],
      default: function _default() {
        return [256, 256];
      },
      validator: function validator(value) {
        return isNumber(value) || isArray(value) && value.length === 2 && value.every(isNumber);
      }
    }
  },
  computed: {
    tileSizeArr: function tileSizeArr() {
      return isArray(this.tileSize) ? this.tileSize : [this.tileSize, this.tileSize];
    },
    derivedTileGridFactory: function derivedTileGridFactory() {
      if (isFunction(this.tileGridFactory)) {
        return this.tileGridFactory;
      }

      var extent = extentFromProjection(this.resolvedDataProjection);
      var maxZoom = this.maxZoom;
      var minZoom = this.minZoom;
      var maxResolution = this.maxResolution;
      var tileSize = this.tileSizeArr;
      return function () {
        return createXYZ({
          extent: extent,
          maxZoom: maxZoom,
          minZoom: minZoom,
          maxResolution: maxResolution,
          tileSize: tileSize
        });
      };
    },
    resolvedTileUrlFunc: function resolvedTileUrlFunc() {
      if (isFunction(this.tileUrlFunc)) {
        return this.tileUrlFunc;
      }

      if (this.expandedUrls.length === 0) return;
      return createTileUrlFunctionFromTemplates(this.expandedUrls, this.tileGrid);
    }
  },
  methods: _objectSpread$k({
    /**
     * @return {module:ol/source/XYZ~XYZSource}
     * @protected
     */
    createSource: function createSource() {
      return new XYZ({
        // ol/source/Source
        attributions: this.currentAttributions,
        attributionsCollapsible: this.attributionsCollapsible,
        projection: this.resolvedDataProjection,
        wrapX: this.wrapX,
        // ol/source/Tile
        cacheSize: this.cacheSize,
        opaque: this.opaque,
        tilePixelRatio: this.tilePixelRatio,
        transition: this.transition,
        zDirection: this.zDirection,
        tileGrid: this.tileGrid,
        // ol/source/UrlTile
        tileLoadFunction: this.resolvedTileLoadFunc,
        tileUrlFunction: this.resolvedTileUrlFunc,
        // ol/source/TileImage
        crossOrigin: this.crossOrigin,
        reprojectionErrorThreshold: this.reprojectionErrorThreshold,
        tileClass: this.tileClass
      });
    }
  }, /*#__PURE__*/pick(tileImageSource.methods, ['beforeInit', 'init', 'deinit', 'beforeMount', 'mount', 'unmount', 'refresh', 'scheduleRefresh', 'remount', 'scheduleRemount', 'recreate', 'scheduleRecreate', 'getServices', 'subscribeAll', 'resolveSource', 'resolveOlObject']))
};

export { CanceledError, EVENT_BUS_PROP, FRAME_TIME, IDENTITY_MAP_PROP, INSTANCES_POOL, LifecycleError, OlObjectAction, OlObjectEvent, OlObjectState, SERVICES_PROP, VM_PROP, arcgisSource, baseLayer, controlsContainer, eventBus, feature, featureHelper, featuresContainer, fillStyleContainer, geometry, geometryContainer, identMap, imageLayer, imageSource, imageStyle, imageStyleContainer, interaction, interactionsContainer, isCreateError, isDestroyError, isMountError, isUnmountError, layer, layersContainer, olCmp, overlaysContainer, projTransforms, regShapeStyle, rxSubs, services, simpleGeometry, source, sourceContainer, strokeStyleContainer, stubVNode, style, styleContainer, textStyleContainer, tileImageSource, tileLayer, tileSource, urlTileSource, vectorLayer, vectorSource, waitForMap, wmsSource, xyzSource };
//# sourceMappingURL=mixins.js.map
